import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, RefreshCw, Loader2, X, Trash2, History, Play, AlertTriangle, CheckCircle, XCircle, FileDown, ExternalLink, CloudDownload, Plus, ChevronLeft, ChevronRight, ChevronDown, Send, Copy, Check, Link2, Scissors, Globe, ArrowLeftRight, ShoppingCart, Truck, BoxIcon, Braces, Printer, Ban, MapPin, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';
import PackingVisualization3D from '../components/PackingVisualization3D';
import ShippyLoader from '../components/ShippyLoader';
import { ShippySpinner } from '../components/ShippyLoader';
import ShippyPackingLoader from '../components/ShippyPackingLoader';
import CustomOrderModal from '../components/CustomOrderModal';
import ScenarioComparisonPanel from '../components/ScenarioComparisonPanel';
import { grossMargin, HEALTHY_MARGIN_PCT } from '../utils/orderMargin';
import CheckoutPreviewModal from '../components/CheckoutPreviewModal';
import ExperimentNotice, { ExperimentQuotePill, ExperimentQuoteDetail } from '../components/ExperimentNotice';

const AVAILABLE_BINS = {
  "CB-20x20x20": { width: 20, height: 20, length: 20 },
  "CB-30x30x30": { width: 30, height: 30, length: 30 },
  "CB-40x40x40": { width: 40, height: 40, length: 40 },
  "CB-45x45x25": { width: 45, height: 45, length: 25 },
  "CB-50x50x50": { width: 50, height: 50, length: 50 },
  "CB-60x40x30": { width: 60, height: 40, length: 30 },
  "CB-60x60x30": { width: 60, height: 60, length: 30 },
  "CB-60x60x60": { width: 60, height: 60, length: 60 },
  "CB-80x50x40": { width: 80, height: 50, length: 40 },
  "CB-70x70x45": { width: 70, height: 70, length: 45 },
  "CB-80x80x28": { width: 80, height: 80, length: 28 },
  "CB-80x20x20": { width: 80, height: 20, length: 20 },
  "CB-100x50x50": { width: 100, height: 50, length: 50 },
  "CB-130x42x42": { width: 130, height: 42, length: 42 },
  "CB-150x35x32": { width: 150, height: 35, length: 32 },
  "CB-PALLET": { width: 80, height: 120, length: 160 }
};

const isItemOversized = (dimensions, weight = 0) => {
  if (!dimensions?.width || !dimensions?.height || !dimensions?.length) return false;
  const { width: iW, height: iH, length: iL } = dimensions;
  const orientations = [[iW,iH,iL],[iW,iL,iH],[iH,iW,iL],[iH,iL,iW],[iL,iW,iH],[iL,iH,iW]];
  for (const [, binDims] of Object.entries(AVAILABLE_BINS)) {
    for (const [w, h, l] of orientations) {
      if (w <= binDims.width && h <= binDims.height && l <= binDims.length && weight <= 10000) return false;
    }
  }
  return true;
};

const formatCurrency = (amount) => `${Number(amount).toFixed(2)}`;

// How a carrier's pallet card counts a pallet's weight (backend
// pallet_chargeable_breakdown): every term, and which one is billed.
const fmtKg = (v) => Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 });
const chargeableParts = (pc) => {
  if (!pc) return [];
  const parts = [];
  if (pc.volume) {
    parts.push({ key: 'volume', text: pc.volume.kg_per_m3
      ? `volume ${pc.volume.m3} m³ × ${fmtKg(pc.volume.kg_per_m3)} kg/m³ = ${fmtKg(pc.volume.kg)} kg`
      : `volume L×W×H ÷ ${pc.volume.divisor} = ${fmtKg(pc.volume.kg)} kg` });
  }
  if (pc.floor) {
    parts.push({ key: 'floor', text: `floor space ${pc.floor.floor_m} floor m × ${fmtKg(pc.floor.kg_per_floor_m)} kg = ${fmtKg(pc.floor.kg)} kg` });
  }
  if (pc.minimum_kg) parts.push({ key: 'minimum', text: `minimum ${fmtKg(pc.minimum_kg)} kg` });
  parts.push({ key: 'real', text: `real ${fmtKg(pc.real_kg)} kg` });
  return parts;
};
const ChargeableLine = ({ pc, label }) => (
  <span>
    {label && <span className="font-medium text-foreground">{label}: </span>}
    billed on <strong className="text-foreground">{fmtKg(pc.chargeable_kg)} kg</strong> ({pc.basis})
    {' · '}
    {chargeableParts(pc).map((p, i) => (
      <span key={p.key}>{i > 0 && ', '}
        <span className={p.key === pc.rule ? 'font-medium text-foreground underline decoration-dotted' : ''}>{p.text}</span>
      </span>
    ))}
  </span>
);

// Static fallback rates (EUR → X) used when live rates are unavailable
const STATIC_EUR_RATES = {
  EUR: 1, USD: 1/0.92, GBP: 1/1.17, CHF: 1/0.95, CAD: 1/0.68, AUD: 1/0.58,
  HKD: 1/0.12, DKK: 1/0.13, SEK: 1/0.087, NOK: 1/0.087, JPY: 1/0.006,
  TWD: 1/0.029, NZD: 1/0.53, SGD: 1/0.69, KRW: 1/0.00066, PLN: 1/0.23,
  CZK: 1/0.040, HUF: 1/0.0025, RON: 1/0.20, BGN: 1/0.51, BRL: 1/0.16,
  MXN: 1/0.047, ZAR: 1/0.050, INR: 1/0.011, CNY: 1/0.13, ILS: 1/0.26,
  AED: 1/0.25, SAR: 1/0.25, TRY: 1/0.027, QAR: 1/0.25, KWD: 1/3.0,
  THB: 1/0.027, MYR: 1/0.21, PHP: 1/0.016, IDR: 1/0.000058, EGP: 1/0.019,
  MAD: 1/0.093, RSD: 1/0.0085, BHD: 1/2.44, OMR: 1/2.39,
};

// Fetch live EUR exchange rates from ECB via exchangerate-api, cache for 1 hour
const RATE_CACHE_KEY = 'shippy_eur_rates';
const RATE_CACHE_TTL = 60 * 60 * 1000; // 1 hour

const fetchLiveRates = async () => {
  try {
    const cached = localStorage.getItem(RATE_CACHE_KEY);
    if (cached) {
      const { rates, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < RATE_CACHE_TTL) return rates;
    }
    const res = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    // data.rates is { USD: 1.08, GBP: 0.85, ... } (how many X per 1 EUR)
    localStorage.setItem(RATE_CACHE_KEY, JSON.stringify({ rates: data.rates, timestamp: Date.now() }));
    return data.rates;
  } catch {
    return null; // fallback to static rates
  }
};

const useLiveRates = () => {
  const [rates, setRates] = useState(STATIC_EUR_RATES);
  const [isLive, setIsLive] = useState(false);
  useEffect(() => {
    fetchLiveRates().then(live => {
      if (live) {
        // Convert from "how many X per 1 EUR" to the same format as our static map
        const converted = { EUR: 1 };
        for (const [code, rate] of Object.entries(live)) {
          converted[code] = rate;
        }
        setRates(converted);
        setIsLive(true);
      }
    });
  }, []);
  return { eurRates: rates, isLive };
};
const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF', CAD: 'CA$', AUD: 'A$',
  HKD: 'HK$', DKK: 'DKK', SEK: 'SEK', NOK: 'NOK', JPY: '¥',
  TWD: 'NT$', NZD: 'NZ$', SGD: 'S$', KRW: '₩', PLN: 'zł',
  CZK: 'Kč', HUF: 'Ft', RON: 'lei', BGN: 'лв', BRL: 'R$',
  MXN: 'MX$', ZAR: 'R', INR: '₹', CNY: '¥', ILS: '₪',
  AED: 'AED', SAR: 'SAR', TRY: '₺', QAR: 'QAR', KWD: 'KWD',
  THB: '฿', MYR: 'RM', PHP: '₱', IDR: 'Rp', EGP: 'E£',
  MAD: 'MAD', RSD: 'RSD', BHD: 'BHD', OMR: 'OMR',
};
const HISTORY_PAGE_SIZE = 25;

// Country name → ISO 2-letter code → flag emoji
const COUNTRY_CODES = {
  'Belgium': 'BE', 'Netherlands': 'NL', 'France': 'FR', 'Germany': 'DE',
  'Luxembourg': 'LU', 'Spain': 'ES', 'Portugal': 'PT', 'Italy': 'IT',
  'Austria': 'AT', 'Switzerland': 'CH', 'United Kingdom': 'GB',
  'Ireland': 'IE', 'Denmark': 'DK', 'Sweden': 'SE', 'Norway': 'NO',
  'Finland': 'FI', 'Poland': 'PL', 'Czech Republic': 'CZ', 'Czechia': 'CZ',
  'Slovakia': 'SK', 'Hungary': 'HU', 'Romania': 'RO', 'Bulgaria': 'BG',
  'Croatia': 'HR', 'Slovenia': 'SI', 'Greece': 'GR', 'Estonia': 'EE',
  'Latvia': 'LV', 'Lithuania': 'LT', 'Malta': 'MT', 'Cyprus': 'CY',
  'United States': 'US', 'Canada': 'CA', 'Australia': 'AU',
  'New Zealand': 'NZ', 'Japan': 'JP', 'South Korea': 'KR',
  'United Arab Emirates': 'AE', 'Singapore': 'SG', 'Hong Kong': 'HK',
  'Taiwan': 'TW', 'Brazil': 'BR', 'Mexico': 'MX', 'South Africa': 'ZA',
  'Turkey': 'TR', 'Israel': 'IL', 'India': 'IN', 'China': 'CN',
};
// Reverse lookup: ISO2 → full name
const ISO2_TO_NAME = Object.fromEntries(Object.entries(COUNTRY_CODES).map(([name, code]) => [code, name]));

/** Normalize country: if it's a short ISO2 code, resolve to full name. */
const normalizeCountry = (val) => {
  if (!val) return '';
  if (val.length <= 3 && ISO2_TO_NAME[val.toUpperCase()]) return ISO2_TO_NAME[val.toUpperCase()];
  return val;
};

const countryFlag = (name) => {
  const resolved = normalizeCountry(name);
  const code = COUNTRY_CODES[resolved] || (name.length <= 3 ? name.toUpperCase() : '');
  if (!code) return '';
  return String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

/**
 * Cut-box helpers. Cut-box only impacts shipping cost when the rate is
 * volumetric (bill = L×W×H). For weight-based or per-parcel lanes the
 * cut is irrelevant, so we don't surface it to avoid confusing the user.
 */
// Pallet deck height, added to the measured load before the carrier bills it.
// EPAL 1 is 144 mm with a +10/-0 tolerance, so 15 cm covers a deck at its upper
// bound. Mirrors PackingConfig.PALLET_DECK_HEIGHT_CM on the backend.
const PALLET_DECK_CM = 15;

// "GLS France (Flex Delivery)" -> "GLS France Flex"; "DHL (Parcel (EU))" -> "DHL Parcel".
const shortCarrierName = (n) => {
  if (!n) return 'Standard';
  const m = String(n).match(/^([^(]+)\((.*)\)$/);
  if (!m) return String(n).split(' (')[0];
  const svc = m[2].trim().split(/[\s(]/)[0];
  return svc && svc.toLowerCase() !== 'default' ? `${m[1].trim()} ${svc}` : m[1].trim();
};
// Parcelization signature: the sorted bin names, the key the carrier matrix
// uses (bin_signature) to point at a strategy.
const strategySignature = (s) => (s?.bins_used || []).map(b => (b.bin_name || '').split(' #')[0]).sort().join('|');

const isVolumetricBin = (bin) => /volum/i.test(bin?.rate_method || '');

// The POSTCODE ZONE that priced a bin, when one did. rate_method carries it in
// brackets ("zone_weight (FR Tier 13 — Corsica)"), and it is worth saying out
// loud: a zone exists because the carrier charges more to get there - an
// island, a mountain, a remote area - so a quote that looks high is explained
// rather than suspicious.
const zoneOf = (rateMethod) => {
  const m = String(rateMethod || '');
  if (!/zone/i.test(m)) return null;
  const inside = m.match(/\(([^)]+)\)/);
  return inside ? inside[1].trim() : 'postcode zone';
};

// Classify a bin's pricing basis from its rate_method for a clear badge.
// rate_method examples: "zone_weight (FR Tier 11)", "direct_shipping_volumetric_rate",
// "direct_shipping_weight_rate", "pallet_fixed (PLT-...)", "direct_coli_rate".
const rateBasisBadge = (rateMethod) => {
  const m = (rateMethod || '').toLowerCase();
  if (!m) return null;
  const zone = /zone/.test(m);
  // Wide-base rule (2026-07-16, replaces the no-fit max-rate rule): an item
  // that fits no standard pallet lies flat on a WIDE base priced at 2x the
  // standard rate. Loud red badge so ops can't miss it. (/no_fit/ kept for
  // historical records priced under the older rule.)
  // Match the METHOD, not any substring: the carrier name "Express Worldwide"
  // contains "wide", so /wide/ put the red 2x badge on every DHL Worldwide
  // pallet, including the ones a pallet card priced (#185342 showed
  // "Wide base · 2x rate" next to a pallet_card price).
  if (/pallet_wide_/.test(m))     return { label: 'Wide base · 2x rate',         cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
  if (/pallet_no_fit/.test(m))    return { label: 'No fit · max pallet rate',    cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
  // Pallet cards (2026-09-16): the carrier's own pallet grid, read on the
  // pallet's chargeable weight. Show the band so ops can see what priced it.
  if (/pallet_card/.test(m)) {
    const band = (rateMethod.match(/(\d+)kg band/i) || [])[1];
    return { label: band ? `Pallet · card (${band}kg band)` : 'Pallet · card', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
  }
  // France pallets priced by weight on the Geodis Messagerie Plus freight bands
  // (2026-09-15): say so, with the band, instead of the generic pallet badge —
  // ops asked how to SEE that the messagerie grid priced the pallet.
  if (/freight_weight/.test(m)) {
    const band = (rateMethod.match(/\d+\s*-\s*\d+\s*kg/i) || [])[0];
    return { label: band ? `Pallet · by weight (${band})` : 'Pallet · by weight', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
  }
  if (/pallet/.test(m))           return { label: 'Pallet',                       cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
  if (/coli|parcel/.test(m))      return { label: 'Per-parcel',                   cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' };
  if (/volum/.test(m))            return { label: zone ? 'Zone · Vol' : 'Volumetric', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' };
  if (/weight/.test(m))           return { label: zone ? 'Zone · Real wt' : 'Real weight', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' };
  return { label: rateMethod, cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
};

/**
 * Returns cut-box info for a bin, ONLY when cutting actually saves money
 * on this rate type. Otherwise canCut=false.
 *   - canCut: true if the cut-box price is cheaper and rate is volumetric
 *   - cutCost: post-cut TOTAL cost for this bin (shipping + packaging)
 *   - savings: € saved vs full box
 *   - cutHeight: the height the box should be cut to
 */
const getBinCutInfo = (bin) => {
  if (!bin || !isVolumetricBin(bin)) {
    return { canCut: false, cutCost: null, savings: 0, cutHeight: null };
  }
  const savings = Number(bin.cut_box_savings || 0);
  if (!(savings > 0) || bin.used_height == null) {
    return { canCut: false, cutCost: null, savings: 0, cutHeight: null };
  }
  return {
    canCut: true,
    cutCost: Number(bin.cut_box_cost || 0),
    savings,
    cutHeight: bin.used_height,
  };
};

/** Sum of cut-box savings across all bins in a strategy (volumetric bins only). */
const getStrategyCutSavings = (strategy) => {
  if (!strategy || !Array.isArray(strategy.bins_used)) return 0;
  return strategy.bins_used.reduce((sum, b) => sum + getBinCutInfo(b).savings, 0);
};

/** What a strategy really costs once boxes are cut down (the decision strip's number). */
const effectiveStrategyCost = (strategy) => {
  const full = Number(strategy?.total_cost || 0);
  return full - Math.max(0, Math.min(getStrategyCutSavings(strategy), full));
};

/** Derive shipment type from bins_summary: 'pallet' | 'oversized' | 'standard' */
const getShipmentType = (binsSummary) => {
  if (!binsSummary || typeof binsSummary !== 'object') return 'standard';
  for (const name of Object.keys(binsSummary)) {
    const upper = name.toUpperCase();
    if (upper.includes('PLT') || upper.includes('PALLET')) return 'pallet';
  }
  for (const name of Object.keys(binsSummary)) {
    const upper = name.toUpperCase();
    if (upper.includes('DIRECT')) return 'oversized';
  }
  return 'standard';
};

const SurchargeModal = ({ open, onClose, orderData, selectedCost, eurRates }) => {
  const [targetMarginPct, setTargetMarginPct] = useState(20);
  const [copied, setCopied] = useState(false);
  const [mollieLoading, setMollieLoading] = useState(false);
  const [mollieUrl, setMollieUrl] = useState(null);
  const [mollieError, setMollieError] = useState(null);
  const [mollieCopied, setMollieCopied] = useState(false);

  if (!open || !orderData) return null;

  const totalValue = orderData.products.reduce((sum, p) => sum + ((p.unit_price || 0) * p.quantity), 0);
  const totalCost = orderData.products.reduce((sum, p) => sum + ((p.cost || 0) * p.quantity), 0);
  const shippingCharged = orderData.total_shipping_cost || 0;
  const orderCurrency = orderData.currency || 'EUR';

  const commission = orderData.professional_commission || 0;

  const requiredMargin = (targetMarginPct / 100) * totalValue;
  const maxShippingCost = totalValue - totalCost - commission - requiredMargin + shippingCharged;
  const surchargeNeeded = Math.max(0, selectedCost - maxShippingCost);
  const surchargeInLocal = orderCurrency !== 'EUR' ? surchargeNeeded * (eurRates[orderCurrency] || 1) : null;

  const actualMarginPct = grossMargin({ value: totalValue, cost: totalCost, commission, charged: shippingCharged, shipping: selectedCost }).pct ?? 0;

  const localAmountStr = surchargeInLocal != null
    ? ` / ${CURRENCY_SYMBOLS[orderCurrency] || orderCurrency} ${surchargeInLocal.toFixed(2)}`
    : '';

  const message = `Dear Customer,

Thank you for your order ${orderData.order_name || ''}.

Due to the size and weight of your order, an additional shipping contribution of €${formatCurrency(surchargeNeeded)}${localAmountStr} is required.

This amount covers the actual transportation costs for your items, which exceed the standard shipping rate.

We appreciate your understanding and remain at your disposal for any questions.

Kind regards,
The LampTwist Team`;

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateMollieLink = async () => {
    setMollieLoading(true);
    setMollieError(null);
    setMollieUrl(null);
    try {
      // Use local currency amount if available, otherwise EUR
      const paymentCurrency = orderCurrency || 'EUR';
      const paymentAmount = surchargeInLocal != null ? surchargeInLocal : surchargeNeeded;
      const res = await axiosInstance.post('api/mollie/payment-link/', {
        order_name: orderData.order_name || '',
        amount: paymentAmount.toFixed(2),
        currency: paymentCurrency,
        description: `Shipping surcharge ${orderData.order_name || ''}`,
      });
      setMollieUrl(res.data.url);
    } catch (err) {
      setMollieError(err.response?.data?.error || 'Failed to generate payment link');
    } finally {
      setMollieLoading(false);
    }
  };

  const handleCopyMollieUrl = () => {
    if (mollieUrl) {
      navigator.clipboard.writeText(mollieUrl);
      setMollieCopied(true);
      setTimeout(() => setMollieCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">Shipping Surcharge — {orderData.order_name || 'Order'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Margin Slider */}
          <div>
            <label className="text-sm font-medium">Target Margin: <span className="font-bold">{targetMarginPct}%</span></label>
            <input
              type="range"
              min={10}
              max={40}
              step={1}
              value={targetMarginPct}
              onChange={(e) => setTargetMarginPct(Number(e.target.value))}
              className="w-full mt-1 accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10%</span><span>40%</span>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Order Value</span><span className="font-mono">€{formatCurrency(totalValue)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Order Cost</span><span className="font-mono">€{formatCurrency(totalCost)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping Charged</span><span className="font-mono">€{formatCurrency(shippingCharged)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Simulated Shipping</span><span className="font-mono">€{formatCurrency(selectedCost)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Current Margin</span><span className={`font-mono font-bold ${actualMarginPct < targetMarginPct ? 'text-red-600' : 'text-emerald-600'}`}>{actualMarginPct.toFixed(1)}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Target Margin</span><span className="font-mono font-bold">{targetMarginPct}%</span></div>
          </div>

          {/* Surcharge Amount */}
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Surcharge needed: <span className="text-lg font-bold">€{formatCurrency(surchargeNeeded)}</span>
              {surchargeInLocal != null && (
                <span className="ml-2 text-base">({CURRENCY_SYMBOLS[orderCurrency] || orderCurrency} {surchargeInLocal.toFixed(2)})</span>
              )}
            </p>
          </div>

          {/* Message Template */}
          <div>
            <label className="text-sm font-medium mb-1 block">Customer Message</label>
            <textarea
              readOnly
              value={message}
              className="w-full h-48 text-sm font-mono bg-muted/50 border border-border rounded-lg p-3 resize-none"
            />
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={handleCopy}
            >
              {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied!</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy to clipboard</>}
            </Button>
          </div>

          {/* Mollie Payment Link */}
          <div className="border-t border-border pt-4">
            <label className="text-sm font-medium mb-2 block">Payment Link</label>
            {!mollieUrl ? (
              <div>
                <Button
                  size="sm"
                  onClick={handleGenerateMollieLink}
                  disabled={mollieLoading || surchargeNeeded <= 0}
                  className="bg-primary text-primary-foreground"
                >
                  {mollieLoading ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating...</>
                  ) : (
                    <><Link2 className="h-3.5 w-3.5 mr-1" /> Generate Mollie Payment Link</>
                  )}
                </Button>
                {surchargeNeeded <= 0 && (
                  <p className="text-xs text-muted-foreground mt-1">No surcharge needed at this target margin.</p>
                )}
                {mollieError && (
                  <p className="text-xs text-red-600 mt-1">{mollieError}</p>
                )}
              </div>
            ) : (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 px-4 py-3">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  <CheckCircle className="h-3.5 w-3.5 inline mr-1" /> Payment link created
                </p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={mollieUrl}
                    className="flex-1 text-xs font-mono bg-white dark:bg-card border border-border rounded px-2 py-1.5 truncate"
                  />
                  <Button size="sm" variant="outline" onClick={handleCopyMollieUrl}>
                    {mollieCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={mollieUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// A rider box (a parcel bin that rode a forced pallet) is one positioned
// cuboid carrying its products in box_items: the 3D view draws the cuboid,
// the table lists the products under a box header row.
const expandRiderBoxes = (items) => (items || []).flatMap((it) => (
  it.box_items && it.box_items.length
    ? [{ ...it, weight: 0, product_title: `📦 ${it.product_title || it.name}`, _riderHeader: true }, ...it.box_items]
    : [it]
));

/* The B2B tab logs four different things into one table. Two of them are
 * B2B customers hitting our own tools; carrier advice is Odoo asking which
 * carrier to book (~740 calls a day, four times the packing sims) and the
 * bot is Shippy's Slack assistant logging its tool runs. Grouping them lets
 * the tab default to the traffic a human generated, with the machine
 * traffic one click away rather than drowning it.
 * `endpoints: []` means no endpoint filter at all. */
const B2B_VIEWS = [
  { key: 'b2b', label: 'B2B', endpoints: ['packing_sim', 'quoty'],
    hint: 'Packing simulations and Quoty · B2B customers using our tools' },
  { key: 'advice', label: 'Carrier advice', endpoints: ['carrier_advice'],
    hint: 'Odoo asking which carrier to book for an order' },
  { key: 'bot', label: 'Bot', endpoints: ['slack_bot'],
    hint: 'The Shippy Slack bot, one row per tool it runs' },
  { key: 'all', label: 'All', endpoints: [],
    hint: 'Everything except carrier service and bpost, which have their own tabs' },
];

const B2B_ENDPOINT_BADGE = {
  packing_sim: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  quoty: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  carrier_advice: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  slack_bot: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  _default: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
};

const PackingSimulator = () => {
  const { eurRates, isLive: isLiveRates } = useLiveRates();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState('');
  const [replacingRecord, setReplacingRecord] = useState(false);
  const [replaceSuccess, setReplaceSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [orderCandidates, setOrderCandidates] = useState([]);  // for "no exact match" picker
  const [isDraftOrder, setIsDraftOrder] = useState(false);
  const hasAutoTriggered = useRef(false);
  const autoCalculateAfterLoad = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packingSolution, setPackingSolution] = useState(null);
  const [pickupSolution, setPickupSolution] = useState(null);
  const [carrierAlternatives, setCarrierAlternatives] = useState(null); // carrier comparison: alternative carrier prices for the same parcelization
  const [carrierMatrix, setCarrierMatrix] = useState(null); // per-carrier best strategy x parcelization combo (sim-only)
  const [expandedOptions, setExpandedOptions] = useState({}); // options panel (carriers/pickup/split) expand state
  const [singleCarrier, setSingleCarrier] = useState(null); // single-carrier vs multi-carrier comparison (sim-only)
  const [forcedCarrier, setForcedCarrier] = useState(null); // customer picked a carrier at checkout (multi-carrier pilot)
  const [shippingExperiment, setShippingExperiment] = useState(null); // running A/B shipping experiment: both arms' price for this cart
  const [splitPreview, setSplitPreview] = useState(null); // Split Shipment (ship what's in stock) two-wave preview
  const [activeStrategyTab, setActiveStrategyTab] = useState(0);
  const [alternativeSolutions, setAlternativeSolutions] = useState([]);
  const [strategiesState, setStrategiesState] = useState([]);
  // Carrier views (2026-10-01): a chip in "Other carriers" opens that carrier's
  // priced solution as an extra strategy tab (api/orders/carrier-view/).
  const [carrierViewLoading, setCarrierViewLoading] = useState(null); // carrier_id being priced
  const [carrierViewError, setCarrierViewError] = useState(null);
  const strategyTabsRef = useRef(null);
  const [calculating, setCalculating] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pushCapability, setPushCapability] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushNotice, setPushNotice] = useState(null);
  const [pushOdooChoice, setPushOdooChoice] = useState(null);
  const [editDimensions, setEditDimensions] = useState({ width: '', height: '', length: '', weight: '' });
  const [applyingFallbackFor, setApplyingFallbackFor] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [analysisRecords, setAnalysisRecords] = useState([]);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [showLossesOnly, setShowLossesOnly] = useState(false);
  const [showSplitOnly, setShowSplitOnly] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyBrand, setHistoryBrand] = useState('');
  const [historyProductName, setHistoryProductName] = useState('');
  const [historyCarrier, setHistoryCarrier] = useState('');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [surchargeModalOpen, setSurchargeModalOpen] = useState(false);

  // B2B API Calls tab state
  const [b2bCalls, setB2bCalls] = useState([]);
  const [b2bTotalCount, setB2bTotalCount] = useState(0);
  const [b2bTotalPages, setB2bTotalPages] = useState(1);
  const [b2bPage, setB2bPage] = useState(1);
  const [b2bDays, setB2bDays] = useState(7);
  const [b2bView, setB2bView] = useState('b2b');
  const [b2bEndpointCounts, setB2bEndpointCounts] = useState({});
  const [b2bLoading, setB2bLoading] = useState(false);
  const [b2bError, setB2bError] = useState(null);
  const [b2bExpandedRow, setB2bExpandedRow] = useState(null);
  const [deletingB2bRecord, setDeletingB2bRecord] = useState(null);
  const [b2bSearch, setB2bSearch] = useState('');
  // A shared link (?call=<id>) pins the list to that one call, whatever its
  // age or view; touching search/view/days or « Show all calls » unpins it.
  const [b2bPinnedCall, setB2bPinnedCall] = useState(null);
  const [useOdooData, setUseOdooData] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null); // null or order number string

  // Checkout (Carrier Service) tab state
  const [csCalls, setCsCalls] = useState([]);
  const [csTotalCount, setCsTotalCount] = useState(0);
  const [csTotalPages, setCsTotalPages] = useState(1);
  const [csPage, setCsPage] = useState(1);
  const [csDays, setCsDays] = useState(1);
  const [csLoading, setCsLoading] = useState(false);
  const [csError, setCsError] = useState(null);
  const [csExpandedRow, setCsExpandedRow] = useState(null);
  const [csShowRaw, setCsShowRaw] = useState(null);
  const [csSearch, setCsSearch] = useState('');
  const [csExperimentOnly, setCsExperimentOnly] = useState(false); // only quotes a shipping experiment touched
  const [csExperiments, setCsExperiments] = useState({}); // experiment slug -> {id, name, countries, link}
  const [csExperimentLabels, setCsExperimentLabels] = useState({});
  const [csCountryStats, setCsCountryStats] = useState([]);
  const [csHourlyByCountry, setCsHourlyByCountry] = useState(null);
  const [csStatsLoading, setCsStatsLoading] = useState(false);
  const [csStatsOpen, setCsStatsOpen] = useState(false);
  const [csHourlyOpen, setCsHourlyOpen] = useState(false);
  const [csHourlyVisible, setCsHourlyVisible] = useState(null); // Set of visible countries
  const [csHoverCountry, setCsHoverCountry] = useState(null); // {country, x, y, val, hour}
  const [csFocusCountry, setCsFocusCountry] = useState(null); // solo a country on legend click

  // Returny (Bpost Checker) tab state
  const [rtCalls, setRtCalls] = useState([]);
  const [rtTotalCount, setRtTotalCount] = useState(0);
  const [rtTotalPages, setRtTotalPages] = useState(1);
  const [rtPage, setRtPage] = useState(1);
  const [rtDays, setRtDays] = useState(7);
  const [rtLoading, setRtLoading] = useState(false);
  const [rtError, setRtError] = useState(null);
  const [rtExpandedRow, setRtExpandedRow] = useState(null);
  const [rtSearch, setRtSearch] = useState('');

  // Scenario comparison state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [checkoutPreviewOpen, setCheckoutPreviewOpen] = useState(false);
  // Quick checkout preview straight from an order row (Shopify tab): fetches the
  // order like the play button does, but opens the checkout modal instead of
  // loading the simulator, so a batch of orders can be flicked through.
  const [quickPreview, setQuickPreview] = useState(null);
  const [quickPreviewLoading, setQuickPreviewLoading] = useState(null);
  // Per-bin 3D visualization override map. Default visibility is derived
  // from safe-mode (large-order heuristic) so a 186-item order doesn't
  // try to mount 30+ WebGL contexts simultaneously and crash the tab.
  const [viz3DOverrides, setViz3DOverrides] = useState({});

  // Reset 3D overrides when the active solution / strategy changes so the
  // user starts from the default each time.
  useEffect(() => {
    setViz3DOverrides({});
  }, [activeStrategyTab, packingSolution]);
  const [baselineSnapshot, setBaselineSnapshot] = useState(null);

  const copyOrderUrl = (orderNum) => {
    const url = `${window.location.origin}/packing-simulator?order=${encodeURIComponent(orderNum)}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(orderNum);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Dimension editing
  // Unique key for each product — variant_id for regular products, line item id for custom items
  const getProductKey = (product) => product.variant_id || product.id || `idx-${orderData?.products?.indexOf(product)}`;

  const handleStartEdit = (product) => {
    setEditingProduct(getProductKey(product));
    setEditDimensions({
      width: product.dimensions?.width?.toString() || '',
      height: product.dimensions?.height?.toString() || '',
      length: product.dimensions?.length?.toString() || '',
      weight: product.weight?.toString() || ''
    });
    // Where can these dims be pushed permanently? Odoo when the variant is
    // linked there, the Shopify custom.packaging metafield when not, and a
    // direct Odoo product search for lines that never became Shopify variants
    // (project/quote products).
    setPushEnabled(false);
    setPushOdooChoice(null);
    const params = new URLSearchParams();
    if (product.variant_id) params.set('variant_id', String(product.variant_id));
    if (product.sku) params.set('sku', String(product.sku));
    if (product.title) params.set('title', String(product.title));
    if ([...params.keys()].length === 0) {
      setPushCapability({ found: false, odoo: false, shopify: false, reason: 'custom line, nothing to match on' });
      return;
    }
    setPushCapability({ checking: true });
    axiosInstance.get(`api/packaging-data/push-capability/?${params.toString()}`)
      .then(res => {
        setPushCapability(res.data);
        if (res.data?.odoo_direct && res.data.candidates?.length) {
          setPushOdooChoice(res.data.candidates[0].odoo_id);
        }
      })
      .catch(() => setPushCapability({ found: false, odoo: false, shopify: false, reason: 'capability check failed' }));
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditDimensions({ width: '', height: '', length: '', weight: '' });
    setPushEnabled(false);
    setPushCapability(null);
    setPushOdooChoice(null);
  };

  const handleSaveDimensions = (productKey) => {
    if (!orderData) return;
    const w = parseFloat(editDimensions.width), h = parseFloat(editDimensions.height), l = parseFloat(editDimensions.length), wt = parseFloat(editDimensions.weight);
    if (isNaN(w) || isNaN(h) || isNaN(l) || w <= 0 || h <= 0 || l <= 0) {
      setError('Please enter valid dimensions (all must be positive)');
      return;
    }
    const product = orderData.products.find(p => getProductKey(p) === productKey);
    const pushTarget = pushCapability?.odoo ? 'odoo' : pushCapability?.shopify ? 'shopify' : null;
    const pushWeight = !isNaN(wt) && wt > 0 ? wt : (product?.weight || 0);
    if (pushEnabled && pushTarget && !(pushWeight > 0)) {
      setError(`Weight is required to push dimensions to ${pushTarget === 'odoo' ? 'Odoo' : 'Shopify'}`);
      return;
    }
    setOrderData({
      ...orderData,
      products: orderData.products.map(p =>
        getProductKey(p) === productKey ? { ...p, dimensions: { width: w, height: h, length: l }, weight: isNaN(wt) ? p.weight : wt } : p
      )
    });
    const odooDirect = pushCapability?.odoo_direct && pushOdooChoice;
    if (pushEnabled && pushTarget && (product?.variant_id || odooDirect)) {
      const label = pushTarget === 'odoo' ? 'Odoo (Boxopedia)' : 'Shopify (custom.packaging)';
      const endpoint = pushTarget === 'odoo'
        ? 'api/packaging-data/variant-manual/'
        : 'api/packaging-data/variant-shopify-push/';
      const body = {
        boxes: [{ length: l, width: w, height: h, weight: pushWeight }],
        ...(odooDirect
          ? { odoo_id: pushOdooChoice, preserve_flags: true }
          : { variant_id: String(product.variant_id) }),
        ...(pushTarget === 'odoo' && !odooDirect ? { preserve_flags: true } : {}),
        ...(pushTarget === 'shopify' ? { title: product.title || '' } : {}),
      };
      setPushNotice({ ok: true, pending: true, message: `Pushing dimensions to ${label}...` });
      axiosInstance.post(endpoint, body)
        .then(res => setPushNotice({
          ok: true,
          message: res.data?.warning
            ? `Pushed to ${label}, with a warning: ${res.data.warning}`
            : `Dimensions saved to ${label} for ${product.title || 'variant ' + product.variant_id}.`,
        }))
        .catch(err => setPushNotice({
          ok: false,
          message: `Push to ${label} failed: ${err.response?.data?.message || err.message}. The dims still apply to this simulation.`,
        }));
    }
    setEditingProduct(null);
    setEditDimensions({ width: '', height: '', length: '', weight: '' });
    setPushEnabled(false);
    setPushCapability(null);
    setPushOdooChoice(null);
    setError(null);
  };

  const handleApplyFallbackDimensions = async (product) => {
    if (!orderData) return;
    const productKey = getProductKey(product);
    setApplyingFallbackFor(productKey);
    setError(null);
    try {
      // Use variant_id for API lookup; for custom items without variant_id, use a placeholder
      const lookupId = product.variant_id || `custom_${productKey}`;
      const response = await axiosInstance.post('api/packaging-data/fallback-dimensions/', { variant_ids: [lookupId] });
      if (response.data.success) {
        const fb = response.data.dimensions[lookupId];
        if (fb) {
          setOrderData({
            ...orderData,
            products: orderData.products.map(p =>
              getProductKey(p) === productKey ? { ...p, dimensions: { width: fb.width, height: fb.height, length: fb.length }, weight: fb.weight || p.weight, dimension_source: fb.source } : p
            )
          });
        } else {
          setError('No fallback dimensions found for this product');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get fallback dimensions');
    } finally {
      setApplyingFallbackFor(null);
    }
  };

  // Order lookup
  const handleLookupOrder = useCallback(async (orderId, isDraft) => {
    const lookupOrderNumber = orderId || orderNumber.trim();
    if (!lookupOrderNumber) { setError('Please enter an order number'); return; }

    const draftOrderPattern = /^#?D-?\d+/i;
    const isDraftDetected = isDraft !== undefined ? isDraft : draftOrderPattern.test(lookupOrderNumber);

    // Auto-detect S-prefixed Odoo orders
    const clean = lookupOrderNumber.replace(/^#/, '');
    const isOdooOrder = /^S\d+$/i.test(clean) || useOdooData;

    setLoading(true);
    setError(null);
    setOrderData(null);
    setOrderCandidates([]);
    setIsDraftOrder(isDraftDetected);

    try {
      const orderNumberWithoutHash = lookupOrderNumber.startsWith('#') ? lookupOrderNumber.substring(1) : lookupOrderNumber;
      const encodedOrderNumber = encodeURIComponent(orderNumberWithoutHash);
      const params = isOdooOrder
        ? { data_source: 'odoo' }
        : isDraftDetected ? { order_type: 'draft_order' } : { odoo_lines: 1 };
      const response = await axiosInstance.get(`api/orders/${encodedOrderNumber}/`, { params });
      setOrderData(response.data);
      setIsDraftOrder(response.data.order_type === 'draft_order');
      if (orderId) setOrderNumber(lookupOrderNumber);
    } catch (err) {
      if (err.response?.status === 404) {
        // Backend may include `candidates` — orders that matched a partial
        // search but not the exact name the user typed. Surface them so the
        // user can pick one rather than guessing.
        const candidates = err.response?.data?.candidates || [];
        if (candidates.length > 0) {
          setOrderCandidates(candidates);
          setError(`No exact match for "${lookupOrderNumber}". ${candidates.length} similar order${candidates.length > 1 ? 's' : ''} found — pick one below.`);
        } else {
          setOrderCandidates([]);
          setError(`Order "${lookupOrderNumber}" not found${isOdooOrder ? ' in Odoo' : ''}. Please check the order number and try again.`);
        }
      } else {
        setOrderCandidates([]);
        setError(err.response?.data?.message || 'Failed to fetch order.');
      }
    } finally {
      setLoading(false);
    }
  }, [orderNumber, useOdooData]);

  const handleReset = () => {
    setOrderNumber('');
    setOrderData(null);
    setError(null);
    setPackingSolution(null);
    setAlternativeSolutions([]);
    setStrategiesState([]);
    setComparisonMode(false);
    setBaselineSnapshot(null);
  };

  // The comparison modal keeps its scenarios while it is closed; they are
  // only dropped when the order, its cart or the chosen strategy changed.
  const handleEnterComparison = () => {
    const strategy = strategiesState[activeStrategyTab] || strategiesState[0]
      || { total_cost: packingSolution?.total_cost, bins_used: packingSolution?.bins_used };
    const key = JSON.stringify([orderData?.order_name, orderData?.products, strategy?.strategy, strategy?.total_cost]);
    if (!baselineSnapshot || baselineSnapshot.key !== key) {
      setBaselineSnapshot({ key, orderData: JSON.parse(JSON.stringify(orderData)), strategy });
    }
    setComparisonMode(true);
  };

  const handleExitComparison = useCallback(() => setComparisonMode(false), []);

  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(null); // 'customer' | 'internal' | null
  const pdfMenuRef = useRef(null);

  useEffect(() => {
    if (!pdfMenuOpen) return;
    const onClick = (e) => {
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setPdfMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPdfMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pdfMenuOpen]);

  const handleDownloadPDF = async (mode = 'internal') => {
    if (!orderNumber || !packingSolution || !orderData) return;
    setPdfMenuOpen(false);
    setPdfDownloading(mode);
    try {
      // Render the strategy the user has SELECTED at the bottom of the page
      // (the active tab), not the default/optimal one. Falls back to the
      // optimal solution when strategy tabs are unavailable.
      const selectedSolution = strategiesState[activeStrategyTab] || packingSolution;
      const packingData = {
        solution: selectedSolution,
        alternative_solutions: alternativeSolutions,
        all_strategies_sorted: strategiesState,
        order_data: orderData,
        pdf_mode: mode,
      };
      const orderNumberWithoutHash = orderNumber.startsWith('#') ? orderNumber.substring(1) : orderNumber;
      const response = await axiosInstance.post(`api/orders/${encodeURIComponent(orderNumberWithoutHash)}/pdf/`, packingData, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const modeSuffix = mode === 'customer' ? '_customer' : '';
      link.download = `packing_report_${orderNumber}${modeSuffix}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PDF report.');
    } finally {
      setPdfDownloading(null);
    }
  };

  // Open (or jump to) the tab that prices the standard parcelization under
  // one carrier. `bestSignature` instead prices the parcelization the carrier
  // matrix found cheapest for that carrier (a different set of boxes).
  const openCarrierView = async (carrierId, { bestSignature = null } = {}) => {
    const kind = bestSignature ? 'best' : 'same';
    const existing = strategiesState.findIndex(s => s.carrier_view?.carrier_id === carrierId && s.carrier_view?.kind === kind);
    const scrollToTabs = () => setTimeout(() => strategyTabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    if (existing >= 0) {
      setActiveStrategyTab(existing);
      scrollToTabs();
      return;
    }
    const base = bestSignature
      ? strategiesState.find(s => !s.carrier_view && strategySignature(s) === bestSignature)
      : (strategiesState.find(s => !s.carrier_view) || packingSolution);
    if (!base?.bins_used?.length) {
      setCarrierViewError('That parcelization is not among the strategies of this run.');
      return;
    }
    setCarrierViewLoading(carrierId);
    setCarrierViewError(null);
    try {
      const response = await axiosInstance.post('api/orders/carrier-view/', {
        solution: base,
        shipping_country: orderData?.shipping_address?.country || '',
        postal_code: orderData?.shipping_address?.zip || '',
        carrier_id: carrierId,
        standard_total: carrierAlternatives?.standard_total,
      });
      const sol = response.data.solution;
      sol.carrier_view = { ...(sol.carrier_view || {}), kind };
      const nextIndex = strategiesState.length;
      setStrategiesState(prev => [...prev, sol]);
      setActiveStrategyTab(nextIndex);
      scrollToTabs();
    } catch (err) {
      setCarrierViewError(err.response?.data?.message || 'Could not price this carrier.');
    } finally {
      setCarrierViewLoading(null);
    }
  };

  const handleCalculatePacking = async () => {
    if (!orderData) return;
    setCalculating(true);
    setError(null);
    setPackingSolution(null);
    setPickupSolution(null);
    setSplitPreview(null);
    setShippingExperiment(null);

    // Short floor only so the loading animation does not flicker; it used to
    // be 1.5 s, which made every cached result wait for the animation.
    const minDisplayTime = new Promise(r => setTimeout(r, 300));

    try {
      const orderNameWithoutHash = orderData.order_name.startsWith('#') ? orderData.order_name.substring(1) : orderData.order_name;
      const shippingCountry = orderData.shipping_address?.country || '';
      const postalCode = orderData.shipping_address?.zip || '';
      const [response] = await Promise.all([
        axiosInstance.post(`api/orders/${encodeURIComponent(orderNameWithoutHash)}/`, {
          products: orderData.products, shipping_country: shippingCountry, postal_code: postalCode,
          // Shipping-experiment notice: the cart total as checkout sees it
          // (incl. VAT) and the street (postcode + street pick the arm).
          order_total: orderData.checkout_total_eur ?? undefined,
          address1: orderData.shipping_address?.address1 || '',
        }),
        minDisplayTime,
      ]);

      if (response.data.status === 'success') {
        setPackingSolution(response.data.solution);
        setPickupSolution(response.data.pickup || null);
        setCarrierAlternatives(response.data.carrier_alternatives || null);
        setCarrierMatrix(response.data.carrier_matrix || null);
        setSingleCarrier(response.data.single_carrier || null);
        setForcedCarrier(response.data.forced_carrier || null);
        setShippingExperiment(response.data.shipping_experiment || null);
        setSplitPreview(response.data.split_shipment || null);
        // Mixed-stock orders: open the split row by default — "what ships
        // now vs later" is the first thing ops checks on a partial order.
        setExpandedOptions(prev => ({ ...prev, split: !!response.data.split_shipment?.eligible }));
        setAlternativeSolutions(response.data.alternative_solutions || []);
        const hasBackendOrder = Array.isArray(response.data.all_strategies_sorted);
        const backendStrategies = hasBackendOrder
          ? response.data.all_strategies_sorted
          : [{ strategy: response.data.solution?.strategy, total_cost: response.data.solution?.total_cost, bin_types: response.data.solution?.bin_types_summary, bins_used: response.data.solution?.bins_used },
             ...(response.data.alternative_solutions || []).map(a => ({ strategy: a.strategy, total_cost: a.total_cost, bin_types: a.bin_types_summary, bins_used: a.bins_used }))];
        // Trust the backend's ordering when it's authoritative — it
        // already uses strategy_effective_total_cost (accounts for
        // cut-box savings) and applies operational overrides like the
        // many-parcels-to-pallet swap. Re-sorting here would override
        // those forced choices (e.g. push pallets back behind a cheaper
        // box solution that ops won't actually ship). Only sort on the
        // fallback path where we synthesise the list from solution +
        // alternatives.
        // Long pieces taped into parcel bundles (#199187): extra tabs AFTER
        // the engine's own answers, so tab 1 stays what Shippy quotes.
        const bundleTabs = (response.data.long_item_bundles?.options || [])
          .map(o => o.strategy).filter(Boolean);
        if (hasBackendOrder) {
          setStrategiesState([...backendStrategies, ...bundleTabs]);
        } else {
          const effectiveCost = (s) => Number(s.total_cost || 0) - getStrategyCutSavings(s);
          setStrategiesState([...backendStrategies.slice().sort((a, b) => effectiveCost(a) - effectiveCost(b)), ...bundleTabs]);
        }
      } else {
        setError(response.data.message || 'Failed to calculate packing solution');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to calculate packing solution');
    } finally {
      setCalculating(false);
    }
  };

  // History
  const fetchAnalysisRecords = useCallback(async (page = historyPage, search = historySearch, lossesOnly = showLossesOnly, brand = historyBrand, productName = historyProductName, carrier = historyCarrier) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const offset = (page - 1) * HISTORY_PAGE_SIZE;
      const params = { limit: HISTORY_PAGE_SIZE, offset };
      if (search) params.search = search;
      if (lossesOnly) params.losses_only = true;
      if (showSplitOnly) params.method = 'split_shipment';
      if (brand) params.brand = brand;
      if (productName) params.product_name = productName;
      if (carrier) params.carrier = carrier;
      const response = await axiosInstance.get('api/packing-analysis-records/', { params });
      if (response.data.status === 'success') {
        setAnalysisRecords(response.data.records);
        setHistoryTotalCount(response.data.total_count || response.data.records.length);
      }
    } catch (err) {
      setHistoryError(err.response?.data?.message || 'Failed to fetch history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historySearch, showLossesOnly, showSplitOnly, historyBrand, historyProductName, historyCarrier]);

  const fetchB2BCalls = useCallback(async (page = b2bPage, days = b2bDays, view = b2bView, search = b2bSearch, pinned = b2bPinnedCall) => {
    setB2bLoading(true);
    setB2bError(null);
    try {
      // carrier_service and bpost_check have tabs of their own · they are
      // never part of this one, whichever view is selected.
      const params = { page, days, exclude_endpoint: 'carrier_service,bpost_check' };
      const endpoints = (B2B_VIEWS.find(v => v.key === view) || {}).endpoints || [];
      if (endpoints.length) params.endpoint = endpoints.join(',');
      if (search) params.search = search;
      if (pinned) params.id = pinned;
      const response = await axiosInstance.get('api/b2b-api-calls/', { params });
      if (response.data.status === 'success') {
        setB2bCalls(response.data.results);
        setB2bTotalCount(response.data.total_count);
        setB2bTotalPages(response.data.total_pages);
        setB2bEndpointCounts(response.data.endpoint_counts || {});
      }
    } catch (err) {
      setB2bError(err.response?.data?.message || 'Failed to fetch B2B API calls');
    } finally {
      setB2bLoading(false);
    }
  }, [b2bPage, b2bDays, b2bView, b2bSearch, b2bPinnedCall]);

  const fetchCSCalls = useCallback(async (page = csPage, days = csDays, search = csSearch) => {
    setCsLoading(true);
    setCsError(null);
    try {
      const params = { page, days, endpoint: 'carrier_service' };
      if (search) params.search = search;
      if (csExperimentOnly) params.experiment_only = 1;
      const response = await axiosInstance.get('api/b2b-api-calls/', { params });
      if (response.data.status === 'success') {
        setCsCalls(response.data.results);
        setCsExperiments(response.data.experiments || {});
        setCsExperimentLabels(response.data.experiment_reason_labels || {});
        setCsTotalCount(response.data.total_count);
        setCsTotalPages(response.data.total_pages);
      }
    } catch (err) {
      setCsError(err.response?.data?.message || 'Failed to fetch checkout calls');
    } finally {
      setCsLoading(false);
    }
  }, [csPage, csDays, csSearch, csExperimentOnly]);

  const fetchRtCalls = useCallback(async (page = rtPage, days = rtDays, search = rtSearch) => {
    setRtLoading(true);
    setRtError(null);
    try {
      const params = { page, days, endpoint: 'bpost_check' };
      if (search) params.search = search;
      const response = await axiosInstance.get('api/b2b-api-calls/', { params });
      if (response.data.status === 'success') {
        setRtCalls(response.data.results);
        setRtTotalCount(response.data.total_count);
        setRtTotalPages(response.data.total_pages);
      }
    } catch (err) {
      setRtError(err.response?.data?.message || 'Failed to fetch Bpost checker calls');
    } finally {
      setRtLoading(false);
    }
  }, [rtPage, rtDays, rtSearch]);

  const handleDeleteCSRecord = async (recordId) => {
    if (!window.confirm('Delete this checkout call record?')) return;
    try {
      await axiosInstance.delete(`api/b2b-api-calls/${recordId}/`);
      fetchCSCalls();
    } catch (err) {
      console.error('Failed to delete checkout record:', err);
    }
  };

  const fetchCSCountryStats = useCallback(async (days = csDays) => {
    setCsStatsLoading(true);
    try {
      const response = await axiosInstance.get('api/dashboard/stats/', { params: { days } });
      if (response.data.status === 'success') {
        setCsCountryStats(response.data.checkout_country_comparison || []);
        const hourly = response.data.checkout_hourly_by_country || null;
        setCsHourlyByCountry(hourly);
        if (hourly?.top_countries) setCsHourlyVisible(new Set(hourly.top_countries));
      }
    } catch (err) {
      console.error('Failed to fetch checkout country stats:', err);
    } finally {
      setCsStatsLoading(false);
    }
  }, [csDays]);

  const handleBatchSync = async (days = 7, retryErrors = false) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axiosInstance.post('api/analyze-packing/batch-sync/', {
        days,
        limit: 200,
        skip_slack: true,
        retry_errors: retryErrors,
      });
      setSyncResult(response.data.summary);
      // Refresh from page 1 after sync (new records appear first)
      setHistoryPage(1);
      fetchAnalysisRecords(1, historySearch, showLossesOnly);
    } catch (err) {
      setSyncResult({ error: err.response?.data?.message || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleCustomSimulate = (products, country, postalCode) => {
    const data = {
      order_name: 'CUSTOM',
      order_type: 'custom',
      total_items: products.reduce((sum, p) => sum + p.quantity * (p.packaging_boxes ? p.packaging_boxes.length : 1), 0),
      shipping_address: { country, zip: postalCode },
      total_shipping_cost: 0,
      products,
    };
    setOrderData(data);
    setOrderNumber('CUSTOM');
    setIsDraftOrder(false);
    setCustomModalOpen(false);
    setPackingSolution(null);
    autoCalculateAfterLoad.current = true;
  };

  const handleReplayB2BCall = async (call) => {
    const req = call.request_data || {};
    const items = req.items || [];
    if (items.length === 0) return;

    setActiveTab(0);
    setLoading(true);
    setError(null);
    setOrderData(null);
    setPackingSolution(null);

    try {
      const response = await axiosInstance.post('api/orders/resolve-variants/', {
        items,
        country: req.country || call.country || '',
        postal_code: req.postal_code || call.postal_code || '',
      });
      setOrderData(response.data);
      setOrderNumber('B2B-REPLAY');
      setIsDraftOrder(false);
      autoCalculateAfterLoad.current = true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resolve B2B variant data');
    } finally {
      setLoading(false);
    }
  };

  const handleReplayCSCall = async (call) => {
    const req = call.request_data || {};
    const items = (req.items || []).map(i => ({
      variant_id: String(i.variant_id || ''),
      quantity: i.quantity || 1,
    }));
    if (items.length === 0) return;

    // Resolve country from destination ISO2 or fall back to call.country
    const country = call.country || '';
    const postalCode = req.destination?.postal_code || call.postal_code || '';

    setActiveTab(0);
    setLoading(true);
    setError(null);
    setOrderData(null);
    setPackingSolution(null);

    try {
      const response = await axiosInstance.post('api/orders/resolve-variants/', {
        items,
        country,
        postal_code: postalCode,
      });
      setOrderData(response.data);
      setOrderNumber('CHECKOUT-REPLAY');
      setIsDraftOrder(false);
      autoCalculateAfterLoad.current = true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resolve checkout variant data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteB2BRecord = async (recordId) => {
    if (!window.confirm('Delete this B2B API call record?')) return;
    setDeletingB2bRecord(recordId);
    try {
      await axiosInstance.delete(`api/b2b-api-calls/${recordId}/`);
      fetchB2BCalls();
    } catch (err) {
      console.error('Failed to delete B2B record:', err);
    } finally {
      setDeletingB2bRecord(null);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Delete this record and permanently exclude this order from future syncs (Sync / Retry Errors)? Un-ignore later from the "Ignored orders" panel.')) return;
    setDeletingRecord(recordId);
    try {
      const response = await axiosInstance.delete(`api/packing-analysis-records/${recordId}/`);
      if (response.data.status === 'success') {
        // Re-fetch current page to keep pagination in sync
        fetchAnalysisRecords(historyPage, historySearch, showLossesOnly);
        // Order is now on the ignore list — refresh it if the panel is open
        if (ignoredOpen) fetchIgnoredOrders();
      }
    } catch (err) {
      setHistoryError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeletingRecord(null);
    }
  };

  const [rerunningRecord, setRerunningRecord] = useState(null);
  const handleRerunRecord = async (record) => {
    setRerunningRecord(record.id);
    try {
      await axiosInstance.post('api/analyze-packing/', {
        order_number: record.order_number,
        skip_slack: true,
        delete_existing: true,
      });
      fetchAnalysisRecords(historyPage, historySearch, showLossesOnly);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to re-run analysis');
    } finally {
      setRerunningRecord(null);
    }
  };

  // Bulk selection & refresh
  const [selectedRecordIds, setSelectedRecordIds] = useState(new Set());
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { found, processed, success, errors, skipped }

  // Ignored orders (permanently excluded from batch sync / retry-errors)
  const [ignoredOrders, setIgnoredOrders] = useState([]);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [ignoredLoading, setIgnoredLoading] = useState(false);
  const [removingIgnored, setRemovingIgnored] = useState(null);

  const fetchIgnoredOrders = async () => {
    setIgnoredLoading(true);
    try {
      const res = await axiosInstance.get('api/ignored-orders/');
      setIgnoredOrders(res.data?.results || []);
    } catch {
      setIgnoredOrders([]);
    } finally {
      setIgnoredLoading(false);
    }
  };

  const handleUnignore = async (orderNumber) => {
    setRemovingIgnored(orderNumber);
    try {
      await axiosInstance.delete('api/ignored-orders/', { params: { order_number: orderNumber } });
      setIgnoredOrders(prev => prev.filter(o => o.order_number !== orderNumber));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to un-ignore order');
    } finally {
      setRemovingIgnored(null);
    }
  };

  const toggleRecordSelection = (id) => {
    setSelectedRecordIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (records) => {
    const visibleIds = records.map(r => r.id);
    const allSelected = visibleIds.every(id => selectedRecordIds.has(id));
    setSelectedRecordIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        // unselect all visible
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedRecordIds(new Set());

  const handleBulkRefresh = async () => {
    if (selectedRecordIds.size === 0) return;
    const orderNumbers = analysisRecords
      .filter(r => selectedRecordIds.has(r.id))
      .map(r => r.order_number);
    if (orderNumbers.length === 0) return;

    setBulkRefreshing(true);
    setBulkResult(null);
    try {
      const response = await axiosInstance.post('api/analyze-packing/refresh/', {
        order_numbers: orderNumbers,
      });
      setBulkResult(response.data?.summary || null);
      clearSelection();
      fetchAnalysisRecords(historyPage, historySearch, showLossesOnly);
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk refresh failed');
    } finally {
      setBulkRefreshing(false);
    }
  };

  const [pushingSlack, setPushingSlack] = useState(null);
  const handlePushToSlack = async (record) => {
    setPushingSlack(record.id);
    try {
      const response = await axiosInstance.post('api/analyze-packing/', {
        order_number: record.order_number,
        force_slack: true,
      });
      const data = response.data;
      if (data.slack_sent) {
        // Update the record in the local state to show slack_sent
        setAnalysisRecords(prev => prev.map(r =>
          r.id === record.id ? { ...r, slack_sent: true } : r
        ));
      } else {
        alert('Slack notification could not be sent. Check Slack configuration.');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to push to Slack');
    } finally {
      setPushingSlack(null);
    }
  };

  const handleQuickCheckoutPreview = async (record) => {
    if (!record.order_number) return;
    const raw = String(record.order_number);
    const withoutHash = raw.startsWith('#') ? raw.substring(1) : raw;
    setQuickPreviewLoading(record.order_number);
    try {
      const response = await axiosInstance.get(`api/orders/${encodeURIComponent(withoutHash)}/`);
      const od = response.data || {};
      setQuickPreview({
        orderNumber: raw,
        items: (od.products || []).map(p => ({
          name: p.title || p.name,
          sku: p.sku || '',
          variant_id: p.variant_id || '',
          quantity: p.quantity,
          // Checkout sees VAT-inclusive, pre-discount line prices (Shopify's
          // subtotal_price); the simulator's unit_price is ex VAT.
          price: (p.original_unit_price_incl_tax ?? p.unit_price_incl_tax ?? p.unit_price) || 0,
          unit_cost: p.cost,
          dimensions: p.dimensions ? {
            length_cm: p.dimensions.length, width_cm: p.dimensions.width,
            height_cm: p.dimensions.height, weight_kg: p.weight,
          } : undefined,
          packaging_boxes: p.packaging_boxes || [],
        })),
        country: od.shipping_address?.country || record.shipping_country || '',
        postalCode: od.shipping_address?.zip || '',
        // The cart total as Shopify sends it (incl. VAT, post-discount): the
        // franco decision is taken on that number. #189866: a EUR 345 cart
        // previewed at EUR 285.12 ex VAT showed no Free Shipping.
        orderTotal: od.checkout_total_eur ?? (od.products || []).reduce((sum, p) => sum + (((p.unit_price_incl_tax ?? p.unit_price) || 0) * p.quantity), 0),
      });
    } catch (err) {
      setError(err.response?.data?.error || `Could not load order ${raw} for the checkout preview.`);
    } finally {
      setQuickPreviewLoading(null);
    }
  };

  const handleLoadFromHistory = (record) => {
    if (!record.order_number) return;
    autoCalculateAfterLoad.current = true;
    setOrderNumber(record.order_number);
    setActiveTab(0);
    handleLookupOrder(record.order_number);
  };

  useEffect(() => {
    if (autoCalculateAfterLoad.current && orderData && !loading && !calculating) {
      const allHaveDims = orderData.products.every(p => p.dimensions);
      autoCalculateAfterLoad.current = false;
      if (allHaveDims) handleCalculatePacking();
    }
  }, [orderData, loading, calculating]);

  // Fetch history when page changes
  useEffect(() => { fetchAnalysisRecords(historyPage, historySearch, showLossesOnly, historyBrand, historyProductName, historyCarrier); }, [historyPage]);

  // Debounce search and filter changes: reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setHistoryPage(1);
      fetchAnalysisRecords(1, historySearch, showLossesOnly, historyBrand, historyProductName, historyCarrier);
    }, 300);
    return () => clearTimeout(timer);
  }, [historySearch, showLossesOnly, showSplitOnly, historyBrand, historyProductName, historyCarrier]);

  // Fetch B2B API calls when tab is selected or filters change
  useEffect(() => {
    if (activeTab === 2) fetchB2BCalls(b2bPage, b2bDays, b2bView, b2bSearch, b2bPinnedCall);
  }, [activeTab, b2bPage, b2bDays, b2bView, b2bPinnedCall]);

  // Debounced B2B search
  useEffect(() => {
    if (activeTab !== 2) return;
    const timer = setTimeout(() => {
      setB2bPage(1);
      fetchB2BCalls(1, b2bDays, b2bView, b2bSearch, b2bPinnedCall);
    }, 300);
    return () => clearTimeout(timer);
  }, [b2bSearch]);

  // Fetch Checkout calls when tab is selected or filters change
  useEffect(() => {
    if (activeTab === 3) {
      fetchCSCalls(csPage, csDays, csSearch);
      fetchCSCountryStats(csDays);
    }
  }, [activeTab, csPage, csDays, csExperimentOnly]);

  // Debounced Checkout search
  useEffect(() => {
    if (activeTab !== 3) return;
    const timer = setTimeout(() => {
      setCsPage(1);
      fetchCSCalls(1, csDays, csSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [csSearch]);

  // Fetch Returny calls when tab is selected or filters change
  useEffect(() => {
    if (activeTab === 4) fetchRtCalls(rtPage, rtDays, rtSearch);
  }, [activeTab, rtPage, rtDays]);

  // Debounced Returny search
  useEffect(() => {
    if (activeTab !== 4) return;
    const timer = setTimeout(() => {
      setRtPage(1);
      fetchRtCalls(1, rtDays, rtSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [rtSearch]);

  const TAB_NAMES = ['simulation', 'shopify', 'b2b', 'checkout', 'returny'];

  useEffect(() => {
    if (hasAutoTriggered.current) return;
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const idx = TAB_NAMES.indexOf(tabParam);
      if (idx >= 0) setActiveTab(idx);
    }
    // ?call=<id> — a B2B API call record, which is how Quoty points at the
    // exact simulation it ran. A quotation often has no draft order yet, so
    // there is nothing else to name the shipment by; the logged call is the
    // one handle that always exists. Open the B2B tab pinned to that row
    // (fetched by id, so it shows even when older than the date window or
    // outside the B2B view) and expanded. « Copy shareable link » makes these.
    const callId = searchParams.get('call');
    if (callId && /^[0-9]+$/.test(callId)) {
      hasAutoTriggered.current = true;
      const idx = TAB_NAMES.indexOf('b2b');
      if (idx >= 0) setActiveTab(idx);
      setB2bView('b2b');
      setB2bPage(1);
      setB2bPinnedCall(Number(callId));
      setB2bExpandedRow(Number(callId));
      setSearchParams({}, { replace: true });
      return;
    }
    const orderId = searchParams.get('orderId') || searchParams.get('order');
    const draftOrderId = searchParams.get('draftOrderId');
    if (draftOrderId) {
      hasAutoTriggered.current = true;
      handleLookupOrder(draftOrderId, true);
      setSearchParams({}, { replace: true });
    } else if (orderId) {
      hasAutoTriggered.current = true;
      handleLookupOrder(orderId, false);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const [copiedTab, setCopiedTab] = useState(false);
  const copyTabLink = () => {
    const url = activeTab === 2 && b2bPinnedCall
      ? `${window.location.origin}/packing-simulator?call=${b2bPinnedCall}`
      : `${window.location.origin}/packing-simulator?tab=${TAB_NAMES[activeTab]}`;
    navigator.clipboard.writeText(url);
    setCopiedTab(true);
    setTimeout(() => setCopiedTab(false), 2000);
  };

  const isPalletBin = (name) => name.includes('PLT-') || name.toUpperCase().includes('PALLET');
  const getStrategyLabel = (s) => {
    if (s?.bundle_view) {
      const { bundles, pieces_per_bundle: per } = s.bundle_view;
      return `${bundles} bundle${bundles !== 1 ? 's' : ''} of ${per}`;
    }
    if (!s?.bin_types) return `${s?.bins_used?.length || 0} boxes`;
    const binTypes = Object.keys(s.bin_types);
    const total = Object.values(s.bin_types).reduce((a, b) => a + b, 0);
    const hasPallet = binTypes.some(isPalletBin);
    const hasDirect = binTypes.some(b => b.includes('Direct'));
    if (total === 1) return hasDirect ? 'Direct shipping' : hasPallet ? '1 pallet' : '1 box';
    const parts = [];
    const boxes = binTypes.filter(b => !isPalletBin(b) && !b.includes('Direct')).reduce((a, b) => a + s.bin_types[b], 0);
    const pallets = binTypes.filter(isPalletBin).reduce((a, b) => a + s.bin_types[b], 0);
    const direct = binTypes.filter(b => b.includes('Direct')).reduce((a, b) => a + s.bin_types[b], 0);
    if (boxes > 0) parts.push(`${boxes} box${boxes !== 1 ? 'es' : ''}`);
    if (pallets > 0) parts.push(`${pallets} pallet${pallets !== 1 ? 's' : ''}`);
    if (direct > 0) parts.push(`${direct} direct`);
    return parts.join(', ') || `${total} ${hasPallet ? 'pallets' : 'boxes'}`;
  };

  const totalPages = Math.max(1, Math.ceil(historyTotalCount / HISTORY_PAGE_SIZE));
  const showingFrom = historyTotalCount === 0 ? 0 : (historyPage - 1) * HISTORY_PAGE_SIZE + 1;
  const showingTo = Math.min(historyPage * HISTORY_PAGE_SIZE, historyTotalCount);

  return (
    <div className="px-6 py-8 max-w-none">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="h-7 w-7 text-primary" />
          Packing Simulator
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enter a Shopify order number or draft order ID to view products and simulate optimal packing.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6 flex gap-1">
        <button
          onClick={() => setActiveTab(0)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 0 ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Play className="h-4 w-4" /> Simulation
        </button>
        <button
          onClick={() => setActiveTab(1)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 1 ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
          Shopify
          {!historyLoading && historyTotalCount > 0 && (
            <span className="ml-1 rounded-full bg-emerald-500 text-white px-1.5 py-0.5 text-xs">{historyTotalCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab(2)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 2 ? 'border-violet-500 text-violet-600 dark:text-violet-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          {b2bLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          B2B
          {!b2bLoading && b2bTotalCount > 0 && (
            <span className="ml-1 rounded-full bg-violet-500 text-white px-1.5 py-0.5 text-xs">{b2bTotalCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab(3)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 3 ? 'border-amber-500 text-amber-600 dark:text-amber-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          {csLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
          Checkout
          {!csLoading && csTotalCount > 0 && (
            <span className="ml-1 rounded-full bg-amber-500 text-white px-1.5 py-0.5 text-xs">{csTotalCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab(4)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 4 ? 'border-rose-500 text-rose-600 dark:text-rose-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          {rtLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
          Returny
          {!rtLoading && rtTotalCount > 0 && (
            <span className="ml-1 rounded-full bg-rose-500 text-white px-1.5 py-0.5 text-xs">{rtTotalCount}</span>
          )}
        </button>
        <button
          onClick={copyTabLink}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          title="Copy link to this tab"
        >
          {copiedTab ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Link2 className="h-3.5 w-3.5" />}
          {copiedTab ? 'Copied!' : 'Share'}
        </button>
      </div>

      {/* Simulation Tab */}
      {activeTab === 0 && (
        <>
          {/* Order Lookup */}
          <div className="rounded-xl border border-border bg-card p-5 mb-4 shadow-sm">
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" /> Order Lookup
            </h2>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  placeholder="e.g., #1234, LAMP-1234, D1917 (draft), or S50942 (Odoo)"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookupOrder()}
                  disabled={loading}
                />
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-xs text-muted-foreground">Shopify order, draft order, or Odoo S-order</p>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={useOdooData}
                      onChange={(e) => setUseOdooData(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-border accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">Use Odoo data</span>
                  </label>
                </div>
              </div>
              <Button onClick={() => handleLookupOrder()} disabled={loading || !orderNumber.trim()} className="h-10">
                {loading ? <ShippySpinner size={18} className="mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {loading ? 'Looking up...' : 'Lookup'}
              </Button>
              {orderData && (
                <Button variant="outline" onClick={handleReset} disabled={loading} className="h-10">
                  <RefreshCw className="h-4 w-4 mr-2" /> Reset
                </Button>
              )}
              <Button variant="outline" onClick={() => setCustomModalOpen(true)} disabled={loading} className="h-10">
                <Plus className="h-4 w-4 mr-2" /> Custom
              </Button>
            </div>
          </div>

          {/* Dimension push result */}
          {pushNotice && (
            <div className={`mb-4 rounded-md border px-4 py-3 text-sm ${pushNotice.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-200'
              : 'border-red-200 bg-red-50 text-red-800 dark:bg-red-950/20 dark:border-red-800 dark:text-red-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="flex-1">{pushNotice.message}</span>
                {!pushNotice.pending && (
                  <button onClick={() => setPushNotice(null)}><X className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              <div className="flex items-start justify-between gap-3">
                <pre className="whitespace-pre-wrap m-0 font-sans flex-1">{error}</pre>
                <button onClick={() => { setError(null); setOrderCandidates([]); }}><X className="h-4 w-4" /></button>
              </div>
              {orderCandidates.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {orderCandidates.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { setError(null); setOrderCandidates([]); handleLookupOrder(c.name?.replace(/^#/, ''), false); }}
                      className="inline-flex items-center rounded-full bg-white dark:bg-red-900/30 border border-red-300 dark:border-red-700 px-3 py-1 text-xs font-medium text-red-900 dark:text-red-100 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                      title={c.id}
                    >
                      Use {c.name || c.id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Order Details */}
          {orderData && (
            <div className="rounded-xl border border-border bg-card p-5 mb-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold">Order Details</h2>
                <div className="flex gap-1.5 flex-wrap items-center">
                  {(isDraftOrder || orderData.order_type === 'draft_order') && (
                    <span className="rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">Draft Order</span>
                  )}
                  <button
                    onClick={async () => {
                      if (orderData.order_type === 'odoo_order') {
                        window.open(`${config.odooBaseUrl}/web#id=${orderData.id}&model=sale.order&view_type=form`, '_blank');
                        return;
                      }
                      try {
                        const res = await axiosInstance.get(`api/odoo/sale-order-lookup/${encodeURIComponent(orderData.order_name.replace('#', ''))}/`);
                        if (res.data?.url) {
                          window.open(res.data.url, '_blank');
                        }
                      } catch (err) {
                        alert(err.response?.data?.message || 'Order not found in Odoo');
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-xs font-medium hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors cursor-pointer"
                    title="Open in Odoo"
                  >
                    Odoo
                    <ExternalLink className="h-3 w-3" />
                  </button>
                  {orderData.id && orderData.id.includes('gid://') ? (
                    <a
                      href={`https://admin.shopify.com/store/lamptwist-live/${orderData.id.includes('DraftOrder') ? 'draft_orders' : 'orders'}/${orderData.id.replace(/gid:\/\/shopify\/(Draft)?Order\//, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-primary/30 text-primary px-2 py-0.5 text-xs font-medium hover:bg-primary/10 transition-colors"
                    >
                      Order: {orderData.order_name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : orderData.order_type !== 'odoo_order' ? (
                    <span className="rounded-full border border-primary/30 text-primary px-2 py-0.5 text-xs font-medium">
                      Order: {orderData.order_name}
                    </span>
                  ) : null}
                  <button
                    onClick={() => copyOrderUrl(orderData.order_name)}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                    title="Copy shareable URL"
                  >
                    {copiedUrl === orderData.order_name ? <><Check className="h-3 w-3 text-emerald-500" /> Copied!</> : <><Link2 className="h-3 w-3" /> Copy URL</>}
                  </button>
                  <span className="rounded-full border border-border px-2 py-0.5 text-xs">{orderData.total_items} items</span>
                  {orderData.shipping_address?.country && (
                    <span className="rounded-full border border-emerald-300 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-xs">
                      Shipping to: {orderData.shipping_address.country}
                    </span>
                  )}
                </div>
              </div>

              {/* Warnings (e.g. missing dimensions from Odoo) */}
              {orderData.warnings?.length > 0 && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 mb-4">
                  {orderData.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {w}
                    </p>
                  ))}
                </div>
              )}

              {/* The Odoo order differs from Shopify: these lines follow Odoo */}
              {orderData.odoo_line_changes && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 mb-4 text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
                  <p className="font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    {(orderData.odoo_line_changes.removed?.length || orderData.odoo_line_changes.added?.length || orderData.odoo_line_changes.changed?.length)
                      ? 'This order differs from Shopify, packed as on the Odoo order'
                      : 'Could not fully compare this order with Odoo, packed as on Shopify'}
                  </p>
                  {orderData.odoo_line_changes.removed?.map((l, i) => <p key={`r${i}`}>Removed: {l.shopify}× {l.title}</p>)}
                  {orderData.odoo_line_changes.added?.map((l, i) => <p key={`a${i}`}>Added: {l.odoo}× {l.title}</p>)}
                  {orderData.odoo_line_changes.changed?.map((l, i) => <p key={`c${i}`}>Quantity {l.shopify} to {l.odoo}: {l.title}</p>)}
                  {orderData.odoo_line_changes.unresolved?.map((t, i) => <p key={`u${i}`}>{t}</p>)}
                </div>
              )}

              {/* Margin Analysis */}
              {(() => {
                const totalValue = orderData.products.reduce((sum, p) => sum + ((p.unit_price || 0) * p.quantity), 0);
                const totalCost = orderData.products.reduce((sum, p) => sum + ((p.cost || 0) * p.quantity), 0);
                const commission = orderData.professional_commission || 0;
                const margin = totalValue - totalCost - commission;
                const marginPercent = totalValue > 0 ? (margin / totalValue) * 100 : 0;
                const shippingCharged = orderData.total_shipping_cost || 0;
                const maxShipping = margin + shippingCharged;
                if (totalValue > 0 && totalCost > 0) {
                  return (
                    <div className={`rounded-lg p-4 mb-4 ${margin > 0 ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-orange-50 dark:bg-orange-950/20'}`}>
                      <p className="text-sm font-semibold mb-2">Order Margin Analysis</p>
                      <div className={`grid grid-cols-2 ${commission > 0 ? 'sm:grid-cols-6' : 'sm:grid-cols-5'} gap-3`}>
                        <div>
                          <p className="text-xs text-muted-foreground">Order Value</p>
                          <p className="text-lg font-mono">{formatCurrency(totalValue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Order Cost</p>
                          <p className="text-lg font-mono">{formatCurrency(totalCost)}</p>
                        </div>
                        {commission > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground">Commission</p>
                            <p className="text-lg font-mono text-amber-600">-{formatCurrency(commission)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground">Gross Margin</p>
                          <p className={`text-lg font-mono font-bold ${margin > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(margin)}</p>
                          <p className={`text-xs ${margin > 0 ? 'text-emerald-600' : 'text-red-600'}`}>({marginPercent.toFixed(1)}%)</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Shipping Charged</p>
                          <p className="text-lg font-mono">{formatCurrency(shippingCharged)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Max Shipping Cost</p>
                          <p className={`text-lg font-mono font-bold ${maxShipping > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(maxShipping)}</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <hr className="border-border mb-4" />

              {/* Products Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-2 py-2 text-left font-medium w-16">Image</th>
                      <th className="px-2 py-2 text-left font-medium">Product</th>
                      <th className="px-2 py-2 text-left font-medium">SKU</th>
                      <th className="px-2 py-2 text-center font-medium">Qty</th>
                      <th className="px-2 py-2 text-right font-medium">Price</th>
                      <th className="px-2 py-2 text-right font-medium">Cost</th>
                      <th className="px-2 py-2 text-right font-medium">Margin</th>
                      <th className="px-2 py-2 text-left font-medium">Dimensions (cm)</th>
                      <th className="px-2 py-2 text-left font-medium">Weight</th>
                      <th className="px-2 py-2 text-center font-medium" title="Free-to-use stock RIGHT NOW (PIM mirror, refreshed every minute) — NOT the stock at the moment of purchase">
                        Stock <span className="font-normal text-[10px] text-muted-foreground">(now)</span>
                      </th>
                      <th className="px-2 py-2 text-center font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData.products.map((product) => {
                      const dimSrc = product.dimension_source || product.dim_source;
                      const isFallback = dimSrc && dimSrc !== 'direct' && dimSrc !== 'odoo';
                      return (
                      <tr key={product.id} className={`border-b border-border hover:bg-muted/30 ${isFallback ? ((dimSrc === 'product_fallback' || dimSrc === 'variant_fallback') ? 'bg-blue-50/50 dark:bg-blue-950/20' : dimSrc === 'brand_average' ? 'bg-orange-50/50 dark:bg-orange-950/20' : dimSrc === 'global_average' ? 'bg-red-50/50 dark:bg-red-950/20' : '') : ''}`}>
                        <td className="px-2 py-2">
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-12 h-12 object-cover rounded border border-border" onError={(e) => e.target.style.display = 'none'} />
                          ) : (
                            <div className="w-12 h-12 rounded border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">N/A</div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <p className="font-medium text-xs">{product.title}</p>
                          {product.variant_title && product.variant_title !== 'Default Title' && (
                            <p className="text-xs text-muted-foreground">Variant: {product.variant_title}</p>
                          )}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs">{product.sku || 'N/A'}</td>
                        <td className="px-2 py-2 text-center">
                          <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-bold">{product.quantity}</span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono text-xs">{product.unit_price ? `${product.unit_price.toFixed(2)}` : '-'}</td>
                        <td className="px-2 py-2 text-right font-mono text-xs">{product.cost ? `${product.cost.toFixed(2)}` : '-'}</td>
                        <td className="px-2 py-2 text-right">
                          {product.unit_price && product.cost ? (
                            <div>
                              <p className={`font-mono text-xs font-bold ${(product.unit_price - product.cost) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {((product.unit_price - product.cost) * product.quantity).toFixed(2)}
                              </p>
                              <p className="text-[10px] text-muted-foreground">{(((product.unit_price - product.cost) / product.unit_price) * 100).toFixed(1)}%</p>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </td>
                        <td className="px-2 py-2">
                          {editingProduct === getProductKey(product) ? (
                            <div className="flex gap-1">
                              <input type="number" placeholder="W" value={editDimensions.width} onChange={(e) => setEditDimensions({ ...editDimensions, width: e.target.value })} className="w-12 rounded border border-border px-1 py-0.5 text-xs" />
                              <input type="number" placeholder="H" value={editDimensions.height} onChange={(e) => setEditDimensions({ ...editDimensions, height: e.target.value })} className="w-12 rounded border border-border px-1 py-0.5 text-xs" />
                              <input type="number" placeholder="L" value={editDimensions.length} onChange={(e) => setEditDimensions({ ...editDimensions, length: e.target.value })} className="w-12 rounded border border-border px-1 py-0.5 text-xs" />
                            </div>
                          ) : product.dimensions ? (
                            <div>
                              {product.packaging_boxes && product.packaging_boxes.length > 1 ? (
                                <div className="space-y-0.5">
                                  {product.packaging_boxes.map((b, i) => (
                                    <div key={i} className="font-mono text-xs">
                                      <span className="text-muted-foreground mr-1">#{i + 1}</span>
                                      {(b.width || 0).toFixed ? b.width.toFixed(0) : b.width} x {(b.height || 0).toFixed ? b.height.toFixed(0) : b.height} x {(b.length || 0).toFixed ? b.length.toFixed(0) : b.length}
                                    </div>
                                  ))}
                                  <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0 text-[10px] font-semibold">
                                    {product.packaging_boxes.length} boxes
                                  </span>
                                </div>
                              ) : (
                                <>
                                  <span className="font-mono text-xs">{product.dimensions.width.toFixed(0)} x {product.dimensions.height.toFixed(0)} x {product.dimensions.length.toFixed(0)}</span>
                                  {(() => {
                                    const src = product.dimension_source || product.dim_source;
                                    if (!src || src === 'direct' || src === 'odoo') return null;
                                    const isVariantFallback = src === 'product_fallback' || src === 'variant_fallback';
                                    const label = isVariantFallback ? 'variant fallback'
                                      : src === 'brand_average' ? 'brand avg'
                                      : src === 'global_average' ? 'global avg'
                                      : src.startsWith('boxopedia') ? 'boxopedia'
                                      : src.startsWith('packaging_data') ? 'pkg data'
                                      : src;
                                    const colorClass = isVariantFallback
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                      : src === 'brand_average'
                                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                      : src === 'global_average'
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                                    return (
                                      <span className={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${colorClass}`}>
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-red-600 font-medium">No dims</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {editingProduct === getProductKey(product) ? (
                            <input type="number" placeholder="kg" value={editDimensions.weight} onChange={(e) => setEditDimensions({ ...editDimensions, weight: e.target.value })} className="w-14 rounded border border-border px-1 py-0.5 text-xs" />
                          ) : (
                            <span className="font-mono text-xs">{product.weight ? product.weight.toFixed(2) : '-'}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {product.transfer_status ? (() => {
                            const ts = product.transfer_status;
                            const freeNote = `Order-level status from this order's Odoo transfers (not global stock). Current free stock: ${product.inventory ?? 'unknown'}.`;
                            const fmtQ = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
                            const STYLES = {
                              delivered: ['Delivered', 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', 'All units left the warehouse for this order.'],
                              partially_delivered: [`${ts.qty_delivered}/${ts.qty_ordered} delivered`, 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', 'Part of this line already shipped for this order.'],
                              ready_to_ship: ['Ready to ship', 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', 'Picked or reserved on the OUT transfer of this order.'],
                              in_warehouse: ['In stock · reserved', 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', 'PICK move assigned: physically in stock, reserved for this order.'],
                              partially_reserved: [`${fmtQ(ts.qty_ready)}/${fmtQ(ts.qty_ordered - (ts.qty_delivered || 0))} in stock`, 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', `Only part of this line is reserved for this order; ${fmtQ(ts.qty_ordered - (ts.qty_delivered || 0) - ts.qty_ready)} still ${Number(ts.qty_from_rfq) > 0 ? 'awaited from a supplier order' : 'awaited'}. The line cannot ship complete yet.`],
                              waiting_on_supplier: ['Awaiting supplier', 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', 'Sourced from a purchase order that has not arrived yet.'],
                              forecasted: ['Forecasted', 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', 'Expected from forecasted supply, nothing reserved yet.'],
                              unavailable: ['No stock', 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', 'Nothing reserved, ordered or forecasted for this line.'],
                            };
                            const [label, cls, hint] = STYLES[ts.status] || ['?', 'bg-muted text-muted-foreground', ''];
                            return (
                              <span className={`inline-flex items-center rounded-full ${cls} px-2 py-0.5 text-[10px] font-medium`} title={`${hint} ${freeNote}`}>
                                {label}
                              </span>
                            );
                          })() : product.inventory == null ? (
                            <span className="text-xs text-muted-foreground/50" title="No live stock figure for this variant (no PIM match or sync inactive)">—</span>
                          ) : product.inventory >= (product.quantity || 1) ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium" title="Current free stock (live PIM mirror), not the stock at the moment of purchase">
                              {product.inventory} in stock
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium" title="Current free stock (live PIM mirror), not the stock at the moment of purchase — below the ordered quantity">
                              {product.inventory} of {product.quantity || 1}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {editingProduct === getProductKey(product) ? (
                            <div className="flex flex-col gap-1 items-center">
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" className="h-6 text-xs px-2" onClick={() => handleSaveDimensions(getProductKey(product))}>Save</Button>
                                <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={handleCancelEdit}>Cancel</Button>
                              </div>
                              {pushCapability?.checking ? (
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">checking push targets...</span>
                              ) : pushCapability && (pushCapability.odoo || pushCapability.shopify) ? (
                                <div className="flex flex-col gap-0.5 items-center">
                                  <label
                                    className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer whitespace-nowrap"
                                    title={pushCapability.odoo_direct
                                      ? 'This line has no Shopify variant, but the product was found in Odoo. Write the dimensions to its Odoo packaging record (verify the match below).'
                                      : pushCapability.odoo
                                      ? 'Also write these dimensions to the product\'s packaging record in Odoo (Boxopedia source of truth). Fragile / pallet flags stay untouched.'
                                      : 'This variant is not linked in Odoo yet. Write the dimensions to the Shopify custom.packaging metafield instead, the fallback the packaging sync reads.'}
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-3 w-3"
                                      checked={pushEnabled}
                                      onChange={(e) => setPushEnabled(e.target.checked)}
                                    />
                                    Push to {pushCapability.odoo ? 'Odoo' : 'Shopify'}
                                  </label>
                                  {pushCapability.odoo_direct && pushCapability.candidates?.length > 0 && (
                                    pushCapability.candidates.length === 1 ? (
                                      <span
                                        className="text-[10px] text-muted-foreground max-w-[160px] truncate"
                                        title={`Odoo match: ${pushCapability.candidates[0].name}${pushCapability.candidates[0].sku ? ` [${pushCapability.candidates[0].sku}]` : ''}`}
                                      >
                                        → {pushCapability.candidates[0].name}
                                      </span>
                                    ) : (
                                      <select
                                        className="text-[10px] rounded border border-border bg-background px-1 py-0.5 max-w-[170px]"
                                        value={pushOdooChoice || ''}
                                        onChange={(e) => setPushOdooChoice(Number(e.target.value))}
                                        title="Multiple Odoo products match this line. Pick the right one before pushing."
                                      >
                                        {pushCapability.candidates.map(c => (
                                          <option key={c.odoo_id} value={c.odoo_id}>
                                            {c.name}{c.sku ? ` [${c.sku}]` : ''}
                                          </option>
                                        ))}
                                      </select>
                                    )
                                  )}
                                </div>
                              ) : pushCapability ? (
                                <span
                                  className="text-[10px] text-muted-foreground whitespace-nowrap"
                                  title="These dimensions apply to this simulation only and cannot be saved upstream."
                                >
                                  simulation only ({pushCapability.reason || 'variant not found'})
                                </span>
                              ) : null}
                            </div>
                          ) : product.dimensions ? (
                            <div className="flex flex-col gap-1 items-center">
                              {isItemOversized(product.dimensions, product.weight || 0) ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-xs" title="Too large for any bin/pallet">
                                  <AlertTriangle className="h-3 w-3" /> Oversized
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-xs">
                                  <CheckCircle className="h-3 w-3" /> OK
                                </span>
                              )}
                              {isFallback && (
                                <Button size="sm" className="h-6 text-[10px] px-2" variant="outline" onClick={() => handleStartEdit(product)}>Edit Dims</Button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1 items-center">
                              <Button size="sm" className="h-6 text-[10px] px-2" variant="outline" onClick={() => handleStartEdit(product)}>Add Dims</Button>
                              <Button size="sm" className="h-6 text-[10px] px-2" variant="ghost" onClick={() => handleApplyFallbackDimensions(product)} disabled={applyingFallbackFor === getProductKey(product)}>
                                {applyingFallbackFor === getProductKey(product) ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Fallback'}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>

              {/* Packing Solution Section */}
              <div className="mt-6">
                <hr className="border-border mb-4" />
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold">Packing Solution</h3>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCalculatePacking}
                      disabled={calculating || orderData.products.some(p => !p.dimensions)}
                    >
                      {calculating ? <ShippySpinner size={18} className="mr-2" /> : <Package className="h-4 w-4 mr-2" />}
                      {calculating ? 'Packing...' : 'Generate Packing Solution'}
                    </Button>
                    {packingSolution && (
                      <div className="relative" ref={pdfMenuRef}>
                        <Button
                          variant="outline"
                          onClick={() => setPdfMenuOpen(o => !o)}
                          disabled={!!pdfDownloading}
                          aria-haspopup="menu"
                          aria-expanded={pdfMenuOpen}
                        >
                          {pdfDownloading
                            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            : <FileDown className="h-4 w-4 mr-2" />}
                          PDF Report
                          <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                        </Button>
                        {pdfMenuOpen && (
                          <div
                            role="menu"
                            className="absolute right-0 mt-1 z-30 min-w-[14rem] rounded-md border border-border bg-card shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1"
                          >
                            <button
                              role="menuitem"
                              onClick={() => handleDownloadPDF('customer')}
                              className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-start gap-2"
                            >
                              <Send className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium">Customer copy</p>
                                <p className="text-[11px] text-muted-foreground leading-tight">Packing layout, dims &amp; weights. No prices.</p>
                              </div>
                            </button>
                            <div className="border-t border-border" />
                            <button
                              role="menuitem"
                              onClick={() => handleDownloadPDF('internal')}
                              className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-start gap-2"
                            >
                              <FileDown className="h-4 w-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium">Internal copy</p>
                                <p className="text-[11px] text-muted-foreground leading-tight">Full report incl. costs, surcharges &amp; alternatives.</p>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {packingSolution && (
                      <Button variant="outline" onClick={handleEnterComparison}>
                        <ArrowLeftRight className="h-4 w-4 mr-2" /> Compare Scenarios
                      </Button>
                    )}
                    {orderData?.products?.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setCheckoutPreviewOpen(true)}
                        title="Run the real carrier service on this cart and show the rates a customer would see, plus the ones that were withheld"
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" /> Checkout Preview
                      </Button>
                    )}
                    {packingSolution && orderNumber && (
                      <Button
                        variant="outline"
                        disabled={replacingRecord}
                        onClick={async () => {
                          setReplacingRecord(true);
                          setReplaceSuccess(false);
                          try {
                            await axiosInstance.post('api/analyze-packing/', {
                              order_number: orderNumber,
                              skip_slack: true,
                              delete_existing: true,
                            });
                            setReplaceSuccess(true);
                            setTimeout(() => setReplaceSuccess(false), 2000);
                          } catch (err) {
                            alert(err.response?.data?.message || 'Failed to replace record');
                          } finally {
                            setReplacingRecord(false);
                          }
                        }}
                      >
                        {replacingRecord ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : replaceSuccess ? <Check className="h-4 w-4 mr-2 text-emerald-500" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        {replaceSuccess ? 'Record Updated' : 'Replace Record'}
                      </Button>
                    )}
                  </div>
                </div>

                {orderData.products.some(p => !p.dimensions) && (
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                    <strong>Missing Dimensions:</strong> {orderData.products.filter(p => !p.dimensions).length} product(s) missing dimensions. Use "Add Dims" or "Fallback".
                  </div>
                )}



                {/* Animated packing loader */}
                {calculating && !packingSolution && (
                  <ShippyPackingLoader />
                )}

                {/* Notices — single calm block consolidating the
                    operational warnings (many-parcels swap/warning + large
                    order 3D-collapse). Each notice is one muted row inside
                    one neutral container — no competing banners. */}
                {packingSolution && (() => {
                  const swap = packingSolution.many_parcels_swap;
                  const warn = packingSolution.many_parcels_warning;
                  const opsActive = !!(swap || warn);
                  const selectedNow = strategiesState[activeStrategyTab];
                  const selBins = selectedNow?.bins_used || [];
                  const selTotalItems = selBins.reduce((s, b) => s + (b.items?.length || 0), 0);
                  const largeOrder = selBins.length > 8 || selTotalItems > 60;
                  if (!opsActive && !largeOrder) return null;
                  return (
                    <div className="mb-4 rounded-md border border-border bg-muted/30 divide-y divide-border text-xs">
                      {opsActive && (
                        <div className="flex items-start gap-2 px-3 py-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                          <p className="text-foreground/80">
                            <strong className="text-foreground">Many-parcels ops rule</strong>{' '}
                            — {(swap || warn).parcel_count} parcels, limit {(swap || warn).threshold}.{' '}
                            {swap ? (
                              <>Pallet selected ({formatCurrency(swap.pallet_cost)}) instead of box ({formatCurrency(swap.box_cost)}) — within 2.5× cost guard.</>
                            ) : warn.no_pallet_alt ? (
                              <>No pallet alternative available — logistics will repack manually.</>
                            ) : (
                              <>Pallet exceeds 2.5× cost guard — kept as boxes, expect manual repack.</>
                            )}
                          </p>
                        </div>
                      )}
                      {largeOrder && (
                        <div className="flex items-start gap-2 px-3 py-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                          <p className="text-foreground/80">
                            <strong className="text-foreground">Large order</strong>{' '}
                            — {selBins.length} bins, {selTotalItems} items. 3D previews collapsed for performance; click <span className="font-medium">Show 3D</span> on any box.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Decision strip — the verdict for the selected scenario in
                    one scannable row: parcels, cost, carrier, margin, charged
                    vs cost, stock. Alerts stay above; options and detail
                    live below. */}
                {packingSolution && strategiesState.length > 0 && (() => {
                  const sel = strategiesState[activeStrategyTab] || strategiesState[0];
                  const bins = sel?.bins_used || [];
                  const fullCost = Number(sel?.total_cost ?? packingSolution?.total_cost ?? 0);
                  const cutSavings = Math.max(0, Math.min(getStrategyCutSavings(sel), fullCost));
                  const cost = fullCost - cutSavings;
                  const carriers = [...new Set(bins.map(b => b.carrier_name).filter(Boolean))]
                    .map(n => String(n).split(' (')[0]);
                  const totalValue = orderData.products.reduce((sum, p) => sum + ((p.unit_price || 0) * p.quantity), 0);
                  const totalCost = orderData.products.reduce((sum, p) => sum + ((p.cost || 0) * p.quantity), 0);
                  const shippingCharged = orderData.total_shipping_cost || 0;
                  const marginPct = totalCost > 0
                    ? grossMargin({ value: totalValue, cost: totalCost, commission: orderData.professional_commission || 0, charged: shippingCharged, shipping: cost }).pct
                    : null;
                  const marginOk = marginPct != null && marginPct >= HEALTHY_MARGIN_PCT;
                  const chargeDelta = shippingCharged - cost;
                  const IN_STOCK = new Set(['delivered', 'partially_delivered', 'ready_to_ship', 'in_warehouse']);
                  let stockOk = 0, stockKnown = 0;
                  orderData.products.forEach(p => {
                    if (p.transfer_status) { stockKnown++; if (IN_STOCK.has(p.transfer_status.status)) stockOk++; }
                    else if (p.inventory != null) { stockKnown++; if (p.inventory >= (p.quantity || 1)) stockOk++; }
                  });
                  const nLines = orderData.products.length;
                  return (
                    <div className="mb-3 rounded-lg border border-border bg-card px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                      <span className="inline-flex items-baseline gap-1.5">
                        <BoxIcon className="h-4 w-4 self-center text-muted-foreground" />
                        <span className="font-semibold">{bins.length}</span>
                        <span className="text-muted-foreground text-xs">{bins.length === 1 ? 'parcel' : 'parcels'}</span>
                        <span className="font-bold">€{formatCurrency(cost)}</span>
                        {carriers.length > 0 && <span className="text-xs text-muted-foreground">via {carriers.join(' + ')}</span>}
                      </span>
                      {marginPct != null && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${marginOk ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                          {marginOk ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                          Margin {marginPct.toFixed(1)}%{marginOk ? '' : ' — below 20%'}
                        </span>
                      )}
                      {shippingCharged > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Charged €{formatCurrency(shippingCharged)}{' '}
                          <span className={chargeDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            ({chargeDelta >= 0 ? '+' : ''}{formatCurrency(chargeDelta)})
                          </span>
                        </span>
                      )}
                      {stockKnown > 0 && (
                        <span className={`text-xs ${stockOk === nLines ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          Stock {stockOk}/{nLines} lines{stockKnown < nLines ? ` (${nLines - stockKnown} unknown)` : ''}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Shipping experiment running for this country: what the
                    control and test groups pay for this cart (read-only). */}
                {shippingExperiment && (
                  <div className="mb-3">
                    <ExperimentNotice block={shippingExperiment} mode="simulator" />
                  </div>
                )}

                {/* Forced carrier — the customer paid for a specific carrier at
                    checkout (multi-carrier pilot), so the engine's cheapest
                    answer below is NOT what ships. Show who was picked and
                    what they cost us, next to the cheapest. */}
                {forcedCarrier && (() => {
                  const short = (n) => String(n || '').split(' (')[0];
                  const d = forcedCarrier.delta || 0;
                  return (
                    <div className="mb-3 rounded-lg border border-orange-300 bg-orange-50/70 dark:bg-orange-950/20 dark:border-orange-800 px-4 py-3">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Truck className="h-4 w-4 shrink-0 text-orange-700 dark:text-orange-400" />
                        <strong className="text-sm text-orange-900 dark:text-orange-200">
                          Customer chose {short(forcedCarrier.carrier_name)} at checkout
                        </strong>
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-orange-200 text-orange-900 dark:bg-orange-900 dark:text-orange-200">ships this carrier</span>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        <div className="rounded-md border border-orange-300 dark:border-orange-700 bg-card px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Their carrier</div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold">€{formatCurrency(forcedCarrier.total_cost)}</span>
                            <span className="text-xs text-muted-foreground">{short(forcedCarrier.carrier_name)}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {d === 0 ? 'same as the cheapest' : `${d > 0 ? '+' : '−'}€${formatCurrency(Math.abs(d))} vs cheapest`}
                          </div>
                        </div>
                        <div className="rounded-md border border-border bg-card px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Customer paid</div>
                          <div className="font-bold">€{formatCurrency(forcedCarrier.customer_paid)}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">shipping on the order</div>
                        </div>
                        <div className="rounded-md border border-border bg-card px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Recorded</div>
                          <div className="font-bold">€{formatCurrency(forcedCarrier.recorded_cost)}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">analysis of {forcedCarrier.order_number}</div>
                        </div>
                      </div>
                      <div className="text-[11px] text-orange-900/80 dark:text-orange-200/80 mt-2">
                        The solution below is the engine's cheapest answer. Ops ships {short(forcedCarrier.carrier_name)}: the customer paid for it and the price they saw was calculated on it.
                      </div>
                    </div>
                  );
                })()}

                {/* Single-carrier vs multi-carrier — this order's optimal
                    parcelization prices each parcel at its own cheapest
                    carrier, which can mix carriers. Ops ships one carrier, so
                    show what the cheapest single carrier costs and the delta.
                    Informational (checkout still uses cheapest-per-parcel). */}
                {singleCarrier && (() => {
                  const short = (n) => String(n || '').split(' (')[0];
                  const delta = singleCarrier.delta || 0;
                  return (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Truck className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                        <strong className="text-sm text-amber-900 dark:text-amber-200">This order splits across carriers</strong>
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200">ops ships one carrier</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div className="rounded-md border border-border bg-card px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Multi-carrier (cheapest per parcel)</div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold">€{formatCurrency(singleCarrier.mixed_total)}</span>
                            <span className="text-xs text-muted-foreground">{singleCarrier.mixed_carriers.map(short).join(' + ')}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">what checkout quotes today</div>
                        </div>
                        <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-card px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Single carrier (cheapest)</div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold">€{formatCurrency(singleCarrier.single_total)}</span>
                            <span className="text-xs text-muted-foreground">{short(singleCarrier.single_carrier_name)}</span>
                            <span className={`text-xs font-semibold ${delta > 0.005 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                              {delta >= 0 ? '+' : ''}€{formatCurrency(delta)}
                            </span>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">what ops pays on one carrier</div>
                        </div>
                      </div>
                      {singleCarrier.candidates?.length > 1 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {singleCarrier.candidates.map(c => (
                            <span key={c.carrier_id} className={`inline-flex items-baseline gap-1 rounded border px-1.5 py-0.5 text-[11px] ${c.carrier_id === singleCarrier.single_carrier_id ? 'border-amber-400 bg-amber-100 dark:bg-amber-900/40 font-medium' : 'border-border bg-muted/30'}`}>
                              {short(c.carrier_name)} €{formatCurrency(c.total_cost)}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-1.5 text-[11px] text-muted-foreground">
                        Same boxes, priced end-to-end (incl. surcharges) under each carrier. Informational: checkout and B2B still quote the cheapest-per-parcel total above.
                      </div>
                    </div>
                  );
                })()}

                {/* Options panel — every alternative way to ship this order
                    (other carriers, pickup point, split shipment) in one
                    neutral container; rows expand on click. Informational:
                    the colored per-topic banners this replaces are gone. */}
                {(carrierAlternatives?.alternatives?.length > 0 || pickupSolution?.eligible || splitPreview?.eligible) && (() => {
                  const toggle = (k) => setExpandedOptions(prev => ({ ...prev, [k]: !prev[k] }));
                  const shortCarrier = (n) => {
                    if (!n) return 'Standard';
                    const m = String(n).match(/^([^(]+)\((.*)\)$/);
                    if (!m) return String(n).split(' (')[0];
                    const svc = m[2].trim().split(/[\s(]/)[0];
                    return svc && svc.toLowerCase() !== 'default' ? `${m[1].trim()} ${svc}` : m[1].trim();
                  };
                  const OptionRow = ({ id, icon, title, badge, summary, children }) => (
                    <div>
                      <button type="button" onClick={() => toggle(id)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors">
                        <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${expandedOptions[id] ? 'rotate-90' : ''}`} />
                        {icon}
                        <span className="text-sm font-medium shrink-0">{title}</span>
                        {badge}
                        <span className="text-xs text-muted-foreground truncate">{summary}</span>
                      </button>
                      {expandedOptions[id] && <div className="px-3 pb-3 pl-9 text-sm">{children}</div>}
                    </div>
                  );
                  const ca = carrierAlternatives;
                  const caRows = (ca?.alternatives || []).map(a => {
                    const m = carrierMatrix?.carriers?.find(r => r.carrier_id === a.carrier_id);
                    const best = (m && !m.same_parcelization && m.best_total < a.total_cost - 0.005) ? m : null;
                    return { ...a, best };
                  });
                  const caStdBest = (() => {
                    const m = carrierMatrix?.carriers?.find(r => r.carrier_name === ca?.standard_carrier);
                    return (m && !m.same_parcelization && ca && m.best_total < ca.standard_total - 0.005) ? m : null;
                  })();
                  const anyBest = !!caStdBest || caRows.some(r => r.best);
                  return (
                    <div className="mb-3 rounded-lg border border-border bg-card divide-y divide-border">
                      {ca?.alternatives?.length > 0 && (
                        <OptionRow
                          id="carriers"
                          icon={<Truck className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          title="Other carriers"
                          badge={ca.checkout_live ? (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-600 text-white shrink-0">offered at checkout</span>
                          ) : null}
                          summary={`${shortCarrier(ca.standard_carrier)} €${formatCurrency(ca.standard_total)} chosen · ${caRows.slice(0, 2).map(a => `${shortCarrier(a.carrier_name)} ${a.delta >= 0 ? '+' : ''}${formatCurrency(a.delta)}`).join(' · ')}${caRows.length > 2 ? ` · +${caRows.length - 2} more` : ''}`}
                        >
                          <div className="flex flex-wrap gap-1.5">
                            <button type="button" onClick={() => { setActiveStrategyTab(0); setTimeout(() => strategyTabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
                              title="Show the chosen solution (strategy tab 1)"
                              className="inline-flex items-baseline gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 hover:bg-primary/10 transition-colors">
                              <span className="text-xs font-medium">{shortCarrier(ca.standard_carrier)}</span>
                              <span className="font-semibold">€{formatCurrency(ca.standard_total)}</span>
                              <span className="text-[10px] uppercase tracking-wide opacity-60">chosen</span>
                              {caStdBest && (
                                <span className="text-[11px] opacity-75">· best €{formatCurrency(caStdBest.best_total)} as {caStdBest.bin_summary}</span>
                              )}
                            </button>
                            {caRows.map(a => {
                              const activeView = strategiesState[activeStrategyTab]?.carrier_view;
                              const isOpen = activeView?.carrier_id === a.carrier_id;
                              const loading = carrierViewLoading === a.carrier_id;
                              return (
                              <span key={a.carrier_id} className="inline-flex items-stretch">
                              <button type="button" disabled={!!carrierViewLoading}
                                onClick={() => openCarrierView(a.carrier_id)}
                                title={(a.not_offered_at_checkout ? `Not offered at checkout: ${a.not_offered_reason}. ` : '') + `Open the chosen parcelization priced on ${a.carrier_name} as a strategy tab`}
                                className={`inline-flex items-baseline gap-1.5 border px-2 py-1 transition-colors hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:cursor-wait ${a.best ? 'rounded-l-md' : 'rounded-md'} ${isOpen && activeView?.kind !== 'best' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-300' : a.not_offered_at_checkout ? 'border-dashed border-border bg-transparent opacity-70' : 'border-border bg-muted/30'}`}>
                                {loading ? <Loader2 className="h-3 w-3 animate-spin self-center" /> : <Truck className={`h-3 w-3 self-center ${isOpen ? 'text-indigo-600' : 'text-muted-foreground'}`} />}
                                <span className="text-xs">{shortCarrier(a.carrier_name) || a.label}</span>
                                <span className="font-medium">€{formatCurrency(a.total_cost)}</span>
                                <span className={`text-[11px] ${a.delta > 0 ? 'text-muted-foreground' : 'text-emerald-700 dark:text-emerald-400 font-medium'}`}>
                                  {a.delta >= 0 ? '+' : ''}{formatCurrency(a.delta)}
                                </span>
                                {a.checkout_price > 0 && a.handling_fee > 0 && (
                                  <span className="text-[11px] text-indigo-700 dark:text-indigo-300" title={`Cost €${formatCurrency(a.total_cost)} + ${ca.handling_fee_percent}% handling fee €${formatCurrency(a.handling_fee)}`}>
                                    · checkout €{formatCurrency(a.checkout_price)}
                                  </span>
                                )}
                                {a.not_offered_at_checkout && (
                                  <span className="text-[10px] text-muted-foreground">· {a.not_offered_reason}</span>
                                )}
                              </button>
                              {a.best && (
                                <button type="button" disabled={!!carrierViewLoading}
                                  onClick={() => openCarrierView(a.carrier_id, { bestSignature: a.best.bin_signature })}
                                  title={`This carrier is cheaper with other boxes: open ${a.best.bin_summary} priced on ${a.carrier_name} as a strategy tab`}
                                  className={`inline-flex items-baseline gap-1 rounded-r-md border border-l-0 px-2 py-1 text-[11px] transition-colors hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:cursor-wait ${isOpen && activeView?.kind === 'best' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-300 text-indigo-700 dark:text-indigo-300 font-medium' : 'border-border bg-muted/10 opacity-80'}`}>
                                  best €{formatCurrency(a.best.best_total)} as {a.best.bin_summary}
                                </button>
                              )}
                              </span>
                              );
                            })}
                          </div>
                          {carrierViewError && (
                            <div className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{carrierViewError}</div>
                          )}
                          {(() => {
                            const chosen = (packingSolution?.bins_used || []).map(b => b.pallet_chargeable).filter(Boolean);
                            const rows = [
                              ...chosen.map(pc => ({ pc, label: `${shortCarrier(pc.carrier_name)} (chosen)` })),
                              ...caRows.flatMap(a => (a.pallet_chargeable || []).map(pc => ({ pc, label: shortCarrier(pc.carrier_name) }))),
                            ];
                            if (!rows.length) return null;
                            return (
                              <div className="mt-2 rounded-md border border-border bg-muted/20 px-2.5 py-1.5 text-[11px] text-muted-foreground space-y-0.5">
                                <div className="font-medium text-foreground">Pallet weight per carrier (at the billed height)</div>
                                {rows.map((r, i) => <div key={i}><ChargeableLine pc={r.pc} label={r.label} /></div>)}
                                <div>Each carrier bills the highest of these (underlined). Floor space ignores height, so a pallet cut does not lower it. Rules per carrier: Pricing Rules page.</div>
                              </div>
                            );
                          })()}
                          <div className="mt-1.5 text-[11px] text-muted-foreground">
                            Same parcelization under each carrier's rates + surcharges, ex-VAT. Click a carrier to open its priced solution as a strategy tab below.
                            {!ca.checkout_live && ' Informational — checkout uses the cheapest only.'}
                            {caRows.some(r => r.not_offered_at_checkout) && ' Dashed = shown for comparison only; checkout never offers a second line of the same carrier brand.'}
                            {ca.checkout_live && ca.handling_fee_percent > 0
                              && ` Prices shown are our cost; at checkout each alternative carries the ${ca.handling_fee_percent}% handling fee, so the customer pays the "checkout" figure. The fee is revenue and is never recorded as cost.`}
                            {anyBest && ` "best" = cheapest of ${carrierMatrix.strategies_considered} packing scenarios under that carrier (sim-only, checkout unaffected).`}
                          </div>
                        </OptionRow>
                      )}
                      {pickupSolution?.eligible && (
                        <OptionRow
                          id="pickup"
                          icon={<MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          title="Pickup point"
                          summary={`€${formatCurrency(pickupSolution.price)} via GLS ParcelShop · ${pickupSolution.parcels} parcel${pickupSolution.parcels !== 1 ? 's' : ''}`}
                        >
                          {pickupSolution.parcels} parcel{pickupSolution.parcels !== 1 ? 's' : ''} via GLS ParcelShop — <strong>€{formatCurrency(pickupSolution.price)}</strong>{' '}
                          <span className="text-xs text-muted-foreground">
                            (ex-VAT, cheapest compliant parcelization{pickupSolution.bin_types && Object.keys(pickupSolution.bin_types).length ? `: ${Object.entries(pickupSolution.bin_types).map(([n, q]) => `${q}× ${n.replace('CB-', '')}`).join(', ')}` : ''})
                          </span>
                        </OptionRow>
                      )}
                      {splitPreview?.eligible && (
                        <OptionRow
                          id="split"
                          icon={<Scissors className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          title="Split shipment"
                          badge={splitPreview.offered_at_checkout ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium shrink-0">Live at checkout</span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium shrink-0" title={!splitPreview.country_enabled ? 'Country not in PARTIAL_SHIPPING_ENABLED_COUNTRIES' : 'Inventory sync heartbeat missing/stale'}>
                              Preview only {!splitPreview.country_enabled ? '(country not enabled)' : '(inventory sync inactive)'}
                            </span>
                          )}
                          summary={`two shipments €${formatCurrency(splitPreview.partial_total)} vs combined €${formatCurrency(splitPreview.combined_total)} → premium €${formatCurrency(splitPreview.premium)}`}
                        >
                          {splitPreview.source === 'order_transfers' && (
                            <div className="mb-2 text-xs text-muted-foreground" title="Waves are based on this order's own Odoo transfers (reserved/ready lines ship now), not global free stock">
                              Waves from this order's own Odoo transfers.
                            </div>
                          )}
                          <div className="grid md:grid-cols-2 gap-3">
                            {[
                              ['ships_now', 'Ships now — in stock', 'border-emerald-300 dark:border-emerald-800'],
                              ['ships_later', 'Ships later — backorder', 'border-amber-300 dark:border-amber-800'],
                            ].map(([key, label, borderCls]) => {
                              const wave = splitPreview[key];
                              if (!wave) return null;
                              const wbins = wave.solution?.bins_used || [];
                              const binCounts = wbins.reduce((acc, b) => {
                                const name = (b.bin_name || '').split(' #')[0] || 'Box';
                                acc[name] = (acc[name] || 0) + 1;
                                return acc;
                              }, {});
                              return (
                                <div key={key} className={`rounded-md border ${borderCls} bg-muted/20 p-3`}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="font-medium text-xs uppercase tracking-wide">{label}</span>
                                    <span className="text-xs">
                                      {wbins.length} parcel{wbins.length !== 1 ? 's' : ''} · <strong>€{formatCurrency(wave.solution?.total_cost || 0)}</strong>
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {Object.entries(binCounts).map(([name, qty]) => (
                                      <span key={name} className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                                        {qty}× {name.replace('CB-', '')}
                                      </span>
                                    ))}
                                  </div>
                                  <ul className="space-y-0.5 text-xs">
                                    {(wave.lines || []).map((line, i) => (
                                      <li key={i} className="flex items-center justify-between gap-2">
                                        <span className="truncate">{line.quantity}× {line.title}{line.variant_title && line.variant_title !== 'Default Title' ? ` (${line.variant_title})` : ''}</span>
                                        <span className="shrink-0 opacity-70">
                                          {line.status
                                            ? line.status.replace(/_/g, ' ')
                                            : key === 'ships_now' ? `stock: ${line.inventory ?? '?'}` : (line.inventory == null ? 'stock: unknown' : `stock: ${line.inventory}`)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        </OptionRow>
                      )}
                    </div>
                  );
                })()}

                {/* Strategy Tabs */}
                {packingSolution && strategiesState.length > 0 && (() => {
                  const swap = packingSolution.many_parcels_swap;
                  const warn = packingSolution.many_parcels_warning;
                  const parcelLimit = (swap || warn)?.threshold || null;
                  const parcelCountFor = (s) => {
                    const bins = s?.bins_used || [];
                    return bins.filter(b => !isPalletBin(b.bin_name || b.bin_code || '')).length;
                  };
                  return (
                  <div className="mb-4">
                    <div ref={strategyTabsRef} className="flex gap-1 overflow-x-auto border-b border-border scroll-mt-4">
                      {strategiesState.map((s, idx) => {
                        const fullCost = Number(s.total_cost) || 0;
                        const cutSavings = getStrategyCutSavings(s);
                        const effectiveCost = fullCost - cutSavings;
                        const showCut = cutSavings > 0;
                        const cv = s.carrier_view;
                        const label = cv
                          ? `${cv.kind === 'best' ? 'best boxes' : 'same boxes'} on ${shortCarrierName(cv.carrier_name)}`
                          : getStrategyLabel(s);
                        const parcelCount = parcelCountFor(s);
                        const overLimit = parcelLimit != null && parcelCount > parcelLimit;
                        return (
                          <button
                            key={idx}
                            onClick={() => setActiveStrategyTab(idx)}
                            className={`flex flex-col items-center gap-0.5 px-4 py-3 min-w-[120px] text-sm border-b-2 transition-colors shrink-0 ${
                              activeStrategyTab === idx ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50'
                            }`}
                          >
                            <div className="flex items-center gap-1">
                              <span className={`rounded-full px-1.5 py-0 text-[10px] font-bold ${idx === 0 ? 'bg-emerald-100 text-emerald-800' : cv ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-muted text-muted-foreground'}`}>{cv ? <Truck className="h-3 w-3 inline" /> : idx + 1}</span>
                              {showCut ? (
                                <span className="font-bold text-teal-700 dark:text-teal-300 inline-flex items-center gap-1">
                                  <Scissors className="h-3 w-3" />
                                  {formatCurrency(effectiveCost)}
                                </span>
                              ) : (
                                <span className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-primary'}`}>{formatCurrency(fullCost)}</span>
                              )}
                            </div>
                            {showCut && (
                              <span className="text-[10px] text-muted-foreground line-through">
                                {formatCurrency(fullCost)} full box
                              </span>
                            )}
                            <span className={`text-xs font-medium ${cv ? 'text-indigo-700 dark:text-indigo-300' : ''}`}>{label}</span>
                            {overLimit && (
                              <AlertTriangle
                                className="h-3 w-3 text-amber-600 dark:text-amber-400"
                                title={`Exceeds ${parcelLimit}-parcel ops limit (${parcelCount} parcels)`}
                              />
                            )}
                            {(() => {
                              const bins = s.bins_used || [];
                              const carrierNames = [...new Set(bins.map(b => b.carrier_name).filter(Boolean))];
                              const methods = [...new Set(bins.map(b => b.rate_method).filter(Boolean))];
                              const zones = [...new Set(bins.map(b => zoneOf(b.rate_method)).filter(Boolean))];
                              const methodLabel = methods.map(m => m.includes('freight_weight') ? 'pallet by weight' : m.includes('weight') ? 'weight' : m.includes('volumetric') ? 'vol' : m.includes('per-parcel') || m.includes('coli') ? 'parcel' : m.includes('pallet') ? 'pallet' : m.includes('direct') ? 'direct' : m).join(', ');
                              return (carrierNames.length > 0 || methods.length > 0) ? (
                                <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
                                  {carrierNames.join(', ')}{carrierNames.length > 0 && methodLabel ? ' · ' : ''}{methodLabel}
                                  {zones.map(z => (
                                    <span key={z}
                                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                                          title={`This postcode is in ${z}, which carries its own rates — the carrier charges more to deliver here than to the mainland.`}>
                                      <MapPin className="h-2.5 w-2.5" />{z}
                                    </span>
                                  ))}
                                </span>
                              ) : null;
                            })()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  );
                })()}

                {/* Selected Strategy Details */}
                {packingSolution && (() => {
                  const selected = strategiesState[activeStrategyTab];
                  const selectedBins = selected?.bins_used || [];
                  const selectedFullCost = Number(selected?.total_cost ?? packingSolution?.total_cost ?? 0);
                  // Clamp savings to the strategy's pre-cut total. The backend
                  // sanitizes per-bin cut_box_savings before responding, but
                  // a stale field could still slip through (e.g., on a cache
                  // hit from before the backend fix deployed). Without this
                  // clamp, sum(bin.cut_box_savings) > strategy.total_cost
                  // would display as a negative total in the scenario tab
                  // (see #166388, US, May 2026).
                  const selectedCutSavings = Math.max(0, Math.min(getStrategyCutSavings(selected), selectedFullCost));
                  const selectedShowCut = selectedCutSavings > 0;
                  const selectedCost = selectedShowCut ? selectedFullCost - selectedCutSavings : selectedFullCost;
                  return (
                    <div className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">
                            <Package className="h-3 w-3" /> {getStrategyLabel(selected)}
                          </span>
                          {selectedShowCut ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 px-2.5 py-0.5 text-xs font-bold">
                              <Scissors className="h-3 w-3" />
                              {formatCurrency(selectedCost)}
                              <span className="ml-1 text-[10px] font-normal opacity-70 line-through">{formatCurrency(selectedFullCost)}</span>
                              {activeStrategyTab === 0 && <span className="ml-1 text-[10px] font-normal opacity-80">(lowest)</span>}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${activeStrategyTab === 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-muted text-muted-foreground'}`}>
                              {formatCurrency(selectedCost)}{activeStrategyTab === 0 ? ' (lowest)' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Packed <strong>{packingSolution.packed_count}</strong> of <strong>{packingSolution.total_items}</strong> items
                        </p>
                        {selected?.carrier_view && (() => {
                          const cv = selected.carrier_view;
                          const d = cv.delta || 0;
                          return (
                            <p className="text-xs mt-1 flex flex-wrap items-center gap-x-1.5 text-indigo-700 dark:text-indigo-300">
                              <Truck className="h-3 w-3" />
                              <span>
                                {cv.kind === 'best' ? 'The boxes this carrier prefers' : 'The chosen parcelization'}, priced on <strong>{cv.carrier_name}</strong>: its rates, fuel and surcharges.
                                {' '}<strong>{d >= 0 ? '+' : ''}{formatCurrency(d)}</strong> vs the standard solution ({formatCurrency(cv.standard_total)}).
                              </span>
                              <span className="text-muted-foreground">What-if only: checkout, the record and the carrier advice keep the standard solution.</span>
                            </p>
                          );
                        })()}
                        {/* Inline ops-rule chip removed — covered by the
                            consolidated Notices block at the top. */}
                        {selectedShowCut && (
                          <p className="text-xs text-teal-600 dark:text-teal-400 flex items-center gap-1 mt-0.5">
                            <Scissors className="h-3 w-3" />
                            Cutting boxes saves <strong>{formatCurrency(selectedCutSavings)}</strong> · alt. without cutting: {formatCurrency(selectedFullCost)}
                          </p>
                        )}
                      </div>

                      {/* Margin Check */}
                      {(() => {
                        const totalValue = orderData.products.reduce((sum, p) => sum + ((p.unit_price || 0) * p.quantity), 0);
                        const totalCost = orderData.products.reduce((sum, p) => sum + ((p.cost || 0) * p.quantity), 0);
                        const shippingCharged = orderData.total_shipping_cost || 0;
                        if (totalValue <= 0 || totalCost <= 0) return null;

                        const commission = orderData.professional_commission || 0;
                        const actualMarginPct = grossMargin({ value: totalValue, cost: totalCost, commission, charged: shippingCharged, shipping: selectedCost }).pct;
                        const threshold = HEALTHY_MARGIN_PCT;
                        const belowThreshold = actualMarginPct < threshold;
                        const maxShippingFor20 = (1 - threshold / 100) * totalValue - totalCost - commission + shippingCharged;
                        const surchargeNeeded = Math.max(0, selectedCost - maxShippingFor20);
                        const orderCurrency = orderData.currency || 'EUR';
                        const surchargeInLocal = orderCurrency !== 'EUR' ? surchargeNeeded * (eurRates[orderCurrency] || 1) : null;

                        return belowThreshold ? (
                          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 px-4 py-3 mb-4 flex items-start gap-3">
                            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                            <div className="text-sm flex-1">
                              <p className="font-medium text-red-800 dark:text-red-200">
                                Margin alert: <span className="font-bold">{actualMarginPct.toFixed(1)}%</span> — below {threshold}% threshold
                              </p>
                              <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">
                                Shipping cost of <strong>{formatCurrency(selectedCost)}</strong> is too high. Max shipping to keep {threshold}% margin: <strong>{formatCurrency(maxShippingFor20)}</strong>
                              </p>
                              <p className="text-xs text-red-700 dark:text-red-200 mt-1 font-semibold">
                                Surcharge needed: <strong>€{formatCurrency(surchargeNeeded)}</strong>
                                {surchargeInLocal != null && ` / ${CURRENCY_SYMBOLS[orderCurrency] || orderCurrency} ${surchargeInLocal.toFixed(2)}`}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2 text-xs border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
                                onClick={() => setSurchargeModalOpen(true)}
                              >
                                <Send className="h-3 w-3 mr-1" /> Inform Customer
                              </Button>
                            </div>
                          </div>
                        ) : null; /* healthy margin now lives in the decision strip pill */
                      })()}

                      {/* Bins Details — the in-place large-order banner
                          was removed; the consolidated Notices block at
                          the top of the solution covers this. */}
                      {selectedBins.map((bin, index) => (
                        <div key={index} className={`rounded-lg border p-4 mb-3 ${(bin.strategy === 'direct_shipping' || (bin.strategy || '').includes('direct') || bin.bin_name === 'Direct Shipping') ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10' : 'border-border bg-muted/20'}`}>
                          {(() => {
                            const dims = bin.bin_dimensions || bin.dimensions;
                            const [w, h, l] = [dims?.width || 0, dims?.height || 0, dims?.length || 0].sort((a, b) => a - b);
                            // The cut shortens the box's HEIGHT axis, which is not always the
                            // middle of the sorted title: a CB-20x80x20 (height 80) cut to 33
                            // is 20 x 20 x 33, not "20 x 33 x 80" (#193521, 2026-09-30).
                            const [cw, ch, cl] = [dims?.width || 0, bin.used_height ?? (dims?.height || 0), dims?.length || 0].sort((a, b) => a - b);
                            const volWeight = (w * h * l) / 5000;
                            const totalWeight = (bin.items || []).reduce((s, it) => s + (it.weight || 0), 0);
                            const cutInfo = getBinCutInfo(bin);
                            const canCut = cutInfo.canCut;
                            // PALLET CUT (2026-09-16): a pallet is billed on the load it
                            // carries, so show the piece that actually leaves. The price
                            // on the bin is already post-cut, so the full-pallet figure is
                            // the one that gets struck through.
                            const palletCut = bin.pallet_cut_applied ? {
                              height: bin.pallet_cut_height,
                              fullHeight: bin.pallet_cut_full_height,
                              savings: bin.pallet_cut_savings || 0,
                              fullCost: (bin.pallet_cut_original_shipping_cost || 0) + (bin.packaging_cost || 0),
                              volWeight: (w * (bin.pallet_cut_height || 0) * l) / 5000,
                            } : null;
                            const isPallet = isPalletBin(bin.bin_name || '');
                            const isDirect = bin.strategy === 'direct_shipping' || (bin.strategy || '').includes('direct') || bin.bin_name === 'Direct Shipping';
                            const DIRECT_PADDING = 4;
                            const pw = isDirect ? w + DIRECT_PADDING : w;
                            const ph = isDirect ? h + DIRECT_PADDING : h;
                            const pl = isDirect ? l + DIRECT_PADDING : l;
                            const isForcedPallet = isPallet && bin.reason?.includes('shipped_on_pallet');
                            const isWeightRule = isPallet && bin.reason?.includes('weight_over_30kg');
                            const label = isDirect ? 'Direct Shipping' : isPallet ? 'Pallet' : 'Box';
                            return (
                              <>
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-0.5">
                                      {label}
                                      {bin.stacked_count > 1 && (
                                        <span className="ml-1.5 inline-flex items-center rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0 text-[9px] font-semibold normal-case">
                                          {bin.stacked_count}× taped together
                                        </span>
                                      )}
                                      {isWeightRule && (
                                        <span className="ml-1.5 inline-flex items-center rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-1.5 py-0 text-[9px] font-semibold normal-case">
                                          &gt; 30 kg rule
                                        </span>
                                      )}
                                      {isForcedPallet && !isWeightRule && (
                                        <span className="ml-1.5 inline-flex items-center rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0 text-[9px] font-semibold normal-case">
                                          Forced rule
                                        </span>
                                      )}
                                    </p>
                                    <p className="font-bold text-base leading-tight">
                                      {isDirect ? `${pw} x ${ph} x ${pl} cm` : `${w} x ${h} x ${l} cm`}
                                    </p>
                                    {isDirect && (
                                      <p className="text-[10px] text-muted-foreground">
                                        Item: {w} x {h} x {l} cm + packaging (+{DIRECT_PADDING}cm/dim)
                                      </p>
                                    )}
                                    {canCut && (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <Scissors className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                                        <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">
                                          {cw} x {ch} x {cl} cm
                                        </span>
                                        {bin.cut_box_savings > 0 && (
                                          <span className="text-xs text-teal-600 dark:text-teal-400 ml-1">
                                            save {formatCurrency(bin.cut_box_savings)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {palletCut && (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <Scissors className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                                        <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">
                                          {w} x {palletCut.height} x {l} cm
                                        </span>
                                        <span
                                          className="text-[10px] text-muted-foreground"
                                          title={`The load is ${(palletCut.height - PALLET_DECK_CM)}cm high on a ${PALLET_DECK_CM}cm pallet deck, so the carrier bills a ${palletCut.height}cm piece, not the ${palletCut.fullHeight}cm pallet it was chosen from.`}
                                        >
                                          billed height (load + {PALLET_DECK_CM}cm deck)
                                        </span>
                                        {palletCut.savings > 0 && (
                                          <span className="text-xs text-teal-600 dark:text-teal-400 ml-1">
                                            save {formatCurrency(palletCut.savings)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Real: {totalWeight.toFixed(2)}kg &middot; Vol: {volWeight.toFixed(2)}kg
                                      {isDirect && <> &middot; Packed vol: {((pw * ph * pl) / 5000).toFixed(2)}kg</>}
                                      {canCut && <> &middot; Cut vol: {bin.cut_box_volumetric_weight}kg</>}
                                      {palletCut && !bin.pallet_chargeable && <> &middot; <span className="text-teal-700 dark:text-teal-300 font-medium">Billed vol: {palletCut.volWeight.toFixed(2)}kg</span></>}
                                      {bin.pallet_chargeable && <> &middot; <span className="text-teal-700 dark:text-teal-300 font-medium" title={chargeableParts(bin.pallet_chargeable).map(p => p.text).join(' · ')}>Billed: {fmtKg(bin.pallet_chargeable.chargeable_kg)}kg ({bin.pallet_chargeable.basis})</span></>}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium mb-1">
                                      {bin.items_packed || bin.items?.length || 1} items
                                    </span>
                                    {!isDirect && typeof bin.utilization === 'number' && (
                                      <p className="text-xs text-muted-foreground" title={palletCut ? 'Fill of the FULL pallet. The price follows the load, not this number.' : undefined}>
                                        {bin.utilization.toFixed(1)}% util{palletCut ? ' (of full pallet)' : ''}
                                      </p>
                                    )}
                                    {bin.cost != null && (
                                      palletCut ? (
                                        <p className="text-base font-bold text-teal-700 dark:text-teal-300 mt-1 inline-flex flex-col items-end">
                                          <span className="inline-flex items-center gap-1">
                                            <Scissors className="h-3.5 w-3.5" />
                                            {formatCurrency((bin.cost || 0) + (bin.surcharge_amount || 0))}
                                          </span>
                                          <span className="block text-[10px] font-normal text-muted-foreground line-through">
                                            {formatCurrency(palletCut.fullCost + (bin.surcharge_amount || 0))} full pallet
                                          </span>
                                          {bin.surcharge_amount > 0 && (
                                            <span className="block text-[10px] font-normal text-muted-foreground">
                                              incl. +{formatCurrency(bin.surcharge_amount)} surcharge
                                            </span>
                                          )}
                                        </p>
                                      ) : canCut ? (
                                        <p className="text-base font-bold text-teal-700 dark:text-teal-300 mt-1 inline-flex flex-col items-end">
                                          <span className="inline-flex items-center gap-1">
                                            <Scissors className="h-3.5 w-3.5" />
                                            {formatCurrency((cutInfo.cutCost || 0) + (bin.surcharge_amount || 0))}
                                          </span>
                                          <span className="block text-[10px] font-normal text-muted-foreground line-through">
                                            {formatCurrency((bin.cost || 0) + (bin.surcharge_amount || 0))} full box
                                          </span>
                                          {bin.surcharge_amount > 0 && (
                                            <span className="block text-[10px] font-normal text-muted-foreground">
                                              incl. +{formatCurrency(bin.surcharge_amount)} surcharge
                                            </span>
                                          )}
                                        </p>
                                      ) : (
                                        <p className="text-base font-bold text-primary mt-1">
                                          {formatCurrency((bin.cost || 0) + (bin.surcharge_amount || 0))}
                                          {bin.surcharge_amount > 0 && (
                                            <span className="block text-[10px] font-normal text-muted-foreground">
                                              {formatCurrency(bin.cost)} shipping + {formatCurrency(bin.surcharge_amount)} surcharge
                                            </span>
                                          )}
                                        </p>
                                      )
                                    )}
                                    {(bin.rate_method || bin.carrier_name) && (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        {(() => {
                                          const rb = rateBasisBadge(bin.rate_method);
                                          return rb ? (
                                            <span
                                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${rb.cls}`}
                                              title={`Priced on ${rb.label} (${bin.rate_method})`}
                                            >
                                              {rb.label}
                                            </span>
                                          ) : null;
                                        })()}
                                        {zoneOf(bin.rate_method) && (
                                          <span
                                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                                            title={`This postcode is in ${zoneOf(bin.rate_method)}, which carries its own rates — the carrier charges more to deliver here than to the mainland.`}
                                          >
                                            <MapPin className="h-2.5 w-2.5" />
                                            {zoneOf(bin.rate_method)}
                                          </span>
                                        )}
                                        {bin.carrier_name && (
                                          <span className="text-[10px] text-muted-foreground font-medium">{bin.carrier_name}</span>
                                        )}
                                      </div>
                                    )}
                                    {bin.surcharge_labels?.length > 0 && (
                                      <div className={`mt-2 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
                                        bin.surcharge_refused
                                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800'
                                          : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                                      }`}>
                                        <span>{bin.surcharge_refused ? '⛔' : '⚠️'} {bin.surcharge_labels.join(', ')}</span>
                                        {bin.surcharge_amount > 0 && (
                                          <span className="ml-1 font-bold">+{bin.surcharge_amount.toFixed(2)}€</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </>
                            );
                          })()}

                          {/* Packed Items */}
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="px-2 py-1 text-left">#</th>
                                <th className="px-2 py-1 text-left">Product</th>
                                <th className="px-2 py-1 text-left">Variant</th>
                                <th className="px-2 py-1 text-left">Dimensions</th>
                                <th className="px-2 py-1 text-right">Weight</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandRiderBoxes(bin.items).map((item, idx) => (
                                <tr key={idx} className="border-b border-border/50">
                                  <td className="px-2 py-1">{idx + 1}</td>
                                  <td className="px-2 py-1 font-medium">
                                    {item.product_title || item.name}
                                    {item.total_boxes > 1 && (
                                      <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-medium">
                                        Box {item.box_number}/{item.total_boxes}
                                      </span>
                                    )}
                                    {item.stacked_units > 1 && (
                                      <span className="ml-1.5 inline-flex items-center rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 text-[10px] font-medium">
                                        {item.stacked_units}x stacked
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1 text-muted-foreground">{item.variant_title || '-'}</td>
                                  <td className="px-2 py-1 font-mono">
                                    {item.width != null ? `${item.width.toFixed(0)}x${item.height.toFixed(0)}x${item.depth.toFixed(0)}` : '-'}
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono">{item.weight != null ? item.weight.toFixed(2) : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {/* 3D Visualization — lazy-mounted per bin */}
                          {(() => {
                            const totalItems = selectedBins.reduce((s, b) => s + (b.items?.length || 0), 0);
                            const safeMode = selectedBins.length > 8 || totalItems > 60;
                            const overrideKey = `${activeStrategyTab}-${index}`;
                            const override = viz3DOverrides[overrideKey];
                            const isShown = override === undefined ? !safeMode : override;
                            return (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() => setViz3DOverrides(prev => ({ ...prev, [overrideKey]: !isShown }))}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card hover:bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
                                  title={isShown ? 'Hide the 3D preview to save GPU' : 'Render a 3D preview of this box'}
                                >
                                  {isShown ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  {isShown ? 'Hide 3D' : 'Show 3D'}
                                  {(bin.items?.length || 0) > 30 && !isShown && (
                                    <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-400">({bin.items.length} items)</span>
                                  )}
                                </button>
                                {isShown && <PackingVisualization3D bin={bin} animate />}
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Scenario Comparison Panel */}
          {baselineSnapshot && (
            <ScenarioComparisonPanel
              key={baselineSnapshot.key}
              open={comparisonMode}
              baselineOrderData={baselineSnapshot.orderData}
              baselineResult={baselineSnapshot}
              effectiveCost={effectiveStrategyCost}
              onClose={handleExitComparison}
            />
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 1 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search orders..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="w-64"
              />
              <label className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={showLossesOnly} onChange={(e) => setShowLossesOnly(e.target.checked)} className="rounded" />
                Losses only
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={showSplitOnly} onChange={(e) => setShowSplitOnly(e.target.checked)} className="rounded" />
                Split shipments
              </label>
              <button
                type="button"
                onClick={() => setAdvancedFiltersOpen(o => !o)}
                className={`inline-flex items-center gap-1 text-xs rounded px-2 py-1 border ${
                  advancedFiltersOpen || historyBrand || historyProductName || historyCarrier
                    ? 'border-primary/40 bg-primary/5 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted/40'
                }`}
              >
                <Search className="h-3 w-3" />
                Advanced filters
                {(historyBrand || historyProductName || historyCarrier) && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground w-4 h-4 text-[9px] font-bold">
                    {(historyBrand ? 1 : 0) + (historyProductName ? 1 : 0) + (historyCarrier ? 1 : 0)}
                  </span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleBatchSync(7)} disabled={syncing || historyLoading}>
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudDownload className="h-4 w-4 mr-2" />}
                {syncing ? 'Syncing...' : 'Sync Recent Orders'}
              </Button>
              <Button variant="outline" onClick={() => handleBatchSync(30, true)} disabled={syncing || historyLoading}
                className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-950/30">
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Retry Errors
              </Button>
              <Button variant="outline" onClick={() => fetchAnalysisRecords(historyPage, historySearch, showLossesOnly)} disabled={historyLoading}>
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => { const n = !ignoredOpen; setIgnoredOpen(n); if (n) fetchIgnoredOrders(); }}
                className={ignoredOpen ? 'border-primary/40 bg-primary/5 text-primary' : ''}
              >
                <Ban className="h-4 w-4 mr-2" /> Ignored{ignoredOrders.length > 0 ? ` (${ignoredOrders.length})` : ''}
              </Button>
            </div>
          </div>

          {ignoredOpen && (
            <div className="mb-4 rounded-md border border-border bg-muted/20 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <Ban className="h-4 w-4 text-muted-foreground" />
                  Ignored orders
                  <span className="text-xs text-muted-foreground font-normal">— permanently excluded from Sync &amp; Retry Errors</span>
                </span>
                <button onClick={fetchIgnoredOrders} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" disabled={ignoredLoading}>
                  <RefreshCw className={`h-3 w-3 ${ignoredLoading ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>
              {ignoredLoading ? (
                <p className="text-xs text-muted-foreground py-2">Loading…</p>
              ) : ignoredOrders.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No ignored orders. Deleting a record adds its order here so it never gets re-synced.</p>
              ) : (
                <div className="divide-y divide-border max-h-64 overflow-y-auto">
                  {ignoredOrders.map((o) => (
                    <div key={o.order_number} className="flex items-center justify-between py-1.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium">{o.order_number}</span>
                        {o.reason ? <span className="text-xs text-muted-foreground truncate">{o.reason}</span> : null}
                        {o.created_at ? <span className="text-[11px] text-muted-foreground shrink-0">{new Date(o.created_at).toLocaleDateString()}</span> : null}
                      </div>
                      <button
                        onClick={() => handleUnignore(o.order_number)}
                        disabled={removingIgnored === o.order_number}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
                        title="Un-ignore — order can be synced again"
                      >
                        {removingIgnored === o.order_number
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <X className="h-3 w-3" />}
                        Un-ignore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {advancedFiltersOpen && (
            <div className="mb-4 rounded-md border border-border bg-muted/20 px-3 py-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Input
                  placeholder="Brand / vendor (e.g. Ferm Living)"
                  value={historyBrand}
                  onChange={(e) => setHistoryBrand(e.target.value)}
                  className="w-64"
                />
                <Input
                  placeholder="Product name contains..."
                  value={historyProductName}
                  onChange={(e) => setHistoryProductName(e.target.value)}
                  className="w-72"
                />
                <Input
                  placeholder="Carrier (e.g. GLS Belgium, UPS)"
                  value={historyCarrier}
                  onChange={(e) => setHistoryCarrier(e.target.value)}
                  className="w-60"
                  list="carrier-filter-options"
                />
                <datalist id="carrier-filter-options">
                  {[...new Set(analysisRecords.map(r => r.carrier_name).filter(Boolean))].sort().map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                {(historyBrand || historyProductName || historyCarrier) && (
                  <button
                    type="button"
                    onClick={() => { setHistoryBrand(''); setHistoryProductName(''); setHistoryCarrier(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
                <span className="text-[11px] text-muted-foreground ml-auto">
                  Filters orders by brand or product name (line items) and by carrier (case-insensitive, partial match).
                </span>
              </div>
            </div>
          )}

          {syncResult && !syncResult.error && (
            <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center justify-between">
              <span>
                Synced {syncResult.success} new orders ({syncResult.shopify_orders_found} found in Shopify, {syncResult.already_in_db} already in DB{syncResult.retried_errors > 0 ? `, ${syncResult.retried_errors} errors retried` : ''}{syncResult.ignored_skipped > 0 ? `, ${syncResult.ignored_skipped} ignored skipped` : ''}{syncResult.errors > 0 ? `, ${syncResult.errors} errors` : ', no errors'})
              </span>
              <button onClick={() => setSyncResult(null)} className="ml-2"><X className="h-4 w-4" /></button>
            </div>
          )}

          {syncResult?.error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200 flex items-center justify-between">
              <span>Sync failed: {syncResult.error}</span>
              <button onClick={() => setSyncResult(null)} className="ml-2"><X className="h-4 w-4" /></button>
            </div>
          )}

          {historyError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {historyError}
            </div>
          )}

          {/* Bulk-action toolbar — visible only when rows are selected */}
          {selectedRecordIds.size > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 shadow-sm">
              <span className="text-sm font-medium text-foreground">
                {selectedRecordIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleBulkRefresh}
                  disabled={bulkRefreshing}
                  title="Re-run packing analysis (delete & replace) for all selected orders"
                >
                  {bulkRefreshing ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Refreshing…</>
                  ) : (
                    <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh &amp; replace</>
                  )}
                </Button>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={clearSelection}
                  disabled={bulkRefreshing}
                >
                  Clear selection
                </button>
              </div>
              {bulkResult && (
                <span className="ml-auto text-xs text-muted-foreground">
                  Last run: {bulkResult.success}/{bulkResult.found} ok
                  {bulkResult.errors > 0 ? `, ${bulkResult.errors} errors` : ''}
                  {bulkResult.skipped > 0 ? `, ${bulkResult.skipped} skipped` : ''}
                </span>
              )}
            </div>
          )}
          {/* Toast for last bulk result (shown briefly after clearing) */}
          {bulkResult && selectedRecordIds.size === 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
              <CheckCircle className="h-3.5 w-3.5" />
              Refreshed {bulkResult.success}/{bulkResult.found} orders
              {bulkResult.errors > 0 ? ` · ${bulkResult.errors} errors` : ''}
              {bulkResult.skipped > 0 ? ` · ${bulkResult.skipped} skipped` : ''}
              <button
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => setBulkResult(null)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {historyLoading ? (
            <div className="flex items-center justify-center h-40"><ShippyLoader size={56} message="Loading history..." /></div>
          ) : (
            <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    {/* Selection */}
                    <th className="px-3 py-2 text-left font-semibold w-10"></th>
                    {/* Order Info */}
                    <th className="px-3 py-2 text-left font-semibold" colSpan={3}>Order</th>
                    {/* Shipping Cost */}
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}>Shipping Cost</th>
                    {/* Margin */}
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={2}>Margin</th>
                    {/* Packing */}
                    <th className="px-3 py-2 text-left font-semibold border-l border-border/50" colSpan={2}>Packing</th>
                    {/* Meta */}
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}></th>
                  </tr>
                  <tr className="bg-muted/30 border-b border-border text-xs text-muted-foreground">
                    <th className="px-3 py-1.5 text-center font-medium w-10">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer"
                        checked={analysisRecords.length > 0 && analysisRecords.every(r => selectedRecordIds.has(r.id))}
                        ref={el => {
                          if (!el) return;
                          const someSelected = analysisRecords.some(r => selectedRecordIds.has(r.id));
                          const allSelected = analysisRecords.length > 0 && analysisRecords.every(r => selectedRecordIds.has(r.id));
                          el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={() => toggleSelectAllVisible(analysisRecords)}
                        title="Select all visible"
                      />
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium w-8"></th>
                    <th className="px-3 py-1.5 text-left font-medium">Order</th>
                    <th className="px-3 py-1.5 text-left font-medium">Country</th>
                    <th className="px-3 py-1.5 text-right font-medium border-l border-border/50">Charged</th>
                    <th className="px-3 py-1.5 text-right font-medium">Optimal</th>
                    <th className="px-3 py-1.5 text-right font-medium">Diff</th>
                    <th className="px-3 py-1.5 text-right font-medium border-l border-border/50">Gross %</th>
                    <th className="px-3 py-1.5 text-right font-medium">Value</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Solution</th>
                    <th className="px-3 py-1.5 text-left font-medium">Carrier</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Date</th>
                    <th className="px-3 py-1.5 text-right font-medium">Time</th>
                    <th className="px-3 py-1.5 text-center font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {analysisRecords.map((record) => {
                    const diff = record.amount_charged_shopify - record.amount_calculated_optimal;
                    const statusColor = record.status === 'alert'
                      ? 'bg-amber-50/50 dark:bg-amber-950/10'
                      : record.status === 'error'
                        ? 'bg-red-50/50 dark:bg-red-950/10'
                        : '';
                    // Mini cost bar: show proportion of optimal vs charged
                    const maxCost = Math.max(record.amount_charged_shopify, record.amount_calculated_optimal, 1);
                    const chargedPct = (record.amount_charged_shopify / maxCost) * 100;
                    const optimalPct = (record.amount_calculated_optimal / maxCost) * 100;
                    // Parse bin_types for visual pills
                    const binPills = record.bin_types && typeof record.bin_types === 'object'
                      ? Object.entries(record.bin_types)
                      : null;
                    const isSelected = selectedRecordIds.has(record.id);
                    return (
                      <tr
                        key={record.id}
                        className={`border-b border-border hover:bg-muted/30 ${isSelected ? 'bg-primary/5 dark:bg-primary/10' : statusColor}`}
                      >
                        {/* Selection checkbox */}
                        <td className="px-3 py-2.5 text-center w-10">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-input accent-primary cursor-pointer"
                            checked={isSelected}
                            onChange={() => toggleRecordSelection(record.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        {/* Status icon */}
                        <td className="px-3 py-2.5 w-8">
                          {record.status === 'success' && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                          {record.status === 'alert' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          {record.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                        </td>
                        {/* Order number + items badge */}
                        <td className="px-3 py-2.5">
                          <button className="text-primary hover:underline font-semibold text-xs" onClick={() => handleLoadFromHistory(record)}>
                            {record.order_number}
                          </button>
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground font-medium">
                            {record.total_items_count} item{record.total_items_count !== 1 ? 's' : ''}
                          </span>
                        </td>
                        {/* Country */}
                        <td className="px-3 py-2.5 text-xs">
                          <span title={record.shipping_country}>{countryFlag(record.shipping_country)} {record.shipping_country}</span>
                        </td>
                        {/* Charged — with mini bar */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="font-mono text-xs">{formatCurrency(record.amount_charged_shopify)}</span>
                            <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-blue-400 rounded-full" style={{ width: `${chargedPct}%` }} />
                            </div>
                          </div>
                        </td>
                        {/* Optimal — with mini bar */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="font-mono text-xs">{formatCurrency(record.amount_calculated_optimal)}</span>
                            {record.surcharge_total > 0 && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0 text-[9px] font-medium" title={record.surcharge_labels?.join(', ')}>
                                +{record.surcharge_total.toFixed(2)}€ surch.
                              </span>
                            )}
                            <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${optimalPct}%` }} />
                            </div>
                          </div>
                        </td>
                        {/* Diff — colored pill */}
                        <td className="px-3 py-2.5 text-right">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                            diff >= 0
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}>
                            {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
                          </span>
                        </td>
                        {/* Margin % — colored pill */}
                        <td className="px-3 py-2.5 text-right border-l border-border/50">
                          {record.gross_margin_percent != null ? (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                              record.gross_margin_percent >= 25
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : record.gross_margin_percent >= 10
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {record.gross_margin_percent.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        {/* Order value */}
                        <td className="px-3 py-2.5 text-right">
                          {record.total_order_value > 0 ? (
                            <span className="font-mono text-xs">{formatCurrency(record.total_order_value)}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        {/* Solution — bin type pills */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                            {record.shipping_method === 'pickup' && (
                              <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-1.5 py-0.5 text-[9px] font-medium">Pickup</span>
                            )}
                            {record.shipping_method === 'split_shipment' && (
                              <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 text-[9px] font-medium" title={record.shipping_solution}>Split</span>
                            )}
                            {record.shipping_method === 'forced_carrier' && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-medium" title="Customer picked this carrier at checkout — cost recorded is that carrier's, not the cheapest">Forced carrier</span>
                            )}
                            {binPills ? (
                              binPills.map(([name, qty]) => (
                                <span key={name} className="inline-flex items-center rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
                                  {name.replace('CB-', '')}{qty > 1 ? ` x${qty}` : ''}
                                </span>
                              ))
                            ) : (
                              <span className="text-[11px] text-muted-foreground truncate max-w-[180px] block">{record.shipping_solution || '-'}</span>
                            )}
                          </div>
                        </td>
                        {/* Carrier */}
                        <td className="px-3 py-2.5">
                          {record.carrier_name ? (
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">{record.carrier_name}</span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">-</span>
                          )}
                        </td>
                        {/* Date */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(record.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                          <span className="block text-[10px] text-muted-foreground/60">
                            {new Date(record.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        {/* Processing time */}
                        <td className="px-3 py-2.5 text-right">
                          {record.processing_time_ms != null ? (
                            <span className={`font-mono text-[11px] ${record.processing_time_ms > 10000 ? 'text-red-600 font-semibold' : record.processing_time_ms > 5000 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {record.processing_time_ms >= 1000 ? `${(record.processing_time_ms / 1000).toFixed(1)}s` : `${Math.round(record.processing_time_ms)}ms`}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">-</span>
                          )}
                        </td>
                        {/* Actions: two rows of four. The checkout-preview "$" button
                            (2026-09-15) made a single row wider than the card, and the
                            wrapper's horizontal scroll hid the last four icons behind the
                            invisible Mac scrollbar (Geoffrey: "where did all the icons go?"). */}
                        <td className="px-2 py-2.5 text-center w-[112px] min-w-[112px]">
                          <div className="grid grid-cols-4 gap-0.5 justify-items-center w-[96px] mx-auto">
                            <button
                              className="rounded p-1 hover:bg-primary/10 text-primary"
                              onClick={() => handleLoadFromHistory(record)}
                              title="Simulate this order"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="rounded p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                              onClick={() => handleQuickCheckoutPreview(record)}
                              disabled={quickPreviewLoading === record.order_number}
                              title="Checkout preview: the rates this order's customer sees at checkout"
                            >
                              {quickPreviewLoading === record.order_number ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DollarSign className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              className="rounded p-1 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 text-cyan-600"
                              onClick={() => handleRerunRecord(record)}
                              disabled={rerunningRecord === record.id}
                              title="Re-run with latest algorithm"
                            >
                              {rerunningRecord === record.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            </button>
                            {record.order_id && (
                              <a
                                href={`https://admin.shopify.com/store/lamptwist-live/orders/${record.order_id.replace('gid://shopify/Order/', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                                title="View in Shopify"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <button
                              className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                              onClick={() => copyOrderUrl(record.order_number)}
                              title="Copy shareable URL"
                            >
                              {copiedUrl === record.order_number ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Link2 className="h-3.5 w-3.5" />}
                            </button>
                            <a
                              href={`/packing-print?orderNumber=${encodeURIComponent(record.order_number)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded p-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600"
                              title="Export packing solution to PDF"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </a>
                            <button
                              className={`rounded p-1 ${record.slack_sent ? 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30' : 'text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/30'}`}
                              onClick={() => handlePushToSlack(record)}
                              disabled={pushingSlack === record.id}
                              title={record.slack_sent ? 'Sent to Slack — click to resend' : 'Push to Slack'}
                            >
                              {pushingSlack === record.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                              onClick={() => handleDeleteRecord(record.id)}
                              disabled={deletingRecord === record.id}
                              title="Delete record"
                            >
                              {deletingRecord === record.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {analysisRecords.length === 0 && (
                    <tr>
                      <td colSpan={13} className="px-3 py-8 text-center text-muted-foreground">
                        No analysis records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {historyTotalCount > 0 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <p className="text-xs text-muted-foreground">
                  Showing {showingFrom}–{showingTo} of {historyTotalCount.toLocaleString()} records
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                    disabled={historyPage <= 1 || historyLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    let start = Math.max(1, historyPage - 2);
                    let end = Math.min(totalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    if (start > 1) {
                      pages.push(
                        <Button key={1} variant={historyPage === 1 ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setHistoryPage(1)} disabled={historyLoading}>1</Button>
                      );
                      if (start > 2) pages.push(<span key="s1" className="text-xs text-muted-foreground px-1">...</span>);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button key={i} variant={historyPage === i ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setHistoryPage(i)} disabled={historyLoading}>{i}</Button>
                      );
                    }
                    if (end < totalPages) {
                      if (end < totalPages - 1) pages.push(<span key="s2" className="text-xs text-muted-foreground px-1">...</span>);
                      pages.push(
                        <Button key={totalPages} variant={historyPage === totalPages ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setHistoryPage(totalPages)} disabled={historyLoading}>{totalPages}</Button>
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                    disabled={historyPage >= totalPages || historyLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* B2B API Calls Tab */}
      {activeTab === 2 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search order #, country, carrier, IP..."
                value={b2bSearch}
                onChange={(e) => { setB2bPinnedCall(null); setB2bSearch(e.target.value); }}
                className="w-64"
              />
              <div className="inline-flex items-center rounded-md border border-border overflow-hidden h-9">
                {B2B_VIEWS.map((v, i) => {
                  const n = v.endpoints.length
                    ? v.endpoints.reduce((sum, e) => sum + (b2bEndpointCounts[e] || 0), 0)
                    : Object.values(b2bEndpointCounts).reduce((sum, c) => sum + c, 0);
                  const active = b2bView === v.key;
                  return (
                    <button
                      key={v.key}
                      onClick={() => { setB2bPinnedCall(null); setB2bView(v.key); setB2bPage(1); }}
                      title={v.hint}
                      className={`h-full px-3 text-sm transition-colors ${i > 0 ? 'border-l border-border' : ''} ${
                        active
                          ? 'bg-violet-500 text-white font-medium'
                          : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {v.label}
                      {n > 0 && (
                        <span className={`ml-1.5 text-[11px] tabular-nums ${active ? 'text-white/80' : 'text-muted-foreground/70'}`}>
                          {n}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <select
                value={b2bDays}
                onChange={(e) => { setB2bPinnedCall(null); setB2bDays(Number(e.target.value)); setB2bPage(1); }}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value={1}>Last 24h</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
            <Button variant="outline" onClick={() => fetchB2BCalls(b2bPage, b2bDays, b2bView, b2bSearch, b2bPinnedCall)} disabled={b2bLoading}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          {b2bPinnedCall && (
            <div className="mb-4 flex items-center justify-between rounded-md border border-violet-300 bg-violet-50 dark:bg-violet-950/20 dark:border-violet-800 px-4 py-2.5 text-sm text-violet-900 dark:text-violet-200">
              <span>
                {!b2bLoading && b2bCalls.length === 0
                  ? <>Shared call <span className="font-mono">#{b2bPinnedCall}</span> not found · it may have been deleted.</>
                  : <>Showing shared call <span className="font-mono">#{b2bPinnedCall}</span> only.</>}
              </span>
              <Button variant="outline" size="sm" onClick={() => { setB2bPinnedCall(null); setB2bPage(1); }}>
                Show all calls
              </Button>
            </div>
          )}

          {b2bError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {b2bError}
            </div>
          )}

          {b2bLoading ? (
            <div className="flex items-center justify-center h-40"><ShippyLoader size={56} message="Loading B2B API calls..." /></div>
          ) : (
            <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold" colSpan={3}>Request</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}>Shipping Cost</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={2}>Margin</th>
                    <th className="px-3 py-2 text-left font-semibold border-l border-border/50">Packing</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}></th>
                  </tr>
                  <tr className="bg-muted/30 border-b border-border text-xs text-muted-foreground">
                    <th className="px-3 py-1.5 text-left font-medium w-8"></th>
                    <th className="px-3 py-1.5 text-left font-medium">Endpoint</th>
                    <th className="px-3 py-1.5 text-left font-medium">Country</th>
                    <th className="px-3 py-1.5 text-right font-medium border-l border-border/50">Order</th>
                    <th className="px-3 py-1.5 text-right font-medium">Total</th>
                    <th className="px-3 py-1.5 text-center font-medium">Franco</th>
                    <th className="px-3 py-1.5 text-right font-medium border-l border-border/50">Gross %</th>
                    <th className="px-3 py-1.5 text-left font-medium">Source</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Solution</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Date</th>
                    <th className="px-3 py-1.5 text-right font-medium">Time</th>
                    <th className="px-3 py-1.5 text-center font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {b2bCalls.map((call) => (
                    <React.Fragment key={call.id}>
                      <tr
                        className={`border-b border-border hover:bg-muted/30 cursor-pointer ${!call.success ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}
                        onClick={() => setB2bExpandedRow(b2bExpandedRow === call.id ? null : call.id)}
                      >
                        {/* Status icon */}
                        <td className="px-3 py-2.5 w-8">
                          {call.success
                            ? <CheckCircle className="h-4 w-4 text-emerald-500" />
                            : <XCircle className="h-4 w-4 text-red-500" />
                          }
                        </td>
                        {/* Endpoint + items badge */}
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            B2B_ENDPOINT_BADGE[call.endpoint] || B2B_ENDPOINT_BADGE._default
                          }`}>
                            {call.endpoint_display}
                          </span>
                          {call.endpoint === 'carrier_advice' ? (
                            <>
                              {call.request_data?.order_number && (
                                <span className="ml-1.5 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-mono font-medium">
                                  {call.request_data.order_number}
                                </span>
                              )}
                              {call.request_data?.picking && (
                                <span className="ml-1 inline-flex items-center rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 px-1.5 py-0.5 text-[10px] font-mono font-medium" title="Advice for this delivery order only (picking call)">
                                  {call.request_data.picking}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground font-medium">
                              {call.items_count} item{call.items_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </td>
                        {/* Country */}
                        <td className="px-3 py-2.5 text-xs">
                          <span title={call.country}>{countryFlag(call.country)} {call.country || '-'}</span>
                        </td>
                        {/* Order Total */}
                        <td className="px-3 py-2.5 text-right font-mono text-xs border-l border-border/50">
                          {call.order_total != null ? formatCurrency(call.order_total) : '-'}
                        </td>
                        {/* Total Cost (original shipping + packaging, before franco) */}
                        <td className="px-3 py-2.5 text-right">
                          {(() => {
                            const total = call.response_data?.original_shipping_cost;
                            const sc = call.response_data?.surcharge;
                            const pickup = call.response_data?.pickup;
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="font-mono text-xs font-semibold">{total != null ? formatCurrency(total) : '-'}</span>
                                {sc?.total > 0 && (
                                  <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0 text-[9px] font-medium" title={sc.labels?.join(', ')}>
                                    +{sc.total.toFixed(2)}€ surch.
                                  </span>
                                )}
                                {pickup?.price != null && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-1.5 py-0 text-[9px] font-medium" title="GLS pickup-point rate returned at checkout">
                                    <MapPin className="h-2.5 w-2.5" /> {formatCurrency(pickup.price)} pickup
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        {/* Franco */}
                        <td className="px-3 py-2.5 text-center">
                          {call.franco_applied ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">Free</span>
                          ) : call.response_data?.franco?.partial ? (
                            <span className="inline-flex flex-col items-center">
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium">Partial</span>
                              <span className="text-[10px] font-mono text-amber-600 mt-0.5">{formatCurrency(call.response_data.franco.customer_charge)} due</span>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        {/* Margin % */}
                        <td className="px-3 py-2.5 text-right border-l border-border/50">
                          {(() => {
                            const mi = call.response_data?.franco?.margin_info;
                            if (!mi) return <span className="text-xs text-muted-foreground">-</span>;
                            const pct = call.franco_applied ? mi.margin_pct_with_franco
                              : call.response_data?.franco?.partial ? mi.actual_margin_pct
                              : mi.margin_pct_without_franco;
                            if (pct == null) return <span className="text-xs text-muted-foreground">-</span>;
                            return (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                                pct >= 25 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : pct >= 10 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {pct.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                        {/* Source */}
                        <td className="px-3 py-2.5">
                          {call.referer ? (
                            <span className="text-[11px] text-muted-foreground truncate max-w-[120px] block" title={call.referer}>
                              {(() => { try { return new URL(call.referer).hostname; } catch { return call.referer; } })()}
                            </span>
                          ) : call.user_agent ? (
                            <span className="text-[11px] text-muted-foreground truncate max-w-[120px] block" title={call.user_agent}>
                              {call.user_agent.split('/')[0]}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">-</span>
                          )}
                        </td>
                        {/* Bins / Solution */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          {call.endpoint === 'carrier_advice' ? (
                            <div className="flex flex-col gap-0.5 max-w-[240px]">
                              <span className="text-[11px] font-medium">{call.response_data?.advised_carrier || (call.success ? 'no carrier (own shipping)' : '-')}</span>
                              {call.response_data?.recheck_reason === 'switched_cheaper' && (
                                <span className="text-[10px] text-emerald-700 dark:text-emerald-400">switched today (was {call.response_data?.recheck_recorded_carrier}, saves {formatCurrency(call.response_data?.recheck_saving_eur)})</span>
                              )}
                              {call.response_data?.recheck_reason === 'recorded_switched_off' && (
                                <span className="text-[10px] text-amber-700 dark:text-amber-400">recorded carrier switched off for this country (was {call.response_data?.recheck_recorded_carrier})</span>
                              )}
                              {call.response_data?.recheck_reason === 'recorded_refuses' && (
                                <span className="text-[10px] text-amber-700 dark:text-amber-400">recorded carrier refuses today (was {call.response_data?.recheck_recorded_carrier})</span>
                              )}
                              {call.response_data?.carrier_source === 'customer_choice' && (
                                <span className="text-[10px] font-medium text-violet-700 dark:text-violet-400" title="The customer paid for this carrier at checkout (multi-carrier): Odoo must not re-route it">customer's pick, do not re-route</span>
                              )}
                              {call.response_data?.carrier_source && !['cheapest_solution', 'customer_choice'].includes(call.response_data.carrier_source) && (
                                <span className="text-[10px] text-muted-foreground">{{ pickup_point: 'pickup point', split_shipment: 'split shipment (order call)', odoo_simulation: 'Odoo simulation (no Shopify record)', own_shipping: 'own shipping' }[call.response_data.carrier_source] || call.response_data.carrier_source.replace(/_/g, ' ')}</span>
                              )}
                            </div>
                          ) : call.bins_summary && typeof call.bins_summary === 'object' ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {Object.entries(call.bins_summary).map(([name, qty]) => (
                                <span key={name} className="inline-flex items-center rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
                                  {name.replace('CB-', '')}{qty > 1 ? ` x${qty}` : ''}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">{call.total_bins > 0 ? `${call.total_bins} bin${call.total_bins > 1 ? 's' : ''}` : '-'}</span>
                          )}
                        </td>
                        {/* Date */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(call.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                          <span className="block text-[10px] text-muted-foreground/60">
                            {new Date(call.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        {/* Processing time */}
                        <td className="px-3 py-2.5 text-right">
                          {call.processing_time_ms != null ? (
                            <span className={`font-mono text-[11px] ${call.processing_time_ms > 10000 ? 'text-red-600 font-semibold' : call.processing_time_ms > 5000 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {call.processing_time_ms >= 1000 ? `${(call.processing_time_ms / 1000).toFixed(1)}s` : `${Math.round(call.processing_time_ms)}ms`}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">-</span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              className="rounded p-1 hover:bg-primary/10 text-primary"
                              onClick={() => handleReplayB2BCall(call)}
                              title="Simulate this order"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                              title="Copy shareable link"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const url = `${window.location.origin}/packing-simulator?call=${call.id}`;
                                  await navigator.clipboard.writeText(url);
                                  setCopiedUrl(`b2b-link-${call.id}`);
                                  setTimeout(() => setCopiedUrl(null), 2000);
                                } catch (err) {
                                  console.error('Clipboard write failed:', err);
                                }
                              }}
                            >
                              {copiedUrl === `b2b-link-${call.id}` ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Link2 className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                              onClick={() => handleDeleteB2BRecord(call.id)}
                              disabled={deletingB2bRecord === call.id}
                              title="Delete record"
                            >
                              {deletingB2bRecord === call.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {b2bExpandedRow === call.id && (
                        <tr className="border-b border-border">
                          <td colSpan={12} className="px-4 py-3 bg-muted/20">
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div>
                                <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Request</h4>
                                <pre className="bg-muted/50 rounded p-2 overflow-x-auto max-h-48 text-[11px]">
                                  {JSON.stringify(call.request_data, null, 2) || 'No data'}
                                </pre>
                              </div>
                              <div>
                                <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Response</h4>
                                <pre className="bg-muted/50 rounded p-2 overflow-x-auto max-h-48 text-[11px]">
                                  {call.error_message
                                    ? <span className="text-red-600">{call.error_message}</span>
                                    : JSON.stringify(call.response_data, null, 2) || 'No data'
                                  }
                                </pre>
                              </div>
                            </div>
                            <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
                              {call.referer && <p>Source: {call.referer}</p>}
                              {call.user_agent && <p>User-Agent: {call.user_agent}</p>}
                              <p>IP: {call.ip_address || 'unknown'}{call.client_identifier ? ` | Client: ${call.client_identifier}` : ''}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {b2bCalls.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">
                        No B2B API calls found for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {b2bTotalCount > 0 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <p className="text-xs text-muted-foreground">
                  Showing {((b2bPage - 1) * 50) + 1}–{Math.min(b2bPage * 50, b2bTotalCount)} of {b2bTotalCount.toLocaleString()} calls
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setB2bPage(p => Math.max(1, p - 1))}
                    disabled={b2bPage <= 1 || b2bLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {(() => {
                    const pages = [];
                    let start = Math.max(1, b2bPage - 2);
                    let end = Math.min(b2bTotalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    if (start > 1) {
                      pages.push(<Button key={1} variant={b2bPage === 1 ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setB2bPage(1)} disabled={b2bLoading}>1</Button>);
                      if (start > 2) pages.push(<span key="s1" className="text-xs text-muted-foreground px-1">...</span>);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(<Button key={i} variant={b2bPage === i ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setB2bPage(i)} disabled={b2bLoading}>{i}</Button>);
                    }
                    if (end < b2bTotalPages) {
                      if (end < b2bTotalPages - 1) pages.push(<span key="s2" className="text-xs text-muted-foreground px-1">...</span>);
                      pages.push(<Button key={b2bTotalPages} variant={b2bPage === b2bTotalPages ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setB2bPage(b2bTotalPages)} disabled={b2bLoading}>{b2bTotalPages}</Button>);
                    }
                    return pages;
                  })()}
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setB2bPage(p => Math.min(b2bTotalPages, p + 1))}
                    disabled={b2bPage >= b2bTotalPages || b2bLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Checkout (Carrier Service) Tab */}
      {activeTab === 3 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search country, postal code..."
                value={csSearch}
                onChange={(e) => setCsSearch(e.target.value)}
                className="w-64"
              />
              <select
                value={csDays}
                onChange={(e) => { setCsDays(Number(e.target.value)); setCsPage(1); }}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value={1}>Last 24h</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
              <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none" title="Only quotes in a country with a running shipping experiment (response_data.experiment)">
                <input type="checkbox" checked={csExperimentOnly} onChange={(e) => { setCsExperimentOnly(e.target.checked); setCsPage(1); }} className="h-3.5 w-3.5 accent-indigo-600" />
                Experiment quotes only
              </label>
            </div>
            <Button variant="outline" onClick={() => fetchCSCalls(csPage, csDays, csSearch)} disabled={csLoading}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          {csError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {csError}
            </div>
          )}

          {/* Country Overview: Checkout vs Orders (collapsed by default) */}
          {(() => {
            const numCountries = csCountryStats.length;
            return (
              <div className="mb-4 rounded-xl border border-border/70 surface-panel shadow-sm">
                <button
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-muted/20 transition-colors"
                  onClick={() => setCsStatsOpen(!csStatsOpen)}
                >
                  <div className="flex items-center gap-2">
                    <ArrowLeftRight className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="text-sm font-semibold">Checkout vs Orders</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {csStatsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${csStatsOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {csStatsOpen && (
                  numCountries > 0 ? (
                    <div className="border-t border-border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/30 border-b border-border text-[11px] text-muted-foreground">
                            <th className="px-3 py-1.5 text-left font-medium">Country</th>
                            <th className="px-3 py-1.5 text-right font-medium">Checkouts</th>
                            <th className="px-3 py-1.5 text-right font-medium">Unique</th>
                            <th className="px-3 py-1.5 text-right font-medium">Orders</th>
                            <th className="px-3 py-1.5 text-right font-medium">Conv.</th>
                            <th className="px-3 py-1.5 text-right font-medium">Avg Cost</th>
                            <th className="px-3 py-1.5 text-right font-medium">Avg Charged</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csCountryStats.map((row) => {
                            const uniqueBase = row.unique_checkouts || row.checkout_count;
                            const convPct = uniqueBase > 0 ? ((row.orders_count / uniqueBase) * 100) : 0;
                            return (
                              <tr key={row.country} className="border-b border-border/50 hover:bg-muted/20">
                                <td className="px-3 py-1.5 text-xs font-medium">
                                  {countryFlag(row.country)} {row.country}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold">
                                    {row.checkout_count}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold">
                                    {row.unique_checkouts || 0}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-1.5 py-0.5 text-[10px] font-semibold">
                                    {row.orders_count}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right" title={`${row.orders_count} orders / ${uniqueBase} unique checkouts`}>
                                  <span className={`text-[11px] font-semibold ${convPct >= 50 ? 'text-emerald-600 dark:text-emerald-400' : convPct >= 20 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'}`}>
                                    {convPct > 0 ? `${convPct.toFixed(0)}%` : '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-[11px]">
                                  {row.checkout_avg_cost > 0 ? `€${formatCurrency(row.checkout_avg_cost)}` : '-'}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-[11px]">
                                  {row.orders_avg_charged > 0 ? `€${formatCurrency(row.orders_avg_charged)}` : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border-t border-border px-4 py-4 text-center text-xs text-muted-foreground">
                      No data yet for the selected period.
                    </div>
                  )
                )}
              </div>
            );
          })()}

          {/* Hourly Checkout Volume by Country (today, Belgian time) */}
          {csHourlyByCountry && csHourlyByCountry.top_countries?.length > 0 && (() => {
            const { hours, top_countries } = csHourlyByCountry;
            const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];
            return (
              <div className="mb-4 rounded-xl border border-border/70 surface-panel shadow-sm">
                <button
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-muted/20 transition-colors"
                  onClick={() => setCsHourlyOpen(!csHourlyOpen)}
                >
                  <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="text-sm font-semibold">Today's Checkout Volume by Country</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${csHourlyOpen ? 'rotate-180' : ''}`} />
                </button>
                {csHourlyOpen && (() => {
                  const visibleCountries = csHourlyVisible || new Set(top_countries);
                  const toggleCountry = (c) => {
                    setCsHourlyVisible(prev => {
                      const next = new Set(prev || top_countries);
                      if (next.has(c)) next.delete(c); else next.add(c);
                      return next;
                    });
                  };

                  const nowHour = new Date().getHours();
                  const maxLine = Math.max(1, ...hours.flatMap(h =>
                    Object.entries(h.countries).filter(([c]) => visibleCountries.has(c)).map(([, v]) => v)
                  ));

                  // Find the last non-zero hour for each visible country (for end labels)
                  const lastPoints = {};
                  top_countries.forEach((country) => {
                    if (!visibleCountries.has(country)) return;
                    let lastH = -1, lastVal = 0;
                    hours.forEach((h, i) => {
                      const val = h.countries[country] || 0;
                      if (val > 0) { lastH = i; lastVal = val; }
                    });
                    if (lastH >= 0) lastPoints[country] = { h: lastH, val: lastVal };
                  });

                  const visibleCount = [...visibleCountries].length;
                  const W = 750, H = Math.max(200, visibleCount * 16 + 50), PL = 35, PR = 110, PT = 10, PB = 25;
                  const chartW = W - PL - PR, chartH = H - PT - PB;
                  const xStep = chartW / 23;
                  const LINE_H = 14; // min spacing between legend entries

                  return (
                    <div className="border-t border-border px-4 py-3">
                      {/* SVG line chart with right-side legend */}
                      <div className="overflow-x-auto">
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 500 }}>
                          {/* Y axis grid */}
                          {[0, 0.25, 0.5, 0.75, 1].map(f => {
                            const y = PT + chartH * (1 - f);
                            return (
                              <g key={f}>
                                <line x1={PL} y1={y} x2={PL + chartW} y2={y} stroke="currentColor" className="text-border" strokeWidth={0.5} />
                                <text x={PL - 4} y={y + 3} textAnchor="end" className="fill-muted-foreground" fontSize={9}>
                                  {Math.round(maxLine * f)}
                                </text>
                              </g>
                            );
                          })}
                          {/* X axis labels */}
                          {hours.map((h, i) => i % 3 === 0 && (
                            <text key={i} x={PL + i * xStep} y={H - 4} textAnchor="middle" className="fill-muted-foreground" fontSize={9}>
                              {`${h.hour}h`}
                            </text>
                          ))}
                          {/* Current hour marker */}
                          <line
                            x1={PL + nowHour * xStep} y1={PT}
                            x2={PL + nowHour * xStep} y2={PT + chartH}
                            stroke="#f59e0b" strokeWidth={1} strokeDasharray="3,3" opacity={0.5}
                          />
                          {/* Lines per country */}
                          {top_countries.map((country, ci) => {
                            if (!visibleCountries.has(country)) return null;
                            const color = COLORS[ci % COLORS.length];
                            const isFocused = !csFocusCountry || csFocusCountry === country;
                            const isHovered = csHoverCountry?.country === country;
                            const dimmed = !isFocused && !isHovered;
                            const points = hours.map((h, i) => {
                              const val = h.countries[country] || 0;
                              const x = PL + i * xStep;
                              const y = PT + chartH * (1 - val / maxLine);
                              return `${x},${y}`;
                            }).join(' ');
                            return (
                              <g key={country} style={{ opacity: dimmed ? 0.15 : 1, transition: 'opacity 0.15s' }}>
                                <polyline points={points} fill="none" stroke={color} strokeWidth={isHovered ? 3 : 2} strokeLinejoin="round" />
                                {/* Invisible thick hit area for hover */}
                                <polyline
                                  points={points} fill="none" stroke="transparent" strokeWidth={12}
                                  style={{ cursor: 'pointer' }}
                                  onMouseEnter={() => setCsHoverCountry({ country })}
                                  onMouseLeave={() => setCsHoverCountry(null)}
                                />
                                {hours.map((h, i) => {
                                  const val = h.countries[country] || 0;
                                  if (val === 0) return null;
                                  const x = PL + i * xStep;
                                  const y = PT + chartH * (1 - val / maxLine);
                                  return (
                                    <circle key={i} cx={x} cy={y} r={isHovered ? 4 : 3} fill={color}
                                      style={{ cursor: 'pointer' }}
                                      onMouseEnter={() => setCsHoverCountry({ country, x, y, val, hour: h.hour })}
                                      onMouseLeave={() => setCsHoverCountry(null)}
                                    />
                                  );
                                })}
                              </g>
                            );
                          })}
                          {/* Hover tooltip */}
                          {csHoverCountry?.val != null && (() => {
                            const tt = csHoverCountry;
                            const ci = top_countries.indexOf(tt.country);
                            const color = ci >= 0 ? COLORS[ci % COLORS.length] : '#888';
                            const tx = tt.x > PL + chartW / 2 ? tt.x - 8 : tt.x + 8;
                            const anchor = tt.x > PL + chartW / 2 ? 'end' : 'start';
                            return (
                              <g style={{ pointerEvents: 'none' }}>
                                <rect x={anchor === 'end' ? tx - 90 : tx - 4} y={tt.y - 22} width={94} height={18} rx={4}
                                  fill="var(--card)" stroke="var(--border)" strokeWidth={0.5} />
                                <text x={tx} y={tt.y - 9} textAnchor={anchor} fontSize={9} fontWeight="600" fill={color}>
                                  {tt.country}: {tt.val} @ {tt.hour}h
                                </text>
                              </g>
                            );
                          })()}
                          {/* Right-side legend — click to focus/unfocus */}
                          {(() => {
                            const legendItems = top_countries
                              .map((c, i) => {
                                if (!visibleCountries.has(c)) return null;
                                const total = hours.reduce((s, h) => s + (h.countries[c] || 0), 0);
                                return { country: c, color: COLORS[i % COLORS.length], total };
                              })
                              .filter(Boolean);
                            const totalNeeded = legendItems.length * LINE_H;
                            const startY = PT + Math.max(0, (chartH - totalNeeded) / 2);
                            legendItems.forEach((item, i) => { item.legendY = startY + i * LINE_H + LINE_H / 2; });
                            const legendX = PL + chartW + 10;
                            return legendItems.map((item) => {
                              const focused = !csFocusCountry || csFocusCountry === item.country;
                              return (
                                <g key={item.country} className="cursor-pointer"
                                  style={{ opacity: focused ? 1 : 0.3, transition: 'opacity 0.15s' }}
                                  onClick={() => setCsFocusCountry(csFocusCountry === item.country ? null : item.country)}
                                  onMouseEnter={() => setCsHoverCountry({ country: item.country })}
                                  onMouseLeave={() => setCsHoverCountry(null)}
                                >
                                  <circle cx={legendX} cy={item.legendY} r={3} fill={item.color} />
                                  <text x={legendX + 7} y={item.legendY + 3.5} fontSize={8.5} fill={item.color} fontWeight="600">
                                    {item.country}
                                  </text>
                                  <text x={legendX + 7 + item.country.length * 5.2} y={item.legendY + 3.5} fontSize={8} className="fill-muted-foreground">
                                    {` ${item.total}`}
                                  </text>
                                </g>
                              );
                            });
                          })()}
                        </svg>
                      </div>
                      {/* Compact hourly breakdown table */}
                      <div className="mt-3 rounded border border-border/50 overflow-hidden max-h-[160px] overflow-y-auto">
                        <table className="w-full text-[11px]">
                          <thead className="sticky top-0 z-10">
                            <tr className="bg-muted/40 text-muted-foreground">
                              <th className="px-2 py-1 text-left font-medium">Hour</th>
                              <th className="px-2 py-1 text-right font-medium">Calls</th>
                              <th className="px-2 py-1 text-right font-medium">Unique</th>
                              <th className="px-2 py-1 text-left font-medium pl-3">Top Country</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hours.filter(h => Object.values(h.countries).some(v => v > 0)).map(h => {
                              const totalCalls = Object.values(h.countries).reduce((s, v) => s + v, 0);
                              const totalUnique = Object.values(h.unique || {}).reduce((s, v) => s + v, 0);
                              const top4 = Object.entries(h.countries).sort((a, b) => b[1] - a[1]).slice(0, 4);
                              const rest = Object.entries(h.countries).length - top4.length;
                              return (
                                <tr key={h.hour} className="border-t border-border/30 hover:bg-muted/20">
                                  <td className="px-2 py-1 font-mono">{`${h.hour}:00`}</td>
                                  <td className="px-2 py-1 text-right font-mono">{totalCalls}</td>
                                  <td className="px-2 py-1 text-right font-mono text-blue-600 dark:text-blue-400">{totalUnique}</td>
                                  <td className="px-2 py-1 pl-3">
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                      {top4.map(([country, count]) => {
                                        const ci = top_countries.indexOf(country);
                                        return (
                                          <span key={country} className="inline-flex items-center gap-1 whitespace-nowrap">
                                            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ci >= 0 ? COLORS[ci % COLORS.length] : '#888' }} />
                                            <span className="text-[10px]">{countryFlag(country)}</span>
                                            <span>{country}</span>
                                            <span className="text-muted-foreground">({count})</span>
                                          </span>
                                        );
                                      })}
                                      {rest > 0 && <span className="text-muted-foreground">+{rest}</span>}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {/* Country toggles below */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {top_countries.map((c, i) => (
                          <button
                            key={c}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all ${
                              visibleCountries.has(c)
                                ? 'border-transparent text-white'
                                : 'border-border text-muted-foreground bg-transparent opacity-40'
                            }`}
                            style={visibleCountries.has(c) ? { backgroundColor: COLORS[i % COLORS.length] } : {}}
                            onClick={() => toggleCountry(c)}
                          >
                            {countryFlag(c)} {c}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {csLoading ? (
            <div className="flex items-center justify-center h-40"><ShippyLoader size={56} message="Loading checkout calls..." /></div>
          ) : (
            <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold" colSpan={3}>Request</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={2}>Shipping</th>
                    <th className="px-3 py-2 text-left font-semibold border-l border-border/50">Packing</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}></th>
                  </tr>
                  <tr className="bg-muted/30 border-b border-border text-xs text-muted-foreground">
                    <th className="px-3 py-1.5 text-left font-medium w-8"></th>
                    <th className="px-3 py-1.5 text-left font-medium">Country</th>
                    <th className="px-3 py-1.5 text-right font-medium">Items</th>
                    <th className="px-3 py-1.5 text-right font-medium border-l border-border/50">Order Total</th>
                    <th className="px-3 py-1.5 text-right font-medium">Cost</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Bins</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Date</th>
                    <th className="px-3 py-1.5 text-right font-medium">Time</th>
                    <th className="px-3 py-1.5 text-center font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group split cart calls together for visual grouping
                    // A group = consecutive calls with split_cart data, same country+postal+customer
                    const groups = [];
                    let currentGroup = null;

                    for (const call of csCalls) {
                      const splitType = call.response_data?.split_cart;
                      const customerKey = `${call.country}|${call.postal_code}|${call.request_data?.destination?.name || ''}`;

                      if (splitType && currentGroup && currentGroup.key === customerKey &&
                          Math.abs(new Date(call.created_at) - new Date(currentGroup.calls[0].created_at)) < 30000) {
                        // Same group — add to it
                        currentGroup.calls.push(call);
                      } else if (splitType) {
                        // Start new group
                        if (currentGroup) groups.push(currentGroup);
                        currentGroup = { key: customerKey, calls: [call] };
                      } else {
                        // Non-split call
                        if (currentGroup) groups.push(currentGroup);
                        currentGroup = null;
                        groups.push({ key: null, calls: [call] });
                      }
                    }
                    if (currentGroup) groups.push(currentGroup);

                    return groups.map((group) => {
                      // A real split group needs at least one follower (not just retries of "first")
                      const hasFollower = group.calls.some(c => c.response_data?.split_cart === 'follower');
                      const isSplitGroup = group.key !== null && hasFollower;
                      let groupTotal = null;
                      let groupItems = null;
                      let groupBins = null;
                      let customerName = null;

                      if (isSplitGroup) {
                        // Find the last follower (has full_cart_cost) for the summary
                        const follower = [...group.calls].reverse().find(c => c.response_data?.split_cart === 'follower');
                        if (follower?.response_data?.full_cart_cost != null) {
                          groupTotal = follower.response_data.full_cart_cost;
                          groupItems = follower.response_data.full_cart_items;
                        } else {
                          groupTotal = group.calls.reduce((sum, c) => sum + (c.shipping_cost || 0), 0);
                          groupItems = group.calls.reduce((sum, c) => sum + (c.items_count || 0), 0);
                        }
                        // Collect all bins from the follower (merged packing)
                        if (follower?.bins_summary) {
                          groupBins = follower.bins_summary;
                        }
                        customerName = group.calls[0].request_data?.destination?.name;
                      }

                      return (
                        <React.Fragment key={group.calls[0].id}>
                          {/* Split cart summary header */}
                          {isSplitGroup && (
                            <tr className="border-b border-border bg-gradient-to-r from-violet-50 to-transparent dark:from-violet-950/20 dark:to-transparent">
                              <td className="px-3 py-2 w-8">
                                {(() => {
                                  const f = group.calls.find(c => c.response_data?.split_cart === 'follower');
                                  const n = f?.response_data?.prev_profiles != null ? f.response_data.prev_profiles + 1 : 2;
                                  return (
                                    <span className="inline-flex items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 w-5 h-5 text-[9px] font-bold" title="Split cart — multiple delivery profiles">
                                      {n}P
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2" colSpan={2}>
                                <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                                  {countryFlag(group.calls[0].country)} Split Cart
                                  {customerName && <span className="font-normal text-muted-foreground ml-1">— {customerName}</span>}
                                </span>
                                {(() => {
                                  const f = group.calls.find(c => c.response_data?.split_cart === 'follower');
                                  const numProfiles = f?.response_data?.prev_profiles != null ? f.response_data.prev_profiles + 1 : 2;
                                  return (
                                    <span className="block text-[10px] text-violet-600/70 dark:text-violet-400/70">
                                      {groupItems} items merged from {numProfiles} profiles
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2 text-right border-l border-border/50">
                                <span className="text-[10px] text-muted-foreground">cart</span>
                                <span className="block text-xs font-mono">{group.calls[0].order_total != null ? `€${formatCurrency(group.calls[0].order_total)}` : '-'}</span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className="text-[10px] text-violet-600 dark:text-violet-400">combined</span>
                                <span className="block font-mono text-xs font-bold text-violet-700 dark:text-violet-300">€{formatCurrency(groupTotal)}</span>
                              </td>
                              <td className="px-3 py-2 border-l border-border/50">
                                {groupBins && typeof groupBins === 'object' && (
                                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                                    {Object.entries(groupBins).map(([name, qty]) => (
                                      <span key={name} className="inline-flex items-center rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
                                        {name.replace('CB-', '')}{qty > 1 ? ` x${qty}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 border-l border-border/50" colSpan={2}></td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  className="rounded p-1 hover:bg-primary/10 text-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Build a merged call with all items from all profiles
                                    const allItems = [];
                                    const seen = new Set();
                                    for (const c of group.calls) {
                                      const key = c.response_data?.split_cart;
                                      if (seen.has(key)) continue; // skip retries
                                      seen.add(key);
                                      for (const item of (c.request_data?.items || [])) {
                                        allItems.push(item);
                                      }
                                    }
                                    const base = group.calls[0];
                                    handleReplayCSCall({
                                      ...base,
                                      request_data: { ...base.request_data, items: allItems },
                                    });
                                  }}
                                  title="Simulate full merged cart"
                                >
                                  <Play className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          )}
                          {/* Individual calls (deduplicate Shopify retries within 5s) */}
                          {group.calls.filter((call, idx, arr) => {
                            if (!isSplitGroup) return true;
                            // Keep if no earlier call with same split_cart type, items_count, and cost within 5s
                            return !arr.slice(0, idx).some(prev =>
                              prev.response_data?.split_cart === call.response_data?.split_cart &&
                              prev.items_count === call.items_count &&
                              Math.abs(prev.shipping_cost - call.shipping_cost) < 0.01 &&
                              Math.abs(new Date(prev.created_at) - new Date(call.created_at)) < 5000
                            );
                          }).map((call) => {
                    const isSplitFirst = call.response_data?.split_cart === 'first';
                    const isSplitFollower = call.response_data?.split_cart === 'follower';
                    const isSplit = isSplitFirst || isSplitFollower;
                    return (
                    <React.Fragment key={call.id}>
                      <tr
                        className={`border-b border-border hover:bg-muted/30 cursor-pointer ${!call.success ? 'bg-red-50/50 dark:bg-red-950/10' : ''} ${isSplitGroup ? 'border-l-2 border-l-violet-300 dark:border-l-violet-700' : ''} ${isSplitFirst ? 'bg-blue-50/20 dark:bg-blue-950/5' : ''} ${isSplitFollower ? 'bg-emerald-50/20 dark:bg-emerald-950/5' : ''}`}
                        onClick={() => setCsExpandedRow(csExpandedRow === call.id ? null : call.id)}
                      >
                        {/* Status + shipment type icon */}
                        <td className="px-3 py-2.5 w-8">
                          {call.status_code === 0 ? (
                            <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" title="Pending — processing" />
                          ) : call.response_data?.kill_switch ? (
                            <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-1.5 py-0.5 text-[9px] font-medium" title="Kill switch active — empty rates returned">OFF</span>
                          ) : !call.success ? (
                            <XCircle className="h-4 w-4 text-red-500" />
                          ) : (() => {
                            const type = getShipmentType(call.bins_summary);
                            if (type === 'pallet') return <Truck className="h-4 w-4 text-orange-500" title="Pallet Shipping" />;
                            if (type === 'oversized') return <BoxIcon className="h-4 w-4 text-amber-500" title="Oversized Shipping" />;
                            return <Package className="h-4 w-4 text-emerald-500" title="Standard Shipping" />;
                          })()}
                        </td>
                        {/* Country + postal */}
                        <td className={`py-2.5 ${isSplitGroup ? 'pl-6 pr-3' : 'px-3'}`}>
                          {isSplitGroup ? (
                            <>
                              {isSplitFirst ? (
                                <span className="text-[10px] text-blue-600 dark:text-blue-400">
                                  ↳ solo — {call.items_count} items, €{formatCurrency(call.shipping_cost)}
                                </span>
                              ) : (
                                <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                                  ↳ merged — {call.response_data?.full_cart_items} items → €{formatCurrency(call.response_data?.adjusted_cost)}
                                  <span className="text-muted-foreground ml-1">(full €{formatCurrency(call.response_data?.full_cart_cost)})</span>
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="text-xs">{countryFlag(call.country)} {normalizeCountry(call.country) || '-'}</span>
                              {call.postal_code && (
                                <span className="block text-[10px] text-muted-foreground">{call.postal_code}</span>
                              )}
                              {call.response_data?.consolidation && (
                                <span className={`block text-[9px] font-medium mt-0.5 ${call.response_data.consolidation === 'leader' ? 'text-violet-600 dark:text-violet-400' : 'text-violet-500/70 dark:text-violet-400/70'}`}>
                                  {call.response_data.consolidation === 'leader' ? `⚡ merged ${call.response_data.num_profiles} profiles` : '↳ follower'}</span>
                              )}
                            </>
                          )}
                        </td>
                        {/* Items */}
                        <td className="px-3 py-2.5 text-right font-mono text-xs">
                          {call.items_count || '-'}
                        </td>
                        {/* Order total */}
                        <td className="px-3 py-2.5 text-right font-mono text-xs border-l border-border/50">
                          {call.order_total != null ? `€${formatCurrency(call.order_total)}` : '-'}
                        </td>
                        {/* Shipping cost returned */}
                        <td className="px-3 py-2.5 text-right">
                          {call.franco_applied || call.response_data?.franco === 'full' ? (
                            <div>
                              <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">Free</span>
                              {call.shipping_cost != null && (
                                <span className="block text-[10px] text-muted-foreground line-through mt-0.5">€{formatCurrency(call.shipping_cost)}</span>
                              )}
                            </div>
                          ) : call.response_data?.franco === 'partial' ? (
                            <div>
                              <span className="font-mono text-xs font-semibold">€{formatCurrency(call.response_data.customer_pays)}</span>
                              {call.shipping_cost != null && (
                                <span className="block text-[10px] text-muted-foreground line-through mt-0.5">€{formatCurrency(call.shipping_cost)}</span>
                              )}
                            </div>
                          ) : call.shipping_cost != null ? (
                            <span className="font-mono text-xs font-semibold">€{formatCurrency(call.shipping_cost)}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                          {/* Pickup-point rate offered alongside home delivery — on its own line below the cost */}
                          {call.response_data?.pickup && (
                            <div className="mt-1.5">
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-2 py-0.5 text-[10px] font-medium"
                                title="A GLS ParcelShop pickup-point rate was also returned at checkout"
                              >
                                <MapPin className="h-2.5 w-2.5" />
                                {call.response_data.pickup.price > 0
                                  ? `Pickup €${formatCurrency(call.response_data.pickup.price)}`
                                  : 'Pickup Free'}
                              </span>
                            </div>
                          )}
                          {/* Split Shipment rate offered — ship in-stock items first */}
                          {call.response_data?.partial && (
                            <div className="mt-1">
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-[10px] font-medium"
                                title={`Split Shipment offered: ${call.response_data.partial.in_stock_lines} line(s) ship now (€${formatCurrency(call.response_data.partial.in_stock_cost)}) + ${call.response_data.partial.out_of_stock_lines} later (€${formatCurrency(call.response_data.partial.out_of_stock_cost)}) — customer price ex-VAT`}
                              >
                                <Scissors className="h-2.5 w-2.5" />
                                {`Split €${formatCurrency(call.response_data.partial.price)}`}
                              </span>
                            </div>
                          )}
                          {/* Shipping experiment decision for this quote (running A/B test) */}
                          {call.response_data?.experiment && (
                            <div className="mt-1">
                              <ExperimentQuotePill exp={call.response_data.experiment} experiments={csExperiments} labels={csExperimentLabels} />
                            </div>
                          )}
                        </td>
                        {/* Bins */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          {call.bins_summary && typeof call.bins_summary === 'object' ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {Object.entries(call.bins_summary).map(([name, qty]) => (
                                <span key={name} className="inline-flex items-center rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap">
                                  {name.replace('CB-', '')}{qty > 1 ? ` x${qty}` : ''}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">{call.total_bins > 0 ? `${call.total_bins} bin${call.total_bins > 1 ? 's' : ''}` : '-'}</span>
                          )}
                        </td>
                        {/* Date */}
                        <td className="px-3 py-2.5 border-l border-border/50">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(call.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                          <span className="block text-[10px] text-muted-foreground/60">
                            {new Date(call.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </td>
                        {/* Processing time */}
                        <td className="px-3 py-2.5 text-right">
                          {call.processing_time_ms != null ? (
                            <span className={`font-mono text-[11px] ${call.processing_time_ms > 8000 ? 'text-red-600 font-semibold' : call.processing_time_ms > 5000 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {call.processing_time_ms >= 1000 ? `${(call.processing_time_ms / 1000).toFixed(1)}s` : `${Math.round(call.processing_time_ms)}ms`}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/40">-</span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              className="rounded p-1 hover:bg-primary/10 text-primary"
                              onClick={() => handleReplayCSCall(call)}
                              title="Simulate this checkout"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                              onClick={() => handleDeleteCSRecord(call.id)}
                              title="Delete record"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {csExpandedRow === call.id && (
                        <tr className="border-b border-border">
                          <td colSpan={9} className="px-4 py-3 bg-muted/20">
                            <div className="flex justify-end mb-2">
                              <button
                                className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                                onClick={(e) => { e.stopPropagation(); setCsShowRaw(csShowRaw === call.id ? null : call.id); }}
                              >
                                <Braces className="h-3 w-3" />
                                {csShowRaw === call.id ? 'Formatted' : 'Raw JSON'}
                              </button>
                            </div>
                            {csShowRaw === call.id ? (
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Request</h4>
                                  <pre className="bg-muted/50 rounded p-2 overflow-x-auto max-h-48 text-[11px]">
                                    {JSON.stringify(call.request_data, null, 2) || 'No data'}
                                  </pre>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Response</h4>
                                  <pre className="bg-muted/50 rounded p-2 overflow-x-auto max-h-48 text-[11px]">
                                    {call.error_message
                                      ? <span className="text-red-600">{call.error_message}</span>
                                      : JSON.stringify(call.response_data, null, 2) || 'No data'
                                    }
                                  </pre>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                {/* Request details */}
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Request</h4>
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span>
                                      {countryFlag(call.country)} {normalizeCountry(call.country)}
                                      {call.postal_code && <span className="text-muted-foreground ml-1">({call.postal_code})</span>}
                                    </span>
                                  </div>
                                  {call.request_data?.currency && call.request_data.currency !== 'EUR' && (
                                    <div className="text-muted-foreground text-[11px]">
                                      Currency: <span className="font-medium text-foreground">{call.request_data.currency}</span>
                                    </div>
                                  )}
                                  {call.request_data?.items?.length > 0 && (() => {
                                    const items = call.request_data.items;
                                    const hasWeight = items.some(i => i.grams);
                                    const hasPrice = items.some(i => i.price);
                                    return (
                                      <div className="rounded border border-border/50 overflow-hidden">
                                        <table className="w-full text-[11px]">
                                          <thead>
                                            <tr className="bg-muted/30 text-muted-foreground">
                                              <th className="px-2 py-1 text-left font-medium">Item</th>
                                              <th className="px-2 py-1 text-right font-medium">Qty</th>
                                              {hasWeight && <th className="px-2 py-1 text-right font-medium">Weight</th>}
                                              {hasPrice && <th className="px-2 py-1 text-right font-medium">Price</th>}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {items.map((item, idx) => (
                                              <tr key={idx} className="border-t border-border/30">
                                                <td className="px-2 py-1">
                                                  <span className="font-medium">{item.name || item.sku || `Variant ${item.variant_id || '?'}`}</span>
                                                  {item.sku && item.name && (
                                                    <span className="block text-[10px] text-muted-foreground">{item.sku}</span>
                                                  )}
                                                </td>
                                                <td className="px-2 py-1 text-right font-mono">{item.quantity}</td>
                                                {hasWeight && (
                                                  <td className="px-2 py-1 text-right font-mono">
                                                    {item.grams ? `${(item.grams / 1000).toFixed(1)}kg` : '-'}
                                                  </td>
                                                )}
                                                {hasPrice && (
                                                  <td className="px-2 py-1 text-right font-mono">
                                                    {item.price ? `€${(item.price / 100).toFixed(2)}` : '-'}
                                                  </td>
                                                )}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    );
                                  })()}
                                </div>
                                {/* Response details */}
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Response</h4>
                                  {call.error_message ? (
                                    <div className="rounded border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2">
                                      <p className="text-red-600 dark:text-red-400 text-[11px]">{call.error_message}</p>
                                    </div>
                                  ) : (
                                    <div className="space-y-1.5">
                                      <div className="flex items-center gap-2">
                                        {(() => {
                                          const type = getShipmentType(call.bins_summary);
                                          if (type === 'pallet') return <><Truck className="h-3.5 w-3.5 text-orange-500" /><span className="font-medium text-[11px]">Pallet Shipping</span></>;
                                          if (type === 'oversized') return <><BoxIcon className="h-3.5 w-3.5 text-amber-500" /><span className="font-medium text-[11px]">Oversized Shipping</span></>;
                                          return <><Package className="h-3.5 w-3.5 text-emerald-500" /><span className="font-medium text-[11px]">Standard Shipping</span></>;
                                        })()}
                                      </div>
                                      <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-muted-foreground">Shipping Cost</span>
                                        {call.franco_applied ? (
                                          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">Free (Franco)</span>
                                        ) : (
                                          <span className="font-mono font-semibold">{call.shipping_cost != null ? `€${formatCurrency(call.shipping_cost)}` : '-'}</span>
                                        )}
                                      </div>
                                      {call.bins_summary && Object.keys(call.bins_summary).length > 0 && (
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-muted-foreground">Bins</span>
                                          <div className="flex flex-wrap gap-1">
                                            {Object.entries(call.bins_summary).map(([name, qty]) => (
                                              <span key={name} className="inline-flex items-center rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium">
                                                {name}{qty > 1 ? ` ×${qty}` : ''}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {call.order_total != null && call.order_total > 0 && (
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-muted-foreground">Order Total</span>
                                          <span className="font-mono">€{formatCurrency(call.order_total)}</span>
                                        </div>
                                      )}
                                      {call.response_data?.experiment && (
                                        <ExperimentQuoteDetail exp={call.response_data.experiment} experiments={csExperiments} labels={csExperimentLabels} />
                                      )}
                                      {call.response_data?.partial && (
                                        <div className="rounded border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/20 px-2.5 py-1.5 text-[11px] space-y-0.5">
                                          <div className="flex items-center justify-between">
                                            <span className="inline-flex items-center gap-1 font-medium text-violet-700 dark:text-violet-300">
                                              <Scissors className="h-3 w-3" />Split Shipment offered
                                            </span>
                                            <span className="font-mono font-semibold">€{formatCurrency(call.response_data.partial.price)}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-muted-foreground">
                                            <span>Ships now: {call.response_data.partial.in_stock_lines} line{call.response_data.partial.in_stock_lines !== 1 ? 's' : ''}, {call.response_data.partial.in_stock_parcels} parcel{call.response_data.partial.in_stock_parcels !== 1 ? 's' : ''}</span>
                                            <span className="font-mono">€{formatCurrency(call.response_data.partial.in_stock_cost)}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-muted-foreground">
                                            <span>Ships later: {call.response_data.partial.out_of_stock_lines} line{call.response_data.partial.out_of_stock_lines !== 1 ? 's' : ''}, {call.response_data.partial.out_of_stock_parcels} parcel{call.response_data.partial.out_of_stock_parcels !== 1 ? 's' : ''}</span>
                                            <span className="font-mono">€{formatCurrency(call.response_data.partial.out_of_stock_cost)}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-muted-foreground">
                                            <span>Premium vs combined (€{formatCurrency(call.response_data.partial.standard_total)})</span>
                                            <span className="font-mono">€{formatCurrency(call.response_data.partial.premium)}</span>
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {call.response_data?.kill_switch && (
                                          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-2 py-0.5 text-[10px] font-medium">Kill Switch</span>
                                        )}
                                        {call.response_data?.cache_hit && (
                                          <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 text-[10px] font-medium">Cache Hit</span>
                                        )}
                                        {call.response_data?.fallback && (
                                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium">Fallback</span>
                                        )}
                                        {call.response_data?.consolidation === 'leader' && (
                                          <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-[10px] font-medium">Leader ({call.response_data.num_profiles} profiles)</span>
                                        )}
                                        {call.response_data?.consolidation === 'follower' && (
                                          <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-[10px] font-medium">Follower</span>
                                        )}
                                        {call.response_data?.split_cart === 'first' && (
                                          <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 text-[10px] font-medium">1st Profile ({call.response_data.items_cached} items)</span>
                                        )}
                                        {call.response_data?.split_cart === 'follower' && (
                                          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">Merged {call.response_data.full_cart_items} items (full: €{call.response_data.full_cart_cost?.toFixed(2)}, adj: €{call.response_data.adjusted_cost?.toFixed(2)})</span>
                                        )}
                                        {call.response_data?.franco === 'full' && (
                                          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">Free Shipping (franco EUR {call.response_data.threshold})</span>
                                        )}
                                        {call.response_data?.franco === 'partial' && (
                                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium">Partial Franco: {call.response_data.ratio}% ratio, discount €{call.response_data.franco_discount}, customer pays €{call.response_data.customer_pays}</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
                              {call.user_agent && <p>User-Agent: {call.user_agent}</p>}
                              <p>IP: {call.ip_address || 'unknown'}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                        </React.Fragment>
                      );
                    });
                  })()}
                  {csCalls.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                        No checkout calls found for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {csTotalCount > 0 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <p className="text-xs text-muted-foreground">
                  Showing {((csPage - 1) * 50) + 1}–{Math.min(csPage * 50, csTotalCount)} of {csTotalCount.toLocaleString()} calls
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setCsPage(p => Math.max(1, p - 1))}
                    disabled={csPage <= 1 || csLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {(() => {
                    const pages = [];
                    let start = Math.max(1, csPage - 2);
                    let end = Math.min(csTotalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    if (start > 1) {
                      pages.push(<Button key={1} variant={csPage === 1 ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setCsPage(1)} disabled={csLoading}>1</Button>);
                      if (start > 2) pages.push(<span key="s1" className="text-xs text-muted-foreground px-1">...</span>);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(<Button key={i} variant={csPage === i ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setCsPage(i)} disabled={csLoading}>{i}</Button>);
                    }
                    if (end < csTotalPages) {
                      if (end < csTotalPages - 1) pages.push(<span key="s2" className="text-xs text-muted-foreground px-1">...</span>);
                      pages.push(<Button key={csTotalPages} variant={csPage === csTotalPages ? 'default' : 'outline'} size="sm" className="h-8 w-8 p-0 text-xs" onClick={() => setCsPage(csTotalPages)} disabled={csLoading}>{csTotalPages}</Button>);
                    }
                    return pages;
                  })()}
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setCsPage(p => Math.min(csTotalPages, p + 1))}
                    disabled={csPage >= csTotalPages || csLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Returny (Bpost Checker) Tab */}
      {activeTab === 4 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search order #, country, carrier, IP..."
                value={rtSearch}
                onChange={(e) => setRtSearch(e.target.value)}
                className="w-64"
              />
              <select
                value={rtDays}
                onChange={(e) => { setRtDays(Number(e.target.value)); setRtPage(1); }}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value={1}>Last 24h</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
            <Button variant="outline" onClick={() => fetchRtCalls(rtPage, rtDays, rtSearch)} disabled={rtLoading}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          {rtError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {rtError}
            </div>
          )}

          {rtLoading ? (
            <div className="flex items-center justify-center h-40"><ShippyLoader size={56} message="Loading Bpost checks..." /></div>
          ) : (
            <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold" colSpan={3}>Request</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={3}>Bpost Result</th>
                    <th className="px-3 py-2 text-center font-semibold border-l border-border/50" colSpan={2}></th>
                  </tr>
                  <tr className="bg-muted/30 border-b border-border text-xs text-muted-foreground">
                    <th className="px-3 py-1.5 text-left font-medium w-8"></th>
                    <th className="px-3 py-1.5 text-left font-medium">Country</th>
                    <th className="px-3 py-1.5 text-right font-medium">Items</th>
                    <th className="px-3 py-1.5 text-center font-medium border-l border-border/50">Eligible</th>
                    <th className="px-3 py-1.5 text-left font-medium">Surcharges</th>
                    <th className="px-3 py-1.5 text-right font-medium">Amount</th>
                    <th className="px-3 py-1.5 text-left font-medium border-l border-border/50">Date</th>
                    <th className="px-3 py-1.5 text-right font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rtCalls.map((call) => {
                    const resp = call.response_data || {};
                    const eligible = resp.bpost_eligible;
                    const surcharges = resp.surcharges || [];
                    const surchargeTotal = resp.surcharge_total || 0;
                    const isExpanded = rtExpandedRow === call.id;
                    const ms = call.processing_time_ms;

                    return (
                      <React.Fragment key={call.id}>
                        <tr
                          className={`border-b border-border hover:bg-muted/30 cursor-pointer ${!call.success ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}
                          onClick={() => setRtExpandedRow(isExpanded ? null : call.id)}
                        >
                          {/* Status icon */}
                          <td className="px-3 py-2.5 w-8">
                            {eligible === true && surchargeTotal === 0 && <CheckCircle className="h-4 w-4 text-emerald-500" title="Eligible — no surcharges" />}
                            {eligible === true && surchargeTotal > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" title="Eligible with surcharges" />}
                            {eligible === false && <XCircle className="h-4 w-4 text-red-500" title="Not eligible" />}
                            {eligible == null && <XCircle className="h-4 w-4 text-gray-400" title="Error or not found" />}
                          </td>
                          {/* Country */}
                          <td className="px-3 py-2.5">
                            <span className="text-xs font-medium">{call.country || '-'}</span>
                            {resp.country_iso2 && (
                              <span className="block text-[10px] text-muted-foreground">{resp.country_iso2}</span>
                            )}
                          </td>
                          {/* Items */}
                          <td className="px-3 py-2.5 text-right font-mono text-xs">
                            {call.items_count || 1}
                          </td>
                          {/* Eligible */}
                          <td className="px-3 py-2.5 text-center border-l border-border/50">
                            {eligible === true && surchargeTotal === 0 && <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-semibold">OK</span>}
                            {eligible === true && surchargeTotal > 0 && <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold">Surcharge</span>}
                            {eligible === false && <span className="inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[10px] font-semibold">Refused</span>}
                            {eligible == null && <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-2 py-0.5 text-[10px] font-semibold">N/A</span>}
                          </td>
                          {/* Surcharges */}
                          <td className="px-3 py-2.5">
                            {surcharges.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {surcharges.map((s, i) => (
                                  <span key={i} className="inline-flex items-center rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-medium">{s}</span>
                                ))}
                              </div>
                            ) : <span className="text-xs text-muted-foreground">-</span>}
                          </td>
                          {/* Amount */}
                          <td className="px-3 py-2.5 text-right">
                            {surchargeTotal > 0 ? (
                              <span className="font-mono text-xs font-semibold text-amber-600 dark:text-amber-400">+€{surchargeTotal.toFixed(2)}</span>
                            ) : <span className="text-xs text-muted-foreground">-</span>}
                          </td>
                          {/* Date */}
                          <td className="px-3 py-2.5 border-l border-border/50">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {new Date(call.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </span>
                            <span className="block text-[10px] text-muted-foreground/60">
                              {new Date(call.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                          </td>
                          {/* Processing time */}
                          <td className="px-3 py-2.5 text-right">
                            {ms != null ? (
                              <span className={`font-mono text-[11px] ${ms > 8000 ? 'text-red-600 font-semibold' : ms > 5000 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                {ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`}
                              </span>
                            ) : <span className="text-[11px] text-muted-foreground/40">-</span>}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-b border-border">
                            <td colSpan={8} className="px-4 py-3 bg-muted/20">
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                {/* Request details */}
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Request</h4>
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span>{call.country}{resp.country_iso2 ? ` (${resp.country_iso2})` : ''}</span>
                                  </div>
                                  {/* Product list from request */}
                                  {(() => {
                                    const req = call.request_data || {};
                                    const items = req.items || (req.product_id ? [{ product_id: req.product_id, quantity: 1 }] : []);
                                    const respItems = resp.items || [];
                                    if (items.length === 0) return null;
                                    return (
                                      <div className="rounded border border-border/50 overflow-hidden">
                                        <table className="w-full text-[11px]">
                                          <thead>
                                            <tr className="bg-muted/30 text-muted-foreground">
                                              <th className="px-2 py-1 text-left font-medium">Product</th>
                                              <th className="px-2 py-1 text-right font-medium">Qty</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {items.map((item, idx) => {
                                              const detail = respItems.find(ri => ri.product_id === item.product_id);
                                              return (
                                                <tr key={idx} className="border-t border-border/30">
                                                  <td className="px-2 py-1">
                                                    <span className="font-medium">{detail?.product_name || resp.product_name || `Product ${item.product_id}`}</span>
                                                    <span className="block text-[10px] text-muted-foreground">ID: {item.product_id}</span>
                                                  </td>
                                                  <td className="px-2 py-1 text-right font-mono">{item.quantity || 1}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    );
                                  })()}
                                </div>
                                {/* Response details */}
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-muted-foreground uppercase text-[10px] mb-1">Bpost Result</h4>
                                  {call.error_message ? (
                                    <div className="rounded border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2">
                                      <p className="text-red-600 dark:text-red-400 text-[11px]">{call.error_message}</p>
                                    </div>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {/* Overall status */}
                                      <div className="flex items-center gap-2">
                                        {eligible === true && surchargeTotal === 0 && <><CheckCircle className="h-3.5 w-3.5 text-emerald-500" /><span className="font-medium text-[11px] text-emerald-700 dark:text-emerald-300">Eligible — no surcharges</span></>}
                                        {eligible === true && surchargeTotal > 0 && <><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /><span className="font-medium text-[11px] text-amber-700 dark:text-amber-300">Eligible with surcharges (+€{surchargeTotal.toFixed(2)})</span></>}
                                        {eligible === false && <><XCircle className="h-3.5 w-3.5 text-red-500" /><span className="font-medium text-[11px] text-red-600 dark:text-red-400">Not eligible for Bpost</span></>}
                                      </div>
                                      {/* Detail message */}
                                      {resp.details && (
                                        <p className="text-[11px] text-muted-foreground">{resp.details}</p>
                                      )}
                                      {/* Parcels (multi-product) */}
                                      {resp.parcels && resp.parcels.length > 0 && (
                                        <div className="rounded border border-border/50 overflow-hidden mt-1">
                                          <table className="w-full text-[11px]">
                                            <thead>
                                              <tr className="bg-muted/30 text-muted-foreground">
                                                <th className="px-2 py-1 text-left font-medium">Parcel</th>
                                                <th className="px-2 py-1 text-right font-medium">Dims</th>
                                                <th className="px-2 py-1 text-right font-medium">Weight</th>
                                                <th className="px-2 py-1 text-center font-medium">Status</th>
                                                <th className="px-2 py-1 text-right font-medium">Surcharge</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {resp.parcels.map((p, pi) => (
                                                <tr key={pi} className="border-t border-border/30">
                                                  <td className="px-2 py-1">
                                                    <span className="font-medium">{p.bin_name || `Parcel ${pi + 1}`}</span>
                                                    <span className="block text-[10px] text-muted-foreground">{p.items_count} item{p.items_count !== 1 ? 's' : ''}</span>
                                                  </td>
                                                  <td className="px-2 py-1 text-right font-mono text-[10px]">
                                                    {p.dimensions ? `${p.dimensions.width}×${p.dimensions.height}×${p.dimensions.length}` : '-'}
                                                  </td>
                                                  <td className="px-2 py-1 text-right font-mono text-[10px]">
                                                    {p.dimensions?.weight ? `${p.dimensions.weight.toFixed(1)}kg` : '-'}
                                                  </td>
                                                  <td className="px-2 py-1 text-center">
                                                    {p.eligible === true && !p.surcharge && <span className="text-emerald-600 text-[10px] font-semibold">OK</span>}
                                                    {p.eligible === true && p.surcharge > 0 && <span className="text-amber-600 text-[10px] font-semibold">Surcharge</span>}
                                                    {p.eligible === false && <span className="text-red-600 text-[10px] font-semibold">Refused</span>}
                                                  </td>
                                                  <td className="px-2 py-1 text-right font-mono text-[10px]">
                                                    {p.surcharge > 0 ? <span className="text-amber-600 font-semibold">+€{p.surcharge.toFixed(2)}</span> : '-'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                      {/* Single product boxes */}
                                      {!resp.parcels && resp.boxes && (
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-muted-foreground">Dimensions</span>
                                          <span className="font-mono">
                                            {(() => {
                                              const b = Array.isArray(resp.boxes) ? resp.boxes[0] : resp.boxes;
                                              const d = b?.dimensions;
                                              return d ? `${d.width}×${d.height}×${d.length} cm, ${d.weight}kg` : '-';
                                            })()}
                                          </span>
                                        </div>
                                      )}
                                      {!resp.parcels && resp.boxes && (
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="text-muted-foreground">Girth / Sum</span>
                                          <span className="font-mono">
                                            {(() => {
                                              const b = Array.isArray(resp.boxes) ? resp.boxes[0] : resp.boxes;
                                              return b ? `${b.girth}cm / ${b.sum_sides}cm` : '-';
                                            })()}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
                                {call.user_agent && <p>User-Agent: {call.user_agent}</p>}
                                <p>IP: {call.ip_address || 'unknown'}</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {rtCalls.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                        No Bpost checker calls found for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {rtTotalCount > 0 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <p className="text-xs text-muted-foreground">
                  Showing {((rtPage - 1) * 50) + 1}–{Math.min(rtPage * 50, rtTotalCount)} of {rtTotalCount.toLocaleString()} calls
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setRtPage(p => Math.max(1, p - 1))}
                    disabled={rtPage <= 1 || rtLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setRtPage(p => Math.min(rtTotalPages, p + 1))}
                    disabled={rtPage >= rtTotalPages || rtLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Custom Order Modal */}
      <CustomOrderModal
        open={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
        onSimulate={handleCustomSimulate}
      />

      {/* Surcharge Modal */}
      <SurchargeModal
        open={surchargeModalOpen}
        onClose={() => setSurchargeModalOpen(false)}
        orderData={orderData}
        selectedCost={Number(strategiesState[activeStrategyTab]?.total_cost ?? packingSolution?.total_cost ?? 0)}
        eurRates={eurRates}
      />

      {/* Checkout preview — the real carrier service, with the withheld rates */}
      <CheckoutPreviewModal
        open={checkoutPreviewOpen}
        onClose={() => setCheckoutPreviewOpen(false)}
        items={(orderData?.products || []).map(p => ({
          name: p.title || p.name,
          sku: p.sku || '',
          variant_id: p.variant_id || '',
          quantity: p.quantity,
          // Checkout sees VAT-inclusive, pre-discount line prices (Shopify's
          // subtotal_price); the simulator's unit_price is ex VAT.
          price: (p.original_unit_price_incl_tax ?? p.unit_price_incl_tax ?? p.unit_price) || 0,
          unit_cost: p.cost,
          // Hand-typed / quote-only carts have no Boxopedia row to look up, so
          // send the dims the simulator is using; real variants resolve as usual.
          dimensions: p.dimensions ? {
            length_cm: p.dimensions.length, width_cm: p.dimensions.width,
            height_cm: p.dimensions.height, weight_kg: p.weight,
          } : undefined,
          packaging_boxes: p.packaging_boxes || [],
        }))}
        country={orderData?.shipping_address?.country || ''}
        postalCode={orderData?.shipping_address?.zip || ''}
        orderTotal={orderData?.checkout_total_eur ?? (orderData?.products || []).reduce(
          (sum, p) => sum + (((p.unit_price_incl_tax ?? p.unit_price) || 0) * p.quantity), 0)}
      />

      {/* Quick checkout preview from an order row (Shopify tab "$" button) */}
      <CheckoutPreviewModal
        open={!!quickPreview}
        onClose={() => setQuickPreview(null)}
        items={quickPreview?.items || []}
        country={quickPreview?.country || ''}
        postalCode={quickPreview?.postalCode || ''}
        orderTotal={quickPreview?.orderTotal || 0}
      />
    </div>
  );
};

export default PackingSimulator;
